import '@fontsource/inter';
import React, {useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect} from 'react';
import {createPortal} from 'react-dom';
import { openProject } from './utils/openProject';
import { useCountdown } from './hooks/useCountdown';

// Seconds the "Open the project" prompt waits, after the agent finishes, before opening the project by itself.
const OPEN_PROJECT_COUNTDOWN_SECONDS = 10;
import Conversations from './Conversations';
import {useLocation, useNavigate, createSearchParams, Routes, Route, Navigate, useParams} from 'react-router-dom';
import AdkClient from './AdkClient';
import * as Messages from './Messages';
import config, {isConfigLoaded} from './config';
import {createParser} from 'eventsource-parser';
import {getHubConfig} from './config';
import AuthCallback from './auth/AuthCallback';
import FlowSidebar from './components/FlowSidebar';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { SearchProvider, useSearch } from './components/search/SearchContext';
import SupportedStacksHost from './components/stacks/SupportedStacksHost';
import ConsoleHandoff from './components/ConsoleHandoff';
import NewProjectLink from './components/NewProjectLink';
import Connectors from './pages/Connectors';
import Projects from './pages/Projects';
import Workspaces from './pages/Workspaces';
import Workspace from './pages/Workspace';
import Templates from './pages/Templates';
import PlaybookLauncher from './pages/PlaybookLauncher';
import Learn from './pages/Learn';
import Discover from './pages/Discover';
import Memories from './pages/Memories';
import Agents from './pages/Agents';
import GlobalBrain from './pages/GlobalBrain';
import InternalStatus from './pages/InternalStatus';
// "Supported languages and frameworks" (/supported-stacks): loaded when opened, the page for shared links (the in-app
// links and ⌘K open it in a dialog: components/stacks/SupportedStacksHost.jsx).
const SupportedStacks = React.lazy(() => import('./pages/SupportedStacks'));

import InstructionsConfig from './components/InstructionsConfig';
import Toast from './components/Toast';
import Modal from './components/Modal';
import Canvas from './components/Canvas';
import AIInstructionsModal from './components/AIInstructionsModal';
import SavePromptModal from './components/SavePromptModal';
import SaveMessageModal from './components/SaveMessageModal';
import AIModeDropdown from './components/AIModeDropdown';
import ModelDropdown from './components/ModelDropdown';
import MCPAppView from './components/MCPAppView';
import ReasoningStatus from './components/ReasoningStatus';
import BrowserStreamViewer from './components/BrowserStreamViewer';
import BrowserStreamViewerStandalone from './components/BrowserStreamViewerStandalone';
import Icon from './components/icons/Icon';
import contextManager from './utils/ContextManager';
import * as CodeExtractor from './utils/CodeExtractor';
import {generateImage, detectImageGenerationRequest} from './ImageGeneration';
import {generateVideo, detectVideoGenerationRequest} from './VideoGeneration';
import indexedDbStorage from './utils/indexedDbStorage';
import indexedDbFileStorage, {GIT_STATE} from './utils/indexedDbFileStorage';
import { isCodeFile } from './utils/codeFileDetection';
import { encodePathSegment, encodeServiceNameForUrl, decodeServiceNameFromUrl } from './utils/urlPathSegment';
import Login from './pages/Login';
import Register from './pages/Register';
import JoinCollaboration from './pages/JoinCollaboration';
import {AuthProvider, useAuth} from './context/AuthContext';
import {OrganizationProvider, useSelectedOrganization} from './context/OrganizationContext';
import {PermissionsProvider} from './context/PermissionsContext';
import {TenantAccessProvider} from './context/TenantAccessContext';
import {RedirectToLogin} from '@mia-platform-internal/authtool-client-js/react';
import MobileAIMode from './components/MobileAIMode';
import MobileCanvasDrawer from './components/MobileCanvasDrawer';
import MobileCodePage from './components/MobileCodePage';
import MobilePreviewPage from './components/MobilePreviewPage';
import Home from './components/Home';
import TenantSwitchOverlay from './components/TenantSwitchOverlay';
import InAppBrowserOverlay from './components/InAppBrowserOverlay';
import {exportConversationToPDF} from './utils/pdfExport';
import miaPlatformDataService from './services/MiaPlatformDataService';
import * as vfsClient from './utils/vfsClient';
import VfsChatPanel from './components/VfsChatPanel';
import miaFlowAuth from './auth/MiaFlowAuth';
import GitLabAuth from './auth/GitLabAuth';
import GitHubAuth from './auth/GitHubAuth';
import BitbucketAuth from './auth/BitbucketAuth';
import AzureDevOpsAuth from './auth/AzureDevOpsAuth';
import {useAutoDiffApplier} from './hooks/useAutoDiffApplier.jsx';
import {useCanvasProjectContext} from './hooks/useCanvasProjectContext';
import useChatFeature from './hooks/useChatFeature';
import {askAiInChat} from './utils/askAiInChat';
import { playbooksInPlan } from './hooks/usePlanFeatures';
import SandboxHome from './pages/SandboxHome';
import ChatFeature from './pages/ChatFeature';
import Connections from './pages/Connections';
import Welcome from './pages/Welcome';
import UserSettings from './pages/UserSettings';
import OpenProject from './pages/OpenProject';
import { getSandbox } from './services/SandboxService';
import { markHomeStep } from './services/SettingsService';
import {useOutsideClick} from './hooks/useOutsideClick';
import {useMcpTools} from './hooks/useMcpTools';
import {usePlaybookApplication} from './hooks/usePlaybookApplication';
import {useTenantSelection} from './hooks/useTenantSelection';
import {getBeHeaders, getBffHeaders} from './utils/apiHeaders';
import VirtualFileSystem from './utils/virtualFileSystem';
import {registerModalStateCallback, showScriptExecutionConfirmation, showScriptExecutionError} from './utils/scriptExecutionConfirmation.jsx';
/* Lines 18-22 omitted */

import SlashCommandMenu from './components/SlashCommandMenu';
import {filterSpeckitCommands, buildCommandsFromSpecs} from './constants/speckitCommands';
import ProjectOpenError from './components/ProjectOpenError';
import SessionExpiredNotice from './components/SessionExpiredNotice';
import { createQuickVfsProject } from './utils/quickProjectCreation';
import { AnalyticsTracker, friction } from './analytics';
import SandboxMessagesBanner from './components/messages/SandboxMessagesBanner';
import { FlowUpdatingBanner, SandboxWakeBanner } from './components/sandbox/SandboxWaking';
import { setLivePollSignedIn } from './utils/livePoll';
import './App.css';
import './auth/auth.css';
import './Mobile.css';
import './ProjectCompact.css';
import CompactProjectBar from './components/CompactProjectBar';
import {useCompact} from './hooks/useMediaQuery';
import { devLog } from './utils/devLog';
import { noteSandboxPage } from './utils/sandboxSleep';
import { searchForFreshConversation, searchForNewChatInProject } from './utils/newChatInProject';

const AI_API_PREFIX = 'api/catalog'

/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */



const MAX_SUGGESTED_TOOLS = 128;

// A fresh id for every "start a new conversation from Home" navigation to
// `/chat` (see VfsChatPanel's `requestedSessionId`/`conversationId` query
// param — the same mechanism Memories.jsx already uses to resume a specific
// past session). With no id at all in the URL, VfsChatPanel's
// `initialSessionId` falls back to its fixed `vfs-${vfsProjectId}` default,
// which for the project-less plain-chat route resolves to the constant
// string "vfs-undefined" — every visit reopening whatever project-less chat
// was last used instead of starting a new one. `crypto.randomUUID` is
// available in every browser this app targets; the fallback only guards a
// non-secure-context edge case (randomUUID requires one).
function newChatConversationId() {
    return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Sandbox-first: the chat page needs Chat turned on; until then it leads to Features › Chat. */
function RequireChat({ children }) {
    const [chatOn] = useChatFeature();
    useEffect(() => { if (chatOn) { markHomeStep('chatTried'); } }, [chatOn]);
    return chatOn ? children : <Navigate replace to="/features/chat" />;
}

function MainChat({onNewConversation, hideCanvas = false}) {
    // React Router hooks (must be at the top before any other code)
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { user, authorizedFetch } = useAuth();
    // Sandbox-first (docs/SANDBOX_FIRST.md): with Chat turned off a project opens without the assistant panel and
    // Canvas takes the whole width.
    const [chatFeatureOn, setChatFeatureOn, chatAvailable] = useChatFeature();
    const chatHidden = !hideCanvas && !chatFeatureOn;
    // Phones and tablets in portrait (CompactProjectBar): one pane at a time, the Chat or the Project (Canvas).
    const compact = useCompact();
    const [compactPane, setCompactPane] = useState('chat');
    const paneSwitch = compact && !hideCanvas && !chatHidden;

    // Core state for the chat interface
    const [messages, setMessages] = useState([]); // All messages in the current chat
    const [inputValue, setInputValue] = useState(() => {
        // Load input value from localStorage if available
        try {
            return localStorage.getItem('chatInputValue') || '';
        } catch (e) {
            return '';
        }
    }); // Text in the message input field
    const [showSendPromptBanner, setShowSendPromptBanner] = useState(false); // Banner to highlight send button for new projects
    // Message queued to be sent into VfsChatPanel's ChatWidget via its `initialMessage`
    // prop (see ChatWidgetProps.initialMessage: sent once per distinct value). Used by
    // Canvas's `onAskAI` callback (build errors, preview "Fix with AI") now that chat
    // for canvas-mode projects is the chatbot-widget, not the legacy `.chat-input`.
    // BUG FIX (live-reported, screen-recorded: "empty chat, then the prompt
    // appears after a while"): this used to always start `null` and only get
    // set by the "Handle initial prompt from Home page navigation" effect
    // below — a `useEffect`, which by definition runs AFTER /chat's first
    // paint. That first paint passed `initialMessage={undefined}` to
    // VfsChatPanel, rendering a visibly empty transcript for one frame (or
    // longer, on a slow render) before the effect fired and a second render
    // finally showed the real message. Seeding this from `location.state`
    // in the lazy initializer instead means the very first render already
    // has it — the effect below still runs for its OTHER jobs (aiMode,
    // chatHasActivity, file conversion) but no longer needs to (and no
    // longer does, see its own updated comment) set this again.
    const [pendingChatMessage, setPendingChatMessage] = useState(() => location.state?.initialPrompt || null);
    // Companion to pendingChatMessage for the "Ask AI" (Preview Logs) flow —
    // the raw error/log text that must reach the agent WITHOUT being pasted
    // into the visible chat transcript. Forwarded to VfsChatPanel's
    // `pageContext` prop (never rendered as message text — see the
    // askAIFromLogs handler below). Read once, like pendingChatMessage; not
    // cleared proactively since a stale one is harmless (VfsChatPanel only
    // forwards it to a NEW turn's `state_delta`, and it gets overwritten by
    // the next real navigation's own pageContext if any is ever added).
    const [pendingAskAIPageContext, setPendingAskAIPageContext] = useState(null);
    // {id, text} | null — forwarded to VfsChatPanel's `pendingMessage` prop.
    // See the askAIFromLogs handler below for why this exists instead of
    // reusing pendingChatMessage.
    const [pendingAskAIMessage, setPendingAskAIMessage] = useState(null);
    // {id, text} | null — forwarded to VfsChatPanel's `pendingDraft` prop: written into the chat's input, not sent
    // (the Data tab's "Ask the AI to add it", see utils/askAiInChat.js).
    const [pendingAskAIDraft, setPendingAskAIDraft] = useState(null);
    // Which project id `pendingChatMessage` was set for (see the
    // initialPrompt effect's own comment) — the clearing effect below uses
    // this instead of a wall-clock timer to know exactly when VfsChatPanel
    // is about to remount under a new project id and the message has
    // already done its one job.
    const pendingChatMessageProjectIdRef = useRef(null);
    // Spec-Kit slash command menu state
    const [showSlashMenu, setShowSlashMenu] = useState(false);
    const [slashMenuIndex, setSlashMenuIndex] = useState(0);
    const [catalogSpecCommands, setCatalogSpecCommands] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Controls loading spinners and disables inputs
    const [abortController, setAbortController] = useState(null); // For canceling ongoing requests
    const [conversationStatus, setConversationStatus] = useState(''); // Status message while LLM is thinking
    const [toast, setToast] = useState(null); // Toast notification state

    // Helper functions to show/hide toast notifications — moved up from their
    // original spot (just before useAutoDiffApplier) so useCanvasProjectContext
    // (src/hooks/useCanvasProjectContext.js) can take showToast as an explicit
    // parameter; both only ever depended on setToast, so this reorder is
    // behavior-identical.
    const showToast = useCallback((message, type = 'error') => {
        setToast({message, type});
    }, []);

    const hideToast = useCallback(() => {
        setToast(null);
    }, []);

    const [aiMode, setAiMode] = useState(() => {
        // Load AI mode from localStorage, default to 'agent'
        return localStorage.getItem('aiMode') || 'agent';
    }); // 'agent' or 'plan' mode
    const [isAiModeDropdownOpen, setIsAiModeDropdownOpen] = useState(false);

    // AI Model state
    const [selectedModel, setSelectedModel] = useState(() => {
        return localStorage.getItem('selectedModel') || 'vertex_ai/claude-haiku-4-5';
    });

    const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
    const [showSendOptions, setShowSendOptions] = useState(false);
    const [messageQueue, setMessageQueue] = useState([]); // Queue of {inputValue, attachedFiles}

    const [isMobileCanvasDrawerOpen, setIsMobileCanvasDrawerOpen] = useState(false);
    const [isMobileCodePageOpen, setIsMobileCodePageOpen] = useState(false);
    const [isMobilePreviewPageOpen, setIsMobilePreviewPageOpen] = useState(false);
    const [mobileActiveView, setMobileActiveView] = useState('chat'); // Track active view for mobile nav highlighting
    const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen modal state

    // File attachment state
    const [attachedFiles, setAttachedFiles] = useState([]);
    const fileInputRef = useRef(null);

    // AI Instructions state
    const [isAIInstructionsModalOpen, setIsAIInstructionsModalOpen] = useState(false);

    // Save Prompt Modal state
    const [isSavePromptModalOpen, setIsSavePromptModalOpen] = useState(false);

    // Save Message Modal state
    const [isSaveMessageModalOpen, setIsSaveMessageModalOpen] = useState(false);
    const [messageToSave, setMessageToSave] = useState('');


    // Script Execution Confirmation Modal state
    const [scriptExecutionModal, setScriptExecutionModal] = useState({ isOpen: false });

    // Context breadcrumb state
    const [aiContext, setAiContext] = useState({items: []});

    // MCP Tools (useMcpTools) and playbook application (usePlaybookApplication)
    // have a genuine two-way dependency — see usePlaybookApplication.js's doc
    // comment for why toolsApiRef exists and why this is safe. The actual
    // `useState`/`useCallback` calls below happen further down (after
    // `showToast` is defined, since usePlaybookApplication needs it) — this
    // comment marks where the wiring conceptually lives.

    // Utility function to convert tool names to friendly display names
    const getToolFriendlyName = (toolName) => {
        // Handle specific patterns
        if (toolName.includes('azure_devops')) {
            return toolName.replace(/.*azure_devops[_-]?/i, '').split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ').replace(/oauth/i, 'OAuth');
        }
        if (toolName.includes('atlassian')) {
            return toolName.replace(/.*atlassian[_-]?/i, '').split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
        }
        // Generic conversion: remove prefixes and convert snake_case to Title Case
        return toolName
            .replace(/^(agents\.hub\.tools\.|get_|list_|create_|update_|delete_)/i, '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Canvas ref for triggering refresh
    const canvasRef = useRef(null);

    // Canvas's portal target (see the createPortal call further down) — looked
    // up via an effect rather than read synchronously at render time, since on
    // the very first mount MainApp's #canvas-portal-root div hasn't committed
    // to the real DOM yet (this component is one of its own descendants), so
    // `document.getElementById` would return null and silently portal into
    // document.body once, with the wrong (viewport-relative, nav-sidebar-
    // ignorant) positioning context for that one frame.
    const [canvasPortalTarget, setCanvasPortalTarget] = useState(null);
    useEffect(() => {
        setCanvasPortalTarget(document.getElementById('canvas-portal-root'));
    }, []);
    // Which pane the compact layout shows (ProjectCompact.css reads it from the shell); none on desktop.
    useEffect(() => {
        if (!canvasPortalTarget) { return undefined; }
        const pane = compact && !hideCanvas ? (paneSwitch ? compactPane : 'project') : null;
        if (pane) { canvasPortalTarget.dataset.compactPane = pane; } else { delete canvasPortalTarget.dataset.compactPane; }
        return () => { delete canvasPortalTarget.dataset.compactPane; };
    }, [canvasPortalTarget, compact, hideCanvas, paneSwitch, compactPane]);
    // Something asked to show a file, a preview or a project panel (`flow:compactPane`, a "See what X sees"): the Project pane.
    useEffect(() => {
        if (!paneSwitch) { return undefined; }
        const toPane = (event) => setCompactPane(event?.detail?.pane === 'chat' ? 'chat' : 'project');
        const toProject = () => setCompactPane('project');
        window.addEventListener('flow:compactPane', toPane);
        window.addEventListener('collab:openPreview', toProject);
        window.addEventListener('openFile', toProject);
        window.addEventListener('collab:openHistory', toProject);
        return () => {
            window.removeEventListener('flow:compactPane', toPane);
            window.removeEventListener('collab:openPreview', toProject);
            window.removeEventListener('openFile', toProject);
            window.removeEventListener('collab:openHistory', toProject);
        };
    }, [paneSwitch]);

    // Token tracking state
    const [totalTokens, setTotalTokens] = useState(0); // Total tokens consumed in session
    const [inputTokens, setInputTokens] = useState(0); // Input/prompt tokens
    const [outputTokens, setOutputTokens] = useState(0); // Output/response tokens

    // State for Canvas mode (always enabled)
    const [isCanvasMode] = useState(true); // Canvas mode for full-screen document/code viewing (always on)
    const [canvasContent, setCanvasContent] = useState(() => {
        // Load Canvas content from localStorage if available
        const savedContent = localStorage.getItem('canvasContent');
        return savedContent ? JSON.parse(savedContent) : null;
    }); // Content to display in Canvas mode
    // Project context + URL/localStorage/IndexedDB sync — owns
    // canvasProjectContext/urlValidationError/showProjectSelector and every
    // effect/listener that writes them (src/hooks/useCanvasProjectContext.js,
    // DDD refactor Phase 4). Called here (rather than right after showToast)
    // because it must be initialized before useAutoDiffApplier below reads
    // canvasProjectContext synchronously.
    const {
        canvasProjectContext, setCanvasProjectContext,
        urlValidationError, setUrlValidationError,
        showProjectSelector, setShowProjectSelector,
        lastLoadedProjectRef,
        handleProjectContextChange,
        syncUrlWithCanvasSelections,
    } = useCanvasProjectContext({showToast, setAiMode});
    const [filesModifiedInCanvas, setFilesModifiedInCanvas] = useState(false); // Track if files were modified in Canvas mode
    // What the person is looking at, for Flow's chat: sent as `state_delta.page_context` on every turn (adk-be-app's
    // page_context.py puts it in the agent's instructions), so "this project" or "the current file" resolve without the
    // agent guessing from a root listing. Paths only: the agent reads the file itself through the vfs tools.
    const workspacePageContext = useMemo(() => {
        const ctx = canvasProjectContext;
        if (!ctx?.projectId) {
            return undefined;
        }
        const projectName = ctx.project?.name || ctx.projectId;
        const service = ctx.selectedMicroservice && ctx.selectedMicroservice.type !== 'configuration' ? ctx.selectedMicroservice.name : null;
        const file = ctx.selectedFile?.path || ctx.selectedFile?.name || null;
        const where = [service && `service ${service}`, file && `file ${file}`].filter(Boolean).join(', ');
        return {
            page: file ? 'project-file' : 'project',
            path: location.pathname,
            summary: `Viewing the project "${projectName}"${where ? ` (${where})` : ''}. Its files are under services/<service>/; look inside them (vfs_tree_json or vfs_ls on that folder) and read .mia/project.json before saying the project is empty.`,
            data: { projectId: ctx.projectId, projectName, service, file },
        };
    }, [canvasProjectContext?.projectId, canvasProjectContext?.project?.name, canvasProjectContext?.selectedMicroservice?.name,
        canvasProjectContext?.selectedMicroservice?.type, canvasProjectContext?.selectedFile?.path, canvasProjectContext?.selectedFile?.name, location.pathname]);

    const [enableMcpApps, setEnableMcpApps] = useState(() => {
        const saved = localStorage.getItem('ENABLE_MCP_APPS');
        return saved === 'true';
    }); // Enable MCP Apps feature flag
    const [chatSidebarWidth, setChatSidebarWidth] = useState(() => {
        // Load sidebar width from localStorage or default to 400px.
        // Clamped to the same 280-1200px range the resize handler itself
        // enforces (see its own comment on the 280px floor — the vendored
        // chatbot-widget's tool-call bubbles hard-refuse to shrink below
        // that) — self-heals a value saved before that floor existed
        // (e.g. a stored 100px from an earlier, buggier version of this
        // clamp), instead of reloading straight into the same overlapping,
        // unusable layout every time.
        const stored = parseInt(localStorage.getItem('chatSidebarWidth') || '400', 10);
        if (Number.isNaN(stored)) return 400;
        return Math.min(1200, Math.max(280, stored));
    }); // Width of chat sidebar when in canvas mode

    // Refs for DOM elements
    const chatMessagesRef = useRef(null); // For scrolling the message list
    const resizeHandleRef = useRef(null); // For resize handle
    const shouldAutoScrollRef = useRef(true); // Track if auto-scroll should happen (user hasn't scrolled up)
    const lastScrollHeightRef = useRef(0); // Track last scroll height to detect new content
    const inputRef = useRef(null); // For focusing the text input
    const isSavingRef = useRef(false); // Track if save is in progress to prevent duplicates
    const restoredMessageCountRef = useRef(0); // Track how many messages were restored from localStorage (not new)
    const manualCanvasSetRef = useRef(false); // Track if canvas was manually set (e.g., for images)
    // lastLoadedProjectRef now comes from useCanvasProjectContext() above.

    /**
     * Load selectedResource from localStorage
     * This contains the full service data including name, type, and all metadata
     * @returns {object|null} - Parsed selectedResource object or null if not found
     */
    const loadSelectedResourceFromStorage = useCallback(() => {
        try {
            const savedResourceStr = localStorage.getItem('mia_selectedResource');
            if (savedResourceStr) {
                const selectedResource = JSON.parse(savedResourceStr);
                // eslint-disable-next-line no-console
                devLog('📌 Loaded selectedResource from localStorage:', selectedResource?.data?.name);
                return selectedResource;
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Failed to parse selectedResource from localStorage:', err);
        }
        return null;
    }, []);

    // Register script execution modal callback
    useEffect(() => {
        registerModalStateCallback(setScriptExecutionModal);
    }, []);

    // IndexedDB-redirect-on-mount / stored-tenant-redirect / URL-validation /
    // project-picker-open / localStorage-load / select-project effects all
    // now live in useCanvasProjectContext() above (DDD refactor Phase 4).

    // Real-time collaboration no longer needs a project-level connect/
    // disconnect effect here — useCollaboration.js now opens/closes its own
    // Yjs WebsocketProvider directly against vfs-service, scoped to whichever
    // file is actually open in Monaco, with no separate init step.

    // State for conversation management
    const [convName, setConvName] = useState(''); // The user-defined name of the conversation
    const [conversationId, setConversationId] = useState(null); // The unique ID of the saved conversation
    const [sessionId, setSessionId] = useState(null); // The backend session ID for this chat instance
    // Task #164 fix: on the plain /chat door (hideCanvas=true), Canvas isn't
    // mounted, so canvasProjectContext.projectId is never populated even
    // after the agent creates a project mid-conversation via
    // vfs_create_project — VfsChatPanel tracks that internally (see its own
    // pendingRedirectRef) but only navigates once the TURN completes, to
    // avoid aborting the in-flight stream. Live-confirmed real bug: a user
    // who clicks the Chat<->Code switcher WHILE that turn is still running
    // races ahead of the auto-redirect, so goToMode below sees no bound
    // project and creates a SECOND, unrelated vfs project instead of
    // opening the one the chat is actively writing to. VfsChatPanel calls
    // onProjectBound the instant it learns the real id (synchronously in
    // handleToolCall, not gated on turn-complete), so goToMode always has
    // an up-to-date fallback even mid-turn.
    const [chatBoundProjectId, setChatBoundProjectId] = useState(null);
    // Set when the agent has built something in this chat; shows the "Open the project" button (never switches by itself).
    const [openProjectPromptDismissed, setOpenProjectPromptDismissed] = useState(false);
    const [openProjectReady, setOpenProjectReady] = useState(false);
    // The service whose preview the "Open the project" button opens, once the chat has built one.
    const [openProjectServiceName, setOpenProjectServiceName] = useState(null);
    // The agent has finished its turn: only then does the "Open the project" countdown run.
    const [openProjectTurnDone, setOpenProjectTurnDone] = useState(false);
    // Mirrors chatBoundProjectId synchronously (no render lag) — read by
    // goToMode's short poll below when a Code-switch click races AHEAD of
    // onProjectBound ever firing at all (as opposed to the already-handled
    // case above, where it fires but the redirect hasn't navigated yet).
    // Live-confirmed: clicking Code in the few-hundred-ms-to-low-seconds
    // window before the agent's first vfs_create_project/vfs_set_project_
    // metadata response lands still saw chatBoundProjectId as null and fell
    // through to creating a brand-new, unrelated vfs project.
    const chatBoundProjectIdRef = useRef(null);

    // BUG FIX: Home's own omnibar (handleStartChatFromHome) mints
    // conversationId CLIENT-SIDE (newChatConversationId()) and puts it in
    // the URL immediately, before the agent's session even exists — but
    // ADK's own session-creation isn't guaranteed to actually use that
    // exact requested id (still not fully root-caused on the backend side
    // — see the session-id investigation). Once a real project exists,
    // `.mia/project.json`'s own conversationId (written by
    // vfs_ensure_project/vfs_create_project from the session ADK actually
    // used) is the ONE authoritative value — live-confirmed: refreshing
    // mid-conversation on the Home-minted URL loses the chat entirely
    // (starts a new one), while opening the SAME conversation via the
    // Projects list (which reads this same real conversationId, not a
    // client guess) resumes it correctly. Once a project is bound, pull
    // its real conversationId and correct the URL to match — so a refresh
    // from this point on resumes correctly too, exactly like the Projects-
    // list path already does.
    // BUG FIX: this used to only run on the plain /chat door
    // (`hideCanvas`), keyed off `chatBoundProjectId`. Live-reported (with a
    // real screenshot): opening a project directly on the CODE route — via
    // Activities, a bookmark, or any navigation that baked in a stale/
    // client-guessed conversationId — showed `.mia/project.json`'s real
    // conversationId in the Files tab while the URL, and the chat panel,
    // both used a DIFFERENT, wrong one — landing on a blank "Flow
    // Assistant" welcome state instead of resuming the real conversation
    // that built the project. Nothing on the Code route ever ran this same
    // correction. Generalized to run on EITHER route: `params.projectId`
    // (from the URL itself, immediate — Code mode) or `chatBoundProjectId`
    // (Chat mode, set once the agent binds a project) — whichever is
    // known.
    const urlCorrectionProjectId = hideCanvas ? chatBoundProjectId : params.projectId;

    // Extracted from the effect below so it can ALSO be called from
    // VfsChatPanel's onCodeReady (see that wiring further down) — see the
    // BUG FIX comment right above the effect's own call site for why a
    // single early call isn't enough.
    // The conversation started by the chat's "New chat" / `/new` / `/clear` inside a project (handleNewChatInProject).
    const freshConversationIdRef = useRef(null);
    const correctConversationIdInUrl = useCallback((projectIdToCheck) => {
        if (!projectIdToCheck) return;
        vfsClient.readProjectMetadata(projectIdToCheck).then((metadata) => {
            if (!metadata?.conversationId) {
                devLog('🔗 correctConversationIdInUrl: no conversationId in project.json yet', { projectIdToCheck });
                return;
            }
            // BUG FIX (severe, live-reported "switching to Code loses the
            // project"): navigate using the LIVE URL (window.location, not
            // any closured React Router `location`) — this can be called
            // well after the effect that originally scheduled it, so a
            // stale closured pathname would silently navigate back and
            // undo whatever the user did in between. Bail out if the user
            // has since left both /chat and /code — this correction only
            // ever makes sense on one of those two.
            //
            // ROOT CAUSE (finally found it, via the devLog added above —
            // "the same" symptom every single test, unconditionally, for
            // days): this whole function has ALWAYS bailed out here in
            // this environment. `window.location.pathname` is the raw
            // browser path, e.g. "/flow/chat" — it includes the Router's
            // `basename` (`import.meta.env.BASE_URL`, "/flow/" here, see
            // index.jsx). `startsWith('/chat')` can never match a path
            // that starts with "/flow/chat" — so this check failed on
            // EVERY call, on every single test, completely independent of
            // every other fix so far (the resync fixes, the turn-complete
            // wiring — all correctly delivered project.json's real
            // conversationId, but this one line threw it away every time
            // before ever reaching the navigate() below). `navigate()`
            // itself expects a basename-RELATIVE path (react-router
            // re-adds the basename internally — see the other navigate()
            // calls in this file, none of which ever include "/flow"), so
            // even a passing check would have double-prefixed the URL to
            // "/flow/flow/chat". Strip the basename once, up front, and
            // use that normalized path for both the check and the
            // navigate() target.
            const basename = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
            const rawPathname = window.location.pathname;
            const routePathname = basename && rawPathname.startsWith(basename)
                ? (rawPathname.slice(basename.length) || '/')
                : rawPathname;
            if (!routePathname.startsWith('/chat') && !routePathname.startsWith('/code')) {
                devLog('🔗 correctConversationIdInUrl: skipped, not on /chat or /code', { rawPathname, routePathname });
                return;
            }
            const currentParams = new URLSearchParams(window.location.search);
            // A conversation the person just started with "New chat" / `/new` / `/clear` in this project: project.json
            // still names the previous one until the new conversation's first vfs_ensure_project rewrites it, and
            // "correcting" the URL back would reopen the conversation they just left (see handleNewChatInProject).
            if (freshConversationIdRef.current && currentParams.get('conversationId') === freshConversationIdRef.current) {
                devLog('🔗 correctConversationIdInUrl: skipped, a fresh conversation in this project', { projectIdToCheck });
                return;
            }
            if (currentParams.get('conversationId') === metadata.conversationId) {
                devLog('🔗 correctConversationIdInUrl: already matches', { conversationId: metadata.conversationId });
                return;
            }
            devLog('🔗 correctConversationIdInUrl: correcting URL', {
                projectIdToCheck,
                urlConversationId: currentParams.get('conversationId'),
                realConversationId: metadata.conversationId,
                routePathname,
            });
            // ROOT CAUSE (live-reported: embedded preview panel vanished the
            // instant this correction ran, replaced by a non-functional
            // floating "Open Preview" button): this navigate() changes the
            // URL's conversationId, which IS the `conversationId` state read
            // elsewhere (search params) — and a separate, pre-existing
            // effect keyed on `[conversationId]` (see
            // skipNextConversationResetRef's own doc above, a few hundred
            // lines below this function) wipes chatBoundProjectId/
            // chatCodeReady back to null on EVERY
            // conversationId change, as a guard against genuine conversation
            // switches. That effect already has a one-shot skip flag built
            // for exactly this class of "we're rewriting the URL's
            // conversationId ourselves, not switching conversations" case —
            // originally only set before handleSaveConversation's own
            // conversationId overwrite. This function does the exact same
            // kind of self-inflicted URL rewrite and needs the same guard,
            // which it never had — so every successful correction was
            // immediately undoing the very state (chatBoundProjectId) it
            // depends on other code paths keeping intact.
            skipNextConversationResetRef.current = true;
            currentParams.set('conversationId', metadata.conversationId);
            navigate(`${routePathname}?${currentParams.toString()}`, { replace: true });
        }).catch((error) => {
            // Best-effort — same as every other readProjectMetadata call site.
            devLog('🔗 correctConversationIdInUrl: readProjectMetadata failed', { projectIdToCheck, error });
        });
    }, [navigate]);

    useEffect(() => {
        // BUG FIX (live-reported, real screenshot: URL conversationId
        // stayed the Home-minted client-side guess while `.mia/
        // project.json` had a DIFFERENT, real one from ADK): this effect
        // only re-runs when urlCorrectionProjectId's IDENTITY changes —
        // which happens exactly ONCE, the moment a project first gets
        // bound (chatBoundProjectId flips from null to a real id). But
        // `.mia/project.json`'s conversationId field is written by a
        // LATER agent tool call (vfs_set_project_metadata), not
        // necessarily done yet at that first moment — so this one-shot
        // check can run too early, see no mismatch (or no conversationId
        // at all) yet, and never re-check again for the rest of the
        // session even once the real value lands. Calling the same
        // correction again from onCodeReady (below, fired after
        // forceResyncServiceFromVfs — the same "content is now final"
        // signal used to fix the stale-IndexedDB bug) closes that gap.
        correctConversationIdInUrl(urlCorrectionProjectId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlCorrectionProjectId]);

    // Set once this conversation actually starts (VfsChatPanel's
    // onActivityStart, fired from the widget's own onChatStarted — earlier
    // than any tool call). Live-confirmed the short poll above still wasn't
    // enough on its own: some turns take 10+ seconds before their first
    // vfs_ensure_project response lands (real durations seen: 2.8s, 10.1s),
    // and clicking Code inside that window still created a brand-new,
    // unrelated vfs project instead of the real one the chat was
    // about to create — explicit product rule: Code must NEVER create a
    // project when this chat already has (or is about to have) its own.
    // goToMode disables the Code option outright while this is true and no
    // project is bound yet, rather than racing a fallback against however
    // long the agent's first tool call happens to take.
    const [chatHasActivity, setChatHasActivity] = useState(false);
    // Explicit instruction: disable Code until BOTH the preview has
    // actually rendered AND IndexedDB has been force-reconciled from
    // vfs-service for it — narrower/later than chatHasActivity (which only
    // means "a project is bound", not "its files are trustworthy yet").
    // Set once by VfsChatPanel's onCodeReady, the moment
    // forceResyncServiceFromVfs resolves for vfs_get_preview_url's project/
    // service — see that callback's own comment. Until this is true,
    // vfs-service is the one master; once true, and once the user opens
    // Monaco for the first time (MonacoFileEditor's own one-time freshness
    // check), IndexedDB and vfs-service are both back in normal two-way
    // sync.
    const [chatCodeReady, setChatCodeReady] = useState(false);
    // Live-confirmed regression: `handleSaveConversation`'s auto-save (fired
    // right after a NEW conversation's first complete exchange — the same
    // turn that just bound the project via onProjectBound) calls
    // `setConversationId(result.conversationId)` with mia-flow-be's own
    // legacy chat-history id — a completely different namespace from the
    // real ADK/VFS session id this same `conversationId` state otherwise
    // holds — and then overwrites the URL's `?conversationId=` with it too.
    // That state change is indistinguishable, to the effect below, from the
    // user genuinely switching to a different conversation, so it fired the
    // same reset: chatBoundProjectId/chatHasActivity all wiped back to null
    // moments after the turn had just set them, killing the "Code must not
    // create a new project" guard for the rest of the session. Set this ref
    // right before
    // that specific `setConversationId` call so this effect can skip its
    // reset exactly once for that internal bookkeeping change, while still
    // resetting normally on every real navigation (New Chat, switching to a
    // different saved conversation, etc).
    const skipNextConversationResetRef = useRef(false);
    // Set by handleNewChatInProject on the /chat door: the URL's conversationId is dropped for a fresh conversation
    // in the SAME project, which must not unbind that project (one-shot, like skipNextConversationResetRef).
    const keepChatBindingRef = useRef(false);
    useEffect(() => {
        if (skipNextConversationResetRef.current) {
            skipNextConversationResetRef.current = false;
            return;
        }
        const keepBinding = keepChatBindingRef.current;
        keepChatBindingRef.current = false;
        // A conversation started with "New chat" / `/new` / `/clear` inside a project stays in that project.
        if ((keepBinding && !conversationId) || (conversationId && conversationId === freshConversationIdRef.current)) {
            return;
        }
        setChatBoundProjectId(null);
        chatBoundProjectIdRef.current = null;
        setChatHasActivity(false);
        setChatCodeReady(false);
    }, [conversationId]);

    // "New chat", `/new` and `/clear` inside a project (owner decision, 2026-10-31): a fresh conversation in the SAME
    // project, never a new project (that is Projects › New Project or `/project new`). VfsChatPanel keeps the
    // project bound (its `x-mia-vfs-project` header); here the URL moves off the previous conversation, which stays
    // in the project's history (/conversations, /resume).
    // - Code door: a fresh conversationId is minted into the URL, as Home does for a new chat. The panel's key
    //   includes it, so the panel remounts on the same project with that id as its `initialSessionId`, and the
    //   first message creates the session under it.
    // - /chat door: the panel is not remounted (its key is fixed) and the widget's new conversation gets its id only
    //   once the first message creates it, so the old id is dropped now and the new one put in the URL by
    //   handleFreshConversationStarted.
    const handleNewChatInProject = useCallback(() => {
        const freshId = hideCanvas ? null : newChatConversationId();
        freshConversationIdRef.current = freshId;
        const nextSearch = searchForNewChatInProject(location.search, freshId);
        if (nextSearch === null) { return; }
        if (hideCanvas) { keepChatBindingRef.current = true; }
        navigate(`${location.pathname}${nextSearch}`, { replace: true });
    }, [hideCanvas, location.pathname, location.search, navigate]);
    const handleFreshConversationStarted = useCallback((sessionId) => {
        keepChatBindingRef.current = false;
        const nextSearch = searchForFreshConversation(location.search, sessionId);
        if (nextSearch === null) { return; }
        freshConversationIdRef.current = sessionId;
        navigate(`${location.pathname}${nextSearch}`, { replace: true });
    }, [location.pathname, location.search, navigate]);

    // Track which sessions have already received full file content to avoid sending it multiple times
    const sessionFilesSent = useRef(new Set()).current;

    // Auto diff applier hook - enables automatic diff application from backend merge requests
    const { isApplyingDiff, diffProgress } = useAutoDiffApplier({
        conversationId: conversationId,
        projectId: canvasProjectContext?.projectId,
        serviceName: canvasProjectContext?.selectedMicroservice?.name,
        sessionId: sessionId,
        enabled: true,
        onDiffApplied: (result) => {
            devLog('✅ [AutoDiffApplier] Diff applied successfully:', result.filePath);

            // Add green success message to chat with clickable file link
            const fileName = result.filePath.split('/').pop();
            const successMsg = Messages.createInfoMessage(
                `✅ Successfully applied changes to [${fileName}](file://${result.filePath})\n\n` +
                `Click the filename to view the changes in the editor.`
            );
            setMessages(prevMessages => Messages.addMessage(prevMessages, successMsg));
        },
        onDiffError: (error) => {
            console.error('❌ [AutoDiffApplier] Diff application failed:', error);
            // Extract error message as string
            let errorMessage = 'Unknown error';
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error && typeof error === 'object') {
                errorMessage = error.error || error.message || JSON.stringify(error);
            }
            showToast(`Failed to apply diff: ${errorMessage}`, 'error');
        }
    });

    // Playbook application (src/hooks/usePlaybookApplication.js) and MCP
    // tools (src/hooks/useMcpTools.js) have a genuine two-way dependency —
    // see usePlaybookApplication.js's doc comment. toolsApiRef breaks the
    // cycle: usePlaybookApplication only reads toolsApiRef.current lazily
    // inside callbacks/effects, so it's safe to populate it right after both
    // hooks have been called, before either's own effects run.
    const toolsApiRef = useRef({});
    const {
        customAIInstructions,
        customAIInstructionsName,
        customAIInstructionsPlaybookData,
        playbookLocksTools,
        playbookToolIdsRef,
        buildPlaybookInstructions,
        handleApplyAIInstructions,
    } = usePlaybookApplication({
        location,
        authorizedFetch,
        hideCanvas,
        setAiMode,
        showToast,
        toolsApiRef,
    });

    // MCP Tools state + handlers — see hooks/useMcpTools.js
    const {
        availableTools, setAvailableTools,
        toolsLoadError, setToolsLoadError,
        toolsAuthRequired, setToolsAuthRequired,
        selectedTools, setSelectedTools,
        expandedCategories, setExpandedCategories,
        sessionTools, setSessionTools,
        showToolChangeNotice, setShowToolChangeNotice,
        toolsModalOpenRef,
        availableToolsRef,
        selectedToolsRef,
        miaFlowToolIdsRef,
        toggleCategoryExpansion,
    } = useMcpTools(customAIInstructionsPlaybookData, playbookToolIdsRef);
    toolsApiRef.current = {setSelectedTools, availableToolsRef, miaFlowToolIdsRef};

    // Persist session state to localStorage (per project)
    useEffect(() => {
        if (sessionId && canvasProjectContext?.projectId) {
            try {
                const projectId = canvasProjectContext.projectId;
                const sessionState = {
                    sessionId,
                    conversationId: conversationId || null,
                    timestamp: Date.now(),
                    wasStreaming: isLoading // Track if we're currently streaming
                };
                localStorage.setItem(`chatSessionState_${projectId}`, JSON.stringify(sessionState));
            } catch (e) {
                console.error('Failed to save session state to localStorage:', e);
            }
        }
    }, [sessionId, conversationId, isLoading, canvasProjectContext?.projectId]);

    // Persist messages to localStorage (per project)
    useEffect(() => {
        if (messages.length > 0 && canvasProjectContext?.projectId) {
            try {
                const projectId = canvasProjectContext.projectId;
                localStorage.setItem(`chatMessagesBackup_${projectId}`, JSON.stringify(messages));
            } catch (e) {
                console.error('Failed to save messages to localStorage:', e);
            }
        }
    }, [messages, canvasProjectContext?.projectId]);

    // Track previous projectId to detect actual project switches
    const prevProjectIdRef = useRef(null);
    const hasRestoredRef = useRef(false);

    // Effect to restore session when project is loaded or changes
    useEffect(() => {
        const currentProjectId = canvasProjectContext?.projectId;
        const previousProjectId = prevProjectIdRef.current;

        // A URL-specified conversationId (?conversationId=X) means a specific,
        // real conversation was requested and is already being loaded by the
        // fetchConversation effect below (via Conversations.fetchConversation) —
        // that response is the source of truth. Without this guard, this
        // effect's localStorage restore (designed for the "just reopened this
        // project, no conversation in the URL" case) can fire after the fetch
        // resolves and clobber the real, just-loaded messages with whatever
        // stale session happened to be cached for this project.
        const hasUrlConversationId = Boolean(new URLSearchParams(location.search).get('conversationId'));

        if (currentProjectId && !hasUrlConversationId) {
            try {
                const savedSession = localStorage.getItem(`chatSessionState_${currentProjectId}`);
                const savedMessages = localStorage.getItem(`chatMessagesBackup_${currentProjectId}`);

                if (savedSession && savedMessages) {
                    const parsed = JSON.parse(savedSession);
                    const messagesParsed = JSON.parse(savedMessages);

                    // Check if session is recent (less than 24 hours old)
                    const isRecent = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;

                    if (isRecent) {
                        // Always restore if we haven't restored yet (initial load from IndexedDB)
                        // OR if we're switching to a different project
                        const shouldRestore = !hasRestoredRef.current ||
                                             (previousProjectId && previousProjectId !== currentProjectId);

                        if (shouldRestore && parsed.sessionId !== sessionId) {
                            setSessionId(parsed.sessionId);
                            setMessages(messagesParsed);
                            // Restore conversationId to prevent auto-save from treating
                            // the restored messages as a new unsaved conversation
                            if (parsed.conversationId) {
                                setConversationId(parsed.conversationId);
                            }
                            // Record how many messages were restored so auto-save
                            // won't fire unless new messages are added this session
                            restoredMessageCountRef.current = messagesParsed.length;
                            devLog('📥 Restored session for project:', currentProjectId, 'Session:', parsed.sessionId);
                            hasRestoredRef.current = true;
                        }
                    }
                } else {
                    // Only clear if we're switching FROM another project TO a different project
                    // Don't clear on initial mount. Note: no `&& sessionId` guard here — the
                    // previous project's messages can still be showing even when sessionId is
                    // already null (e.g. it was cleared earlier), and this project has no saved
                    // backup of its own, so there's nothing to restore; the stale messages must
                    // still be cleared or the chat keeps showing the wrong project's conversation.
                    if (previousProjectId && previousProjectId !== currentProjectId) {
                        devLog('🆕 Switching to new project, creating new session');
                        setSessionId(null);
                        setMessages([]);
                        hasRestoredRef.current = false;
                    }
                }
            } catch (e) {
                console.error('Failed to restore session for project:', e);
            }
        }

        // Update the ref with current projectId
        prevProjectIdRef.current = currentProjectId;
    }, [canvasProjectContext?.projectId, sessionId, location.search]); // Trigger when projectId, sessionId, or the URL's conversationId changes

    // Check if we need to reconnect to a streaming session (per project)
    // Only run once when project or sessionId changes, not on every message
    useEffect(() => {
        if (canvasProjectContext?.projectId && sessionId) {
            try {
                const projectId = canvasProjectContext.projectId;
                const savedSession = localStorage.getItem(`chatSessionState_${projectId}`);

                if (savedSession && messages.length > 0) {
                    const parsed = JSON.parse(savedSession);

                    // Check if we were streaming AND the session hasn't been checked yet
                    const lastMessage = messages[messages.length - 1];
                    const wasStreaming = parsed.wasStreaming || false;
                    const lastCheckedTimestamp = parsed.lastCheckedTimestamp || 0;

                    // Only show the message if:
                    // 1. We were streaming
                    // 2. Last message is received
                    // 3. We haven't checked this session yet (prevent multiple triggers)
                    if (wasStreaming && lastMessage && lastMessage.type === 'received' &&
                        parsed.timestamp > lastCheckedTimestamp) {
                        devLog('🔄 Detected interrupted stream, session can continue...');
                        // Set a flag that we recovered from interruption
                        setConversationStatus('Session restored - ready to continue');
                        setTimeout(() => setConversationStatus(''), 3000);

                        // Mark this session as checked to prevent repeated triggers
                        parsed.lastCheckedTimestamp = Date.now();
                        localStorage.setItem(`chatSessionState_${projectId}`, JSON.stringify(parsed));
                    }
                }
            } catch (e) {
                console.error('Failed to check for reconnection:', e);
            }
        }
    }, [canvasProjectContext?.projectId, sessionId]); // Only re-check when project or session changes

    // Persist canvas content to localStorage
    useEffect(() => {
        if (canvasContent) {
            localStorage.setItem('canvasContent', JSON.stringify(canvasContent));
        } else {
            localStorage.removeItem('canvasContent');
        }
    }, [canvasContent]);

    // Persist AI mode to localStorage
    useEffect(() => {
        localStorage.setItem('aiMode', aiMode);

        // Canvas is always active, notify parent
        window.dispatchEvent(new CustomEvent('canvasToggled', {
            detail: {isActive: true}
        }));

        // Auto-populate canvas with content when switching modes
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.type === 'received') {
            setCanvasContent({
                type: 'document',
                content: lastMessage.text,
                title: 'Generated Content'
            });
        } else if (!canvasContent) {
            // Provide placeholder content only if no content exists
            setCanvasContent({
                type: 'document',
                content: '# Ready to Flow!?! Create your first service or load a project to get started.',
                title: 'Canvas'
            });
        }
    }, [aiMode, messages]);

    // MCP tools fetch/default-selection/persistence effects now live in useMcpTools() above.

    // REMOVED (dead code — live-reported console noise: POST .../api/suggestions
    // 404ing on every "New chat"): this effect fetched dynamic prompt
    // suggestions into `promptSuggestions`/`isSuggestionsLoading`, but
    // neither was ever passed to VfsChatPanel/the chat widget — the "Try
    // with" cards actually shown are the vendored widget's own static
    // defaults (see its README's `suggestedPrompts`/`overview.prompts`
    // props, never wired up here). The fetch ran, 404'd, and its result
    // was silently discarded on every render of the empty state — pure
    // overhead and console noise with zero effect on what the user sees.

    // Hide banner when user clicks anywhere on screen
    useEffect(() => {
        const handleClickAnywhere = () => {
            if (showSendPromptBanner) {
                setShowSendPromptBanner(false);
            }
        };

        if (showSendPromptBanner) {
            document.addEventListener('click', handleClickAnywhere);
        }

        return () => {
            document.removeEventListener('click', handleClickAnywhere);
        };
    }, [showSendPromptBanner]);

    // Tool-ID re-resolution, session-tool-change detection, and expanded-category
    // persistence now live in useMcpTools() above.

    // Handle tool selection

    // Handle category selection (select/deselect all tools in category)

    // Cleanup browser sessions
    const cleanedSessionsRef = useRef(new Set());
    const messagesRef = useRef(messages);
    const prevUrlConversationIdRef = useRef(null); // Track previous conversationId from URL

    // Keep messagesRef in sync with messages
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const cleanupBrowserSessions = useCallback(() => {
        // Find all browser session IDs in current messages
        const browserSessionIds = messagesRef.current
            .filter(msg => msg.browserSessionId)
            .map(msg => msg.browserSessionId)
            .filter(sessionId => !cleanedSessionsRef.current.has(sessionId)); // Only cleanup uncleaned sessions

        if (browserSessionIds.length > 0) {
            devLog('🧹 Cleaning up browser sessions:', browserSessionIds);

            // Call cleanup API for each session
            browserSessionIds.forEach(async (sessionId) => {
                // Mark as cleaned immediately to prevent duplicate calls
                cleanedSessionsRef.current.add(sessionId);

                try {
                    const response = await authorizedFetch(`${config.bffUrl}/api/browser/${sessionId}/cleanup`, {
                        method: 'POST',
                        headers: getBffHeaders()
                    });

                    if (response.ok) {
                        devLog(`✅ Browser session ${sessionId} cleaned up`);
                    } else {
                        console.warn(`⚠️ Failed to cleanup browser session ${sessionId}`);
                        // Remove from cleaned set on failure so it can be retried
                        cleanedSessionsRef.current.delete(sessionId);
                    }
                } catch (error) {
                    console.error(`❌ Error cleaning up browser session ${sessionId}:`, error);
                    // Remove from cleaned set on error so it can be retried
                    cleanedSessionsRef.current.delete(sessionId);
                }
            });
        }
    }, []);

    // Close dropdowns when clicking outside — see hooks/useOutsideClick.js
    useOutsideClick(isAiModeDropdownOpen, '.ai-mode-selector', () => setIsAiModeDropdownOpen(false));
    useOutsideClick(isMoreOptionsOpen, '.more-options-dropdown-container', () => setIsMoreOptionsOpen(false));
    useOutsideClick(showSendOptions, '.send-options-container', () => setShowSendOptions(false));

    // Effect to monitor token usage
    useEffect(() => {
        const {maxTokens, warningThreshold} = config.tokenLimits;
        const warningLimit = maxTokens * warningThreshold; // 80% = 25.6K

        if (totalTokens >= warningLimit) {
            // 80% limit reached - show warning
            // This warning only shows once per threshold crossing
            const warningShown = sessionStorage.getItem('tokenWarningShown');
            if (!warningShown) {
                showToast(`⚠️ Approaching token limit: ${totalTokens.toLocaleString()}/${maxTokens.toLocaleString()} tokens used`, 'info');
                sessionStorage.setItem('tokenWarningShown', 'true');
            }
        }
    }, [totalTokens, messages.length, showToast]);

    // Effect to load a conversation from the URL when the component mounts or URL changes.
    useEffect(() => {
        // Subscribe to context manager updates
        const unsubscribeContext = contextManager.subscribe((newContext) => {
            setAiContext(newContext);
        });

        // Listen for force new chat event
        const handleForceNewChat = () => {
            // Cleanup browser sessions before resetting chat
            cleanupBrowserSessions();

            // Force reset regardless of current state
            setMessages([]);
            setConvName('');
            setConversationId(null);
            setSessionId(null); // ✅ CRITICAL: Clear session ID to force new session creation
            setSessionTools(null); // Reset session tools for new chat
            setShowToolChangeNotice(false); // Clear tool change notice
            setInputValue('');
            setIsLoading(false);
            setConversationStatus('');
            setTotalTokens(0); // Reset token count
            setInputTokens(0);
            setOutputTokens(0);
            sessionStorage.removeItem('tokenWarningShown'); // Reset warning flag
            manualCanvasSetRef.current = false; // Reset canvas manual flag
            contextManager.clearContext(); // Clear AI context
            if (abortController) {
                abortController.abort();
                setAbortController(null);
            }
            // Clear AdkClient's session file tracking to force resending files on next message
            if (sessionId) {
                AdkClient.clearSessionFiles(sessionId);
                // eslint-disable-next-line no-console
                devLog('🔄 Cleared session file tracking for new chat');
            }
            // Clear localStorage
            try {
                localStorage.removeItem('chatInputValue');
                // Clear all project-specific sessions
                const keys = Object.keys(localStorage);
                keys.forEach(key => {
                    if (key.startsWith('chatSessionState_') || key.startsWith('chatMessagesBackup_')) {
                        localStorage.removeItem(key);
                    }
                });
            } catch (e) {
                console.error('Failed to clear localStorage:', e);
            }
        };

        window.addEventListener('forceNewChat', handleForceNewChat);

        // Listen for MCP Apps feature flag changes
        const handleFeatureToggleChange = (event) => {
            if (event.detail?.enableMcpApps !== undefined) {
                setEnableMcpApps(event.detail.enableMcpApps);
            }
        };

        window.addEventListener('featureToggleChanged', handleFeatureToggleChange);

        // Live-confirmed real bug: this whole legacy conversation-hydration
        // path (Conversations.fetchConversation, the old mia-flow-be-backed
        // system) is dead in the real app — chat always renders through
        // VfsChatPanel now, in both canvas and hideCanvas modes (see the
        // "Handle initial prompt from Home page navigation" effect's own
        // comment for the same conclusion), which owns loading its own
        // conversation history via its real `initialSessionId`/ADK session
        // lookup. But this effect below still fires whenever a
        // `conversationId` appears in the URL — which VfsChatPanel itself
        // adds once a real ADK session starts — and tries to fetch that
        // SAME id from the old mia-flow-be backend, which has never heard
        // of it (that conversation was never created there; it's a real ADK
        // session created directly). Confirmed live: a plain "this is a
        // test" prompt from Home produced two visible 404 error toasts
        // (`/api/be/conversations/:id`, `/api/adk/apps/...`) even though
        // the real chat worked fine underneath them — worse, the toast
        // fired even on a genuinely NEW, never-saved conversation, since
        // mia-flow-be predictably has never heard of ANY real ADK session
        // id. Disabled outright: a no-op, not just a "pretend it failed"
        // stub — a fake failure would still hit the `else` branch below and
        // show its own "Failed to load conversation" toast on every single
        // navigation, which is exactly the same class of bug, just always
        // instead of conditionally.
        const fetchConversation = async () => {
            // Disabled — see the comment above for why.
        };
        // Read conversationId from URL and hydrate state
        const searchParams = createSearchParams(location.search);
        const conversationIdFromParams = searchParams.get('conversationId')

        if (conversationIdFromParams) {
            // There's a conversationId in URL - load it only if it's different from current.
            // Also guard against the effect re-firing mid-navigation (e.g. sync-URL redirect)
            // by comparing against prevUrlConversationIdRef which is updated synchronously.
            if (conversationIdFromParams !== conversationId && conversationIdFromParams !== prevUrlConversationIdRef.current) {
                setConversationId(conversationIdFromParams);
                prevUrlConversationIdRef.current = conversationIdFromParams;
                fetchConversation();
            } else if (conversationIdFromParams !== prevUrlConversationIdRef.current) {
                // Update the ref to track this conversationId
                prevUrlConversationIdRef.current = conversationIdFromParams;
            }
        } else if (prevUrlConversationIdRef.current !== null && !isSavingRef.current && !isLoading) {
            // URL conversationId changed from something to null
            // Only clear if user explicitly navigated to "New Chat", NOT when navigating to tabs
            // Check if we're navigating to a project tab (service/dashboard/architecture/resources)
            const isNavigatingToTab = params.tab || params.microservice;

            if (!isNavigatingToTab) {
                // User clicked "New Chat" or navigated away from project - clear conversation
                // Only clear if:
                // 1. We had a conversationId in URL before (prevUrlConversationIdRef.current !== null)
                // 2. We're not in the middle of saving (to avoid race condition)
                // 3. We're not loading a response (to avoid clearing during SSE)
                // 4. We're not navigating to a project tab (new check)
                // This ensures conversation history persists when navigating within the project
                // eslint-disable-next-line no-console
                devLog('🧹 Clearing conversation - User navigated away', {
                    prevUrlConversationId: prevUrlConversationIdRef.current,
                    currentUrlConversationId: conversationIdFromParams,
                    messagesCount: messages.length
                });
                setMessages([]);
                setConvName('');
                setConversationId(null);
                setSessionTools(null); // Reset session tools
                setShowToolChangeNotice(false); // Clear tool change notice
                setTotalTokens(0); // Reset token count
                setInputTokens(0);
                setOutputTokens(0);
                sessionStorage.removeItem('tokenWarningShown'); // Reset warning flag
                // DON'T reset sessionId - keep the same session across conversations
                setInputValue('');
                setIsLoading(false);
                setConversationStatus('');
                if (abortController) {
                    abortController.abort();
                    setAbortController(null);
                }
                // Update ref to reflect no conversationId in URL
                prevUrlConversationIdRef.current = null;
            } else {
                // Navigating to a tab - keep conversation but don't update the ref
                // This allows the conversation to persist while viewing different tabs
                // eslint-disable-next-line no-console
                devLog('📌 Preserving conversation - navigating to tab', {
                    tab: params.tab,
                    microservice: params.microservice,
                    conversationId: prevUrlConversationIdRef.current,
                    messagesCount: messages.length
                });
            }
        }

        return () => {
            window.removeEventListener('forceNewChat', handleForceNewChat);
            window.removeEventListener('featureToggleChanged', handleFeatureToggleChange);
            unsubscribeContext();

            // Cleanup browser sessions on unmount
            cleanupBrowserSessions();
        };

    }, [location, showToast, abortController, cleanupBrowserSessions, isLoading]);

    // (moved into useCanvasProjectContext() above — DDD refactor Phase 4)


    // (moved into useCanvasProjectContext() above — DDD refactor Phase 4)


    // Fetch available spec commands from the catalog whenever the tenant changes
    const effectiveTenantIdForSpecs = params.tenantId
        || canvasProjectContext?.tenant?.tenantId
        || miaFlowAuth.getSelectedTenant()?.tenantId
        || '';
    useEffect(() => {
        if (!effectiveTenantIdForSpecs) return;
        let active = true;

        const loadCatalogSpecs = async () => {
            try {
                const baseUrl = config.bffUrl || window.location.origin;
                const qs = new URLSearchParams({ page: '1', perPage: '100' });
                const res = await authorizedFetch(`${baseUrl}/${AI_API_PREFIX}/ai-specs?${qs}`, { headers: getBffHeaders() });
                if (!active || !res.ok) return;
                const result = await res.json();
                if (active && result.success && result.data?.items) {
                    setCatalogSpecCommands(buildCommandsFromSpecs(result.data.items));
                }
            } catch { /* silently ignore */ }
        };

        loadCatalogSpecs();

        const handleSpecsChanged = () => loadCatalogSpecs();
        window.addEventListener('mia-flow-spec-commands-changed', handleSpecsChanged);
        return () => {
            active = false;
            window.removeEventListener('mia-flow-spec-commands-changed', handleSpecsChanged);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveTenantIdForSpecs]);


    // Effect to persist Canvas mode state to localStorage
    useEffect(() => {
        localStorage.setItem('canvasMode', isCanvasMode.toString());
    }, [isCanvasMode]);

    // Effect to persist chat sidebar width to localStorage
    useEffect(() => {
        localStorage.setItem('chatSidebarWidth', chatSidebarWidth.toString());
    }, [chatSidebarWidth]);

    // Effect to handle resize drag
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (resizeHandleRef.current && resizeHandleRef.current.dataset.dragging === 'true') {
                // BUG FIX: this used to set chatSidebarWidth straight from
                // e.clientX (raw viewport mouse X) — only correct if
                // .main-content's own left edge sits at viewport x=0, which
                // was never quite true (NavigationSidebar/FlowSidebar always
                // sits to its left) and became visibly wrong once
                // .main-content gained its own margin-left (see the
                // catalog-style-shell change in App.css): every drag now
                // computed a width offset by the sidebar's width plus that
                // margin, instead of tracking the cursor, effectively
                // breaking the resize (the reported "chat is no more
                // resizable"). Resolve the real container edge live, via
                // the handle's own offsetParent (.canvas-mode-chat-panel,
                // the position:relative ancestor `left: <px>` is already
                // resolved against) rather than assuming 0 — self-corrects
                // for any future layout change instead of hardcoding an
                // offset that can drift again.
                const container = resizeHandleRef.current.offsetParent;
                const containerRect = container?.getBoundingClientRect();
                const containerLeft = containerRect?.left ?? 0;
                // BUG FIX: `container` here (the handle's own offsetParent)
                // is `.canvas-mode-chat-panel` — the panel BEING resized,
                // not the outer shell. Its LEFT edge is fine to use above
                // (the panel sits flush against .main-content's own left
                // edge, so both share the same x), but its WIDTH is
                // self-referential — the panel's current, already-small
                // width, not the full space available to grow into. Using
                // it for `containerWidth` below meant every drag right
                // computed a `maxChatWidth` barely above wherever the panel
                // ALREADY was, capping growth to almost nothing — live-
                // reported as "cannot increase the width" (confirmed: the
                // handle moved a few px then refused to go further, exactly
                // matching a ceiling that shrinks in lockstep with the
                // panel it's supposed to be bounding). #canvas-portal-root
                // is `.main-content` itself (App.jsx's own id on it) — the
                // actual outer shell whose full width the chat panel can
                // grow into.
                const outerContainer = document.getElementById('canvas-portal-root');
                const containerWidthRect = outerContainer?.getBoundingClientRect();
                const containerWidth = containerWidthRect?.width ?? containerRect?.width ?? window.innerWidth;
                const newWidth = e.clientX - containerLeft;
                // BUG FIX: the fixed `newWidth <= containerWidth - 400` upper
                // bound, paired with the fixed `newWidth >= 300` lower bound,
                // becomes mathematically impossible to satisfy the instant
                // containerWidth drops under 700px (upper bound < lower
                // bound) — silently making every drag a no-op regardless of
                // mouse position, not just clamped to an edge. Live-reported
                // "widget cannot be resized" at a narrower Code-mode
                // viewport (more reachable now that the sidebar itself
                // collapses responsively down to phone width instead of
                // always claiming a fixed 248px). Clamp the upper bound to
                // never fall below the lower bound instead of hardcoding an
                // "other side" minimum that assumes a wide viewport —
                // resize stays draggable at any container width, even if
                // the other side ends up narrower than its nominal target
                // in an extreme case.
                // Explicit ask was 100px-1200px, but 100px is NOT actually
                // safe: the vendored chatbot-widget hard-codes
                // `min-width: 280px` on any chat bubble that contains
                // tool-call steps (._bubbleHasSteps, style.css) — a bubble
                // like that simply refuses to shrink below 280px no matter
                // how narrow this outer panel gets, so at 100px its content
                // visibly overflowed and overlapped the resize
                // handle/canvas, live-confirmed via screenshot (the "Vfs
                // write" tool-call cards spilling past the 100px-wide
                // panel, handle sitting on top of the chat text). 280px is
                // the real, vendored-package-enforced floor for any
                // conversation with tool calls — going lower breaks
                // rendering regardless of what this clamp allows. Ceiling
                // stays at 1200px per the original ask. minOtherWidth is
                // just enough to keep the divider/canvas edge clickable,
                // not a hard reservation for the other pane.
                const minChatWidth = 280;
                const maxAllowedChatWidth = 1200;
                const minOtherWidth = 40;
                const maxChatWidth = Math.max(
                    minChatWidth,
                    Math.min(maxAllowedChatWidth, containerWidth - minOtherWidth)
                );
                if (newWidth >= minChatWidth && newWidth <= maxChatWidth) {
                    setChatSidebarWidth(newWidth);
                }
            }
        };

        const handleMouseUp = () => {
            if (resizeHandleRef.current) {
                resizeHandleRef.current.dataset.dragging = 'false';
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                // See the mousedown handlers' own comment (ChatColumn's
                // local handle + the main chat-resize-handle below) for why
                // this class exists.
                document.body.classList.remove('resizing-chat-sidebar');
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    // (moved into useCanvasProjectContext() above — DDD refactor Phase 4)

    // Listen for file changes in Canvas mode
    useEffect(() => {
        const handleCanvasFileChange = (event) => {
            const {projectId, serviceName, state, deleted} = event.detail || {};

            // Only track changes if we're in Canvas mode and have a project context
            if (isCanvasMode && canvasProjectContext &&
                projectId === canvasProjectContext.projectId &&
                serviceName === canvasProjectContext.selectedMicroservice?.name) {

                // Check if this is a file addition or deletion
                const isNewFile = state === GIT_STATE.NEW;
                const isDeleted = deleted === true;

                if (isNewFile || isDeleted) {
                    // File was added or removed - clear session tracking to resend all files
                    if (sessionId) {
                        AdkClient.clearSessionFiles(sessionId);
                        // eslint-disable-next-line no-console
                        devLog(`📂 File ${isNewFile ? 'added' : 'deleted'} - cleared session tracking. All files will be resent on next message.`);
                    }
                    // Also clear chat history on next message
                    setFilesModifiedInCanvas(true);
                } else {
                    // Regular file modification
                    // eslint-disable-next-line no-console
                    devLog('📝 File modified in Canvas mode - will clear chat history on next message');
                    setFilesModifiedInCanvas(true);
                }
            }
        };

        window.addEventListener('monacoFileChanged', handleCanvasFileChange);

        return () => {
            window.removeEventListener('monacoFileChanged', handleCanvasFileChange);
        };
    }, [isCanvasMode, canvasProjectContext]);

    // Handle ESC key to close fullscreen mode
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    // Effect to persist Canvas project context to IndexedDB
    useEffect(() => {
        const saveCanvasContext = async () => {
            try {
                if (canvasProjectContext && canvasProjectContext.projectId) {
                    await indexedDbStorage.setItem('canvasProjectContext', canvasProjectContext);
                    // eslint-disable-next-line no-console
                    devLog('💾 Saved Canvas project context to IndexedDB');
                }
                // Don't remove from IndexedDB when context is null - keep the saved context
                // This allows restoration when navigating back from home
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to save Canvas project context:', error);
                // Show user-friendly error message
                // eslint-disable-next-line no-console
                console.warn('Canvas project context could not be saved. Your selections may not persist.');
            }
        };
        saveCanvasContext();
    }, [canvasProjectContext]);

    // Ref to track last processed message ID for context updates
    const lastContextMessageIdRef = useRef(null);

    // Effect to update AI context when messages change
    useEffect(() => {
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            // Update context from assistant messages (only final messages, not intermediate)
            if (lastMessage.type === 'received' && !lastMessage.isIntermediate) {
                // Only update if this is a new message (not already processed)
                if (lastMessage.id !== lastContextMessageIdRef.current) {
                    lastContextMessageIdRef.current = lastMessage.id;
                    contextManager.updateContextFromMessage(lastMessage);
                }
            }
        }
    }, [messages]);



    // Smart auto-scroll: scroll automatically unless user has manually scrolled up
    useEffect(() => {
        if (!chatMessagesRef.current) return;

        const container = chatMessagesRef.current;
        const hasNewContent = container.scrollHeight !== lastScrollHeightRef.current;

        // Auto-scroll if enabled and there's new content
        if (shouldAutoScrollRef.current && hasNewContent) {
            // Use requestAnimationFrame to batch scroll updates and reduce flicker
            requestAnimationFrame(() => {
                if (container) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'auto' // Use 'auto' for instant scroll during streaming to avoid lag
                    });
                    lastScrollHeightRef.current = container.scrollHeight;
                }
            });
        }
    }, [messages, conversationStatus]);

    // Track user scroll to detect manual scrolling away from bottom
    useEffect(() => {
        const container = chatMessagesRef.current;
        if (!container) return;

        const handleScroll = () => {
            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
            // Only disable auto-scroll if user has scrolled up (not near bottom)
            // If they scroll back down near bottom, re-enable auto-scroll
            shouldAutoScrollRef.current = isNearBottom;
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // Auto-detect Canvas-worthy content in new messages
    // Memoize the last message to prevent unnecessary effect triggers
    const lastMessage = useMemo(() => {
        return messages.length > 0 ? messages[messages.length - 1] : null;
    }, [messages]);

    // Use a ref to track the last processed message ID to avoid redundant updates
    const lastProcessedMessageIdRef = useRef(null);

    useEffect(() => {
        if (!lastMessage) return;

        // Skip if we already processed this message
        if (lastProcessedMessageIdRef.current === lastMessage.id) {
            return;
        }

        // Skip auto-detection if canvas was manually set (e.g., for direct image generation)
        if (manualCanvasSetRef.current) {
            return;
        }

        // Mark this message as processed
        lastProcessedMessageIdRef.current = lastMessage.id;

        // Update Canvas content in real-time, even for intermediate messages
        // Canvas is always active, so update it for every received message
        if (lastMessage?.type === 'received') {
            // Check for special [IMAGE_DATA] markers first
            const imageDataMatch = lastMessage.text.match(/\[IMAGE_DATA\](data:image\/[^[]+)\[\/IMAGE_DATA\]/);
            if (imageDataMatch) {
                const imageUrl = imageDataMatch[1];
                // Extract prompt and aspect ratio from the message
                const promptMatch = lastMessage.text.match(/\*\*Prompt:\*\*\s*(.+)/);
                const aspectRatioMatch = lastMessage.text.match(/\*\*Aspect Ratio:\*\*\s*(\S+)/);

                setCanvasContent({
                    type: 'image',
                    content: imageUrl,
                    title: 'Generated Image',
                    prompt: promptMatch ? promptMatch[1].trim() : null,
                    aspect_ratio: aspectRatioMatch ? aspectRatioMatch[1].trim() : '1:1'
                });
            } else {
                // Fallback to old detection method
                const imageUrlMatch = lastMessage.text.match(/data:image\/[^;]+;base64,[^\s)]+/);
                if (imageUrlMatch) {
                    const imageUrl = imageUrlMatch[0];
                    const promptMatch = lastMessage.text.match(/(?:prompt|description):\s*["']?([^"'\n]+)["']?/i);
                    setCanvasContent({
                        type: 'image',
                        content: imageUrl,
                        title: 'Generated Image',
                        prompt: promptMatch ? promptMatch[1] : null,
                        aspect_ratio: '1:1'
                    });
                } else {
                    setCanvasContent({
                        type: 'document',
                        content: lastMessage.text,
                        title: lastMessage.isIntermediate ? 'Generating...' : 'Generated Content'
                    });
                }
            }
            // Don't continue to the auto-trigger logic if canvas is already active
            return;
        }

        // Auto-trigger Canvas content for finalized messages
        if (lastMessage?.type === 'received' && !lastMessage.isIntermediate) {
            const content = lastMessage.text;

            // Check for special [IMAGE_DATA] markers first (highest priority)
            const imageDataMatch = content.match(/\[IMAGE_DATA\](data:image\/[^[]+)\[\/IMAGE_DATA\]/);
            if (imageDataMatch) {
                const imageUrl = imageDataMatch[1];
                // Extract prompt and aspect ratio from the message
                const promptMatch = content.match(/\*\*Prompt:\*\*\s*(.+)/);
                const aspectRatioMatch = content.match(/\*\*Aspect Ratio:\*\*\s*(\S+)/);

                // Canvas is always enabled, just set content
                setCanvasContent({
                    type: 'image',
                    content: imageUrl,
                    title: 'Generated Image',
                    prompt: promptMatch ? promptMatch[1].trim() : null,
                    aspect_ratio: aspectRatioMatch ? aspectRatioMatch[1].trim() : '1:1'
                });

                // Dispatch event to hide sidebar
                window.dispatchEvent(new CustomEvent('canvasToggled', {
                    detail: {isActive: true}
                }));

                showToast('Canvas mode activated for image viewing', 'info');
                return;  // Skip document/code detection for image messages
            }

            // Fallback: Check for image data URLs (old format)
            const imageUrlMatch = content.match(/data:image\/[^;]+;base64,[^\s)]+/);
            if (imageUrlMatch) {
                const imageUrl = imageUrlMatch[0];
                // Extract prompt if available
                const promptMatch = content.match(/(?:prompt|description):\s*["']?([^"'\n]+)["']?/i);

                // Canvas is always enabled, just set content
                setCanvasContent({
                    type: 'image',
                    content: imageUrl,
                    title: 'Generated Image',
                    prompt: promptMatch ? promptMatch[1] : null,
                    aspect_ratio: '1:1'  // Default
                });

                // Dispatch event to hide sidebar
                window.dispatchEvent(new CustomEvent('canvasToggled', {
                    detail: {isActive: true}
                }));

                showToast('Canvas mode activated for image viewing', 'info');
                return;  // Skip document/code detection for image messages
            }

            // Define patterns that should trigger Canvas mode
            const canvasPatterns = [
                // Code patterns
                /```[\s\S]+```/g, // Code blocks
                /function\s+\w+\s*\(/g, // Function declarations
                /class\s+\w+/g, // Class declarations
                /import\s+.*from/g, // ES6 imports
                /export\s+(default\s+)?/g, // ES6 exports
                /<\/?\w+[^>]*>/g, // HTML tags (multiple)
                /\{\s*"[\w\s:,]+"\s*:/g, // JSON objects

                // Document patterns
                /^#{1,6}\s+.+$/m, // Multiple markdown headers
                /^\*\s+.+(\n\*\s+.+){2,}/m, // Multiple bullet points
                /^\d+\.\s+.+(\n\d+\.\s+.+){2,}/m, // Multiple numbered lists
                /\n\n.+(\n.+){5,}/g, // Long structured content
            ];

            // Check if content matches Canvas patterns
            const shouldAutoTriggerCanvas = canvasPatterns.some(pattern => {
                const matches = content.match(pattern);
                return matches && matches.length > 0;
            });

            // Auto-trigger Canvas for substantial content (>500 chars with structure)
            const isSubstantialContent = content.length > 500 && (
                content.includes('\n\n') || // Has paragraphs
                content.includes('```') || // Has code blocks
                content.match(/^#+\s/m) // Has headers
            );

            if (shouldAutoTriggerCanvas || isSubstantialContent) {
                // Canvas is always enabled, just set content
                setCanvasContent({
                    type: 'document',
                    content: content,
                    title: 'Generated Content'
                });

                // Dispatch event to hide sidebar
                window.dispatchEvent(new CustomEvent('canvasToggled', {
                    detail: {isActive: true}
                }));

                // Show a subtle notification
                showToast('Canvas mode activated for better viewing', 'info');
            }
        }
    }, [lastMessage, showToast]);

    /**
     * Extract code update from AI response for Monaco editor
     * Uses CodeExtractor utility for unified diff detection
     */
    const extractCodeUpdate = useCallback((responseText, currentFilePath) => {
        // First priority: Check for unified diff format (handles file creation, deletion, modification)
        if (CodeExtractor.containsUnifiedDiff(responseText)) {
            const diffContent = CodeExtractor.extractUnifiedDiff(responseText);
            if (diffContent) {
                // eslint-disable-next-line no-console
                devLog('📊 Detected unified diff format in AI response');
                return {
                    filePath: currentFilePath || 'unknown.txt',
                    updateType: 'diff',
                    diffContent: diffContent,
                    isDiff: true,
                    isPartialUpdate: true
                };
            }
        }

        // Second priority: Check for replace operations
        const replaceOp = CodeExtractor.extractReplaceOperation(responseText);
        if (replaceOp) {
            return {
                filePath: currentFilePath || 'unknown.txt',
                updateType: 'replace',
                oldString: replaceOp.oldString,
                newString: replaceOp.newString,
                isPartialUpdate: true
            };
        }

        // Third priority: Try to extract file path from common patterns
        const filePathPatterns = [
            /(?:content for|create|update|modify)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?\s+(?:is|file)?:?/i,
            /(?:here'?s?\s+(?:the\s+)?(?:updated\s+)?content\s+for)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?:?/i,
            /^([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+):?\s*$/m,
            /```\w*\n\/\/\s*([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)/
        ];

        // Extract all code blocks with their context
        const codeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/g;
        const matches = [...responseText.matchAll(codeBlockRegex)];

        if (matches.length === 0) return null;

        const results = [];

        // Try to match each code block with a file path
        for (const match of matches) {
            const code = match[1].trim();
            if (code.length <= 50) continue; // Skip trivial blocks

            // Get the text before this code block (up to 200 chars)
            const beforeText = responseText.substring(Math.max(0, match.index - 200), match.index);

            let detectedPath = null;

            // Try to find file path in the text before the code block
            for (const pattern of filePathPatterns) {
                const pathMatch = beforeText.match(pattern);
                if (pathMatch && pathMatch[1]) {
                    detectedPath = pathMatch[1];
                    break;
                }
            }

            // Also check inside the code block for file path comments
            const firstLine = code.split('\n')[0];
            if (!detectedPath && (firstLine.includes('//') || firstLine.includes('#') || firstLine.includes('/*'))) {
                const inlinePathMatch = firstLine.match(/[/\\]?([a-zA-Z0-9_-]+\/[a-zA-Z0-9_./-]+)/);
                if (inlinePathMatch) {
                    detectedPath = inlinePathMatch[1];
                }
            }

            results.push({
                filePath: detectedPath || currentFilePath || 'unknown.txt',
                content: code,
                updateType: 'full',
                isNewFile: detectedPath && detectedPath !== currentFilePath
            });
        }

        // Return the first result (or null if no valid blocks found)
        return results.length > 0 ? results[0] : null;
    }, []);

    /**
     * Extract ALL code updates from an AI response (handles multiple diffs/files)
     * This is used when AI returns multiple file modifications in a single response
     */
    const extractAllCodeUpdates = useCallback((responseText, currentFilePath) => {
        const updates = [];

        // Helper function to check if response is actually requesting code changes
        // vs. just showing code as an example or explanation
        const isActualModificationRequest = (text) => {
            // PRIORITY 1: If there are actual unified diff blocks present, always process them
            // This handles cases where AI provides code AND asks a follow-up question
            const hasUnifiedDiff = /---\s+(?:a\/)?[^\n]+\n\+\+\+\s+(?:b\/)?[^\n]+\n@@/m.test(text);
            if (hasUnifiedDiff) {
                return true; // Always process if valid diffs are present
            }

            // PRIORITY 2: Check for explicit modification indicators
            const modificationIndicators = [
                /(?:here(?:'s| is)|i(?:'ve| have)) (?:updated|modified|changed|fixed|added|refactored)/i,
                /(?:let me|i'll|i will) (?:update|modify|change|fix|add|refactor)/i,
                /replace.*with/i,
                /(?:updated|modified|changed|fixed) (?:code|file|implementation)/i,
                /apply (?:this|these) change/i,
                /make (?:this|these) change/i
            ];

            const hasModificationIndicators = modificationIndicators.some(pattern => pattern.test(text));
            if (hasModificationIndicators) {
                return true;
            }

            // PRIORITY 3: Only check for questions if no diffs/modifications detected
            const questionIndicators = [
                /^(?:what|how|why|when|where|can you explain)/i,
                /\bexplain\b/i,
                /\bshow me\b/i,
                /\btell me\b/i,
                /\bwhat (?:does|is|are)\b/i,
                /\bhow (?:does|is|can)\b/i,
                /\?\s*$/m  // Ends with a question mark
            ];

            const hasQuestionIndicators = questionIndicators.some(pattern => pattern.test(text));
            if (hasQuestionIndicators) {
                return false; // Skip only if it's purely a question
            }

            // Default: process if uncertain
            return true;
        };

        // Check if this response is actually a modification request
        if (!isActualModificationRequest(responseText)) {
            // eslint-disable-next-line no-console
            devLog('📖 Response appears to be a question/explanation, not a modification request. Skipping code updates.');
            return [];
        }

        // First priority: Extract ALL unified diffs
        // CRITICAL: Capture all content until next file header or end
        const unifiedDiffRegex = /---\s+(?:a\/)?([^\n]+)\n\+\+\+\s+(?:b\/)?([^\n]+)\n([\s\S]*?)(?=\n---\s+(?:a\/)?[^\n]+\n\+\+\+|$)/g;
        let diffMatch;

        while ((diffMatch = unifiedDiffRegex.exec(responseText)) !== null) {
            const oldFilePath = diffMatch[1].trim();
            const newFilePath = diffMatch[2].trim();
            let diffBody = diffMatch[3];

            // Clean up the diff body - remove any trailing commentary or explanations
            // Keep all hunks (@@) but stop at non-diff content
            const lines = diffBody.split('\n');
            const cleanedLines = [];
            let inHunk = false;
            let consecutiveNonDiffLines = 0;

            for (const line of lines) {
                // Start of a hunk - reset counter
                if (line.startsWith('@@')) {
                    inHunk = true;
                    cleanedLines.push(line);
                    consecutiveNonDiffLines = 0;
                    continue;
                }

                // Valid diff line (starts with space, +, -, or \ for "no newline" marker)
                if (inHunk && (line.startsWith(' ') || line.startsWith('+') || line.startsWith('-') || line.startsWith('\\'))) {
                    cleanedLines.push(line);
                    consecutiveNonDiffLines = 0;
                    continue;
                }

                // Empty line could be between hunks or part of context - keep it
                if (line.trim() === '') {
                    cleanedLines.push(line);
                    consecutiveNonDiffLines++;
                    // Stop if we have 2+ consecutive empty lines (likely end of diff)
                    if (consecutiveNonDiffLines >= 2) {
                        break;
                    }
                    continue;
                }

                // Non-diff content (commentary, explanations) - stop here
                if (inHunk) {
                    // Check if it looks like commentary (bullets, checkmarks, plain text)
                    if (line.match(/^[\s-]*[✓✅❌📝🔧]/u)) {
                        break;
                    }
                    // Stop at lines that don't follow diff format
                    break;
                }
            }

            diffBody = cleanedLines.join('\n').trim();

            // Skip if we didn't get any valid diff content
            if (!diffBody || !diffBody.includes('@@')) {
                continue;
            }

            // Reconstruct the full diff for this file WITH proper a/ and b/ prefixes
            // The diffParser expects standard unified diff format
            // Handle /dev/null specially to avoid double slashes
            const oldPath = oldFilePath === '/dev/null' ? '/dev/null' : `a/${oldFilePath}`;
            const newPath = newFilePath === '/dev/null' ? '/dev/null' : `b/${newFilePath}`;
            const fullDiff = `--- ${oldPath}\n+++ ${newPath}\n${diffBody}`;

            // Determine the target file path
            let targetPath = newFilePath;
            if (newFilePath === '/dev/null') {
                targetPath = oldFilePath; // File deletion
            } else if (oldFilePath === '/dev/null') {
                targetPath = newFilePath; // File creation
            }

            // Remove path prefixes (a/ or b/) from target path only
            targetPath = targetPath.replace(/^[ab]\//, '');

            updates.push({
                filePath: targetPath,
                updateType: 'diff',
                diffContent: fullDiff,
                isDiff: true,
                isPartialUpdate: true
            });

            // eslint-disable-next-line no-console
            devLog(`📊 Extracted unified diff for: ${targetPath}`);
            // eslint-disable-next-line no-console
            devLog(`📄 Diff preview (first 300 chars):`, fullDiff.substring(0, 300));
        }

        // If we found unified diffs, return them
        if (updates.length > 0) {
            return updates;
        }

        // Second priority: Check for replace operations
        const replaceOp = CodeExtractor.extractReplaceOperation(responseText);
        if (replaceOp) {
            return [{
                filePath: currentFilePath || 'unknown.txt',
                updateType: 'replace',
                oldString: replaceOp.oldString,
                newString: replaceOp.newString,
                isPartialUpdate: true
            }];
        }

        // Third priority: Extract all code blocks
        // Patterns to detect file paths in AI responses
        const filePathPatterns = [
            /(?:content for|create|update|modify)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?\s+(?:is|file)?:?/i,
            /(?:here'?s?\s+(?:the\s+)?(?:updated\s+)?content\s+for)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?:?/i,
            /^([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+):?\s*$/m,
            /```\w*\n\/\/\s*([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)/
        ];

        const codeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/g;
        const matches = [...responseText.matchAll(codeBlockRegex)];

        for (const match of matches) {
            const code = match[1].trim();
            if (code.length <= 50) continue;

            const beforeText = responseText.substring(Math.max(0, match.index - 200), match.index);
            let detectedPath = null;

            for (const pattern of filePathPatterns) {
                const pathMatch = beforeText.match(pattern);
                if (pathMatch && pathMatch[1]) {
                    detectedPath = pathMatch[1];
                    break;
                }
            }

            const firstLine = code.split('\n')[0];
            if (!detectedPath && (firstLine.includes('//') || firstLine.includes('#') || firstLine.includes('/*'))) {
                const inlinePathMatch = firstLine.match(/[/\\]?([a-zA-Z0-9_-]+\/[a-zA-Z0-9_./-]+)/);
                if (inlinePathMatch) {
                    detectedPath = inlinePathMatch[1];
                }
            }

            updates.push({
                filePath: detectedPath || currentFilePath || 'unknown.txt',
                content: code,
                updateType: 'full',
                isNewFile: detectedPath && detectedPath !== currentFilePath
            });
        }

        return updates.length > 0 ? updates : null;
    }, []);

    /**
     * Stops/cancels the current ongoing request
     */

    /**
     * Helper function to build file context with fresh content from IndexedDB
     * This ensures we always send the latest modified code to the AI, not stale versions
     */
    const buildFileContextWithFreshContent = useCallback(async (file, projectId, serviceName, includeLineCount = true) => {
        let fileContent = file.content;

        // If we have projectId and serviceName, try to load fresh content from IndexedDB
        if (projectId && serviceName) {
            const storedFile = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
            if (storedFile && storedFile.content) {
                fileContent = storedFile.content;
            }
        }

        // Build context string with optional line numbers
        const lines = fileContent.split('\n');
        const contextLines = lines.map((line, idx) =>
            includeLineCount ? `${idx + 1}: ${line}` : line
        ).join('\n');

        return `File: ${file.path}\n\`\`\`\n${contextLines}\n\`\`\`\n`;
    }, []);

    /**
     * Handles sending a new message to the ADK backend.
     * 1. Ensures an ADK session exists (or creates one).
     * 2. Adds the user's message to the messages array.
     * 3. Sends the message to the backend and begins processing the streamed response (SSE).
     * 4. Parses events to handle text, function calls, and errors incrementally.
     * 5. Updates the UI with the assistant's response as it arrives.
     */
    const handleSendMessage = useCallback(async () => {
        if (inputValue.trim()) {
            // Check if this is an image generation request
            const imageRequest = detectImageGenerationRequest(inputValue);
            if (imageRequest) {
                // Handle image generation directly via API endpoint
                const userMessage = Messages.createUserMessage(inputValue);
                setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));
                setInputValue('');
                setIsLoading(true);
                setConversationStatus('Generating Image...');

                const result = await generateImage(
                    imageRequest.prompt,
                    imageRequest.aspectRatio,
                    imageRequest.numberOfImages
                );

                setIsLoading(false);
                setConversationStatus('');

                /* eslint-disable no-console */
                devLog('Image generation complete. Result:', result);
                /* eslint-enable no-console */

                if (result.success && result.data && result.data.images && result.data.images.length > 0) {
                    // Create assistant message with image data
                    const imageData = result.data.images[0]; // Use first image

                    /* eslint-disable no-console */
                    devLog('Image generation result:', result);
                    devLog('Image data:', imageData);
                    devLog('Image URL:', imageData.url);
                    /* eslint-enable no-console */

                    const assistantMessage = Messages.createInfoMessage(
                        `I've generated an image based on your prompt: "${imageRequest.prompt}"`
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, assistantMessage));

                    // Automatically open Canvas with the image
                    const canvasData = {
                        type: 'image',
                        content: imageData.url,  // Canvas expects 'content' not 'data'
                        title: 'Generated Image',
                        prompt: imageData.prompt,
                        aspect_ratio: imageData.aspect_ratio,
                    };

                    /* eslint-disable no-console */
                    devLog('Setting canvas content:', canvasData);
                    devLog('Current isCanvasMode before:', isCanvasMode);
                    /* eslint-enable no-console */

                    // Mark that canvas was manually set to prevent auto-detection from overriding
                    manualCanvasSetRef.current = true;

                    setCanvasContent(canvasData);
                    // Canvas is always enabled, no need to set it

                    /* eslint-disable no-console */
                    devLog('Canvas content set for image');
                    /* eslint-enable no-console */

                    // Dispatch event to hide sidebar
                    window.dispatchEvent(new CustomEvent('canvasToggled', {
                        detail: {isActive: true}
                    }));
                } else {
                    const errorMessage = Messages.createErrorMessage(
                        result.message || 'Failed to generate image. Please try again.'
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                }
                return;
            }

            // Check if this is a video generation request
            const videoRequest = detectVideoGenerationRequest(inputValue);
            if (videoRequest) {
                // Handle video generation directly via API endpoint
                const userMessage = Messages.createUserMessage(inputValue);
                setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));
                setInputValue('');
                setIsLoading(true);
                setConversationStatus('Generating Video (this may take 2-3 minutes)...');

                const result = await generateVideo(
                    videoRequest.prompt,
                    videoRequest.aspectRatio,
                    videoRequest.durationSeconds
                );

                setIsLoading(false);
                setConversationStatus('');

                /* eslint-disable no-console */
                devLog('Video generation complete. Result:', result);
                /* eslint-enable no-console */

                if (result.success && result.data && result.data.video) {
                    // Create assistant message with video data
                    const videoData = result.data.video;

                    /* eslint-disable no-console */
                    devLog('Video generation result:', result);
                    devLog('Video data:', videoData);
                    devLog('Video URL:', videoData.url);
                    /* eslint-enable no-console */

                    const assistantMessage = Messages.createInfoMessage(
                        `I've generated a video based on your prompt: "${videoRequest.prompt}"`
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, assistantMessage));

                    // Automatically open Canvas with the video
                    const canvasData = {
                        type: 'video',
                        content: videoData.url,  // Canvas expects 'content' not 'data'
                        title: 'Generated Video',
                        prompt: videoData.prompt,
                        aspect_ratio: videoData.aspect_ratio,
                        duration_seconds: videoData.duration_seconds,
                    };

                    /* eslint-disable no-console */
                    devLog('Setting canvas content:', canvasData);
                    devLog('Current isCanvasMode before:', isCanvasMode);
                    /* eslint-enable no-console */

                    // Mark that canvas was manually set to prevent auto-detection from overriding
                    manualCanvasSetRef.current = true;

                    setCanvasContent(canvasData);
                    // Canvas is always enabled, no need to set it

                    /* eslint-disable no-console */
                    devLog('Canvas content set for video');
                    /* eslint-enable no-console */

                    // Dispatch event to hide sidebar
                    window.dispatchEvent(new CustomEvent('canvasToggled', {
                        detail: {isActive: true}
                    }));
                } else {
                    const errorMessage = Messages.createErrorMessage(
                        result.message || 'Failed to generate video. Please try again.'
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                }
                return;
            }

            // Ensure we have an ADK session; create one on first user message
            let currentSessionId = sessionId;
            if (!currentSessionId) {
                // eslint-disable-next-line no-console
                devLog('🆕 No session ID - creating new session...');
                const sessionResult = await AdkClient.createSession();
                if (sessionResult.success) {
                    currentSessionId = sessionResult.sessionId;
                    setSessionId(currentSessionId);
                    sessionFilesSent.clear(); // Reset file tracking for new session
                    // eslint-disable-next-line no-console
                    devLog(`✅ New session created: ${currentSessionId}`);
                } else {
                    const errorMessage = Messages.createErrorMessage(sessionResult.message);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                    return;
                }
            } else {
                // eslint-disable-next-line no-console
                devLog(`♻️  Reusing existing session: ${currentSessionId}`);
            }

            const userMessage = Messages.createUserMessage(inputValue);

            // Retroactively name a still-placeholder-named project from its first
            // real message, instead of guessing a name before any interaction
            // happened (utils/quickProjectCreation.js gives new "quick create"
            // projects a placeholder like "Untitled Project 07/09 14:32"). The
            // placeholder's exact format is distinctive enough that a user's own
            // rename always moves the name off this pattern, so re-checking it on
            // every send is safe: it fires once per project and never re-fires
            // after either this auto-rename or a manual rename lands. Fire-and-
            // forget — must not block sending the message itself.
            if (
                canvasProjectContext?.projectId &&
                canvasProjectContext?.tenant?.id &&
                /^Untitled Project \d{2}\/\d{2} \d{2}:\d{2}$/.test(canvasProjectContext?.project?.name || '')
            ) {
                const projectIdForRename = canvasProjectContext.projectId;
                const tenantIdForRename = canvasProjectContext.tenant.id;
                miaPlatformDataService.generateProjectName(inputValue, null, null, tenantIdForRename)
                    .then((nameResult) => (nameResult?.suggestedName
                        ? miaPlatformDataService.updateProject(tenantIdForRename, projectIdForRename, { name: nameResult.suggestedName })
                        : null))
                    .then((updated) => {
                        if (updated?.name) {
                            setCanvasProjectContext(prev => (prev && prev.projectId === projectIdForRename)
                                ? { ...prev, project: { ...prev.project, name: updated.name } }
                                : prev);
                        }
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error('Failed to auto-name project from first message:', error);
                    });
            }

            // Same idea as the project auto-rename above, but for the default
            // service quickProjectCreation.js registers as '.' (the project
            // root itself). '.' can't be physically renamed — there's no real
            // subfolder to move — so this relabels its DISPLAY name only (see
            // vfsClient.setVfsServiceDisplayName's comment). Only fires once:
            // guarded on no display name existing yet, same fire-and-forget
            // pattern, must not block sending the message.
            if (
                canvasProjectContext?.projectId &&
                vfsClient.getVfsServiceNames(canvasProjectContext.projectId).includes('.') &&
                !vfsClient.getVfsServiceDisplayName(canvasProjectContext.projectId, '.')
            ) {
                const projectIdForServiceRename = canvasProjectContext.projectId;
                miaPlatformDataService.generateProjectName(inputValue, null, null, canvasProjectContext?.tenant?.id)
                    .then((nameResult) => {
                        if (nameResult?.suggestedName) {
                            vfsClient.setVfsServiceDisplayName(projectIdForServiceRename, '.', nameResult.suggestedName);
                        }
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error('Failed to auto-label default service from first message:', error);
                    });
            }

            // Check if files were modified in Canvas mode - if so, clear chat history
            if (isCanvasMode && filesModifiedInCanvas) {
                // eslint-disable-next-line no-console
                devLog('🔄 Files modified in Canvas mode - clearing chat history and resending fresh source code');
                setMessages([userMessage]); // Clear history, keep only current message
                setFilesModifiedInCanvas(false); // Reset flag

                // Clear conversation context to force fresh start
                setConversationId(null);
                setConvName('');

                // CRITICAL: Clear session file tracking to force re-sending files with updated content
                AdkClient.clearSessionFiles(currentSessionId);
            } else {
                setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));
                // NOTE: Don't mark session here - AdkClient.sendMessage will mark it after successfully sending files
                // AdkClient.markSessionInitialized(currentSessionId);
            }

            setInputValue('');
            // Clear localStorage input
            try {
                localStorage.removeItem('chatInputValue');
            } catch (e) {
                console.error('Failed to clear input from localStorage:', e);
            }
            setIsLoading(true);
            setConversationStatus('Deciphering User Intent');

            // Re-enable auto-scroll when user sends a new message
            shouldAutoScrollRef.current = true;

            // Create AbortController for this request
            const controller = new AbortController();
            setAbortController(controller);

            // Retry wrapper with exponential backoff for rate limit errors
            const maxRetries = 5;
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    // Send message and stream the assistant response via SSE
                    const userRequest = inputValue;
                    const filesToSend = attachedFiles.length > 0 ? [...attachedFiles] : null;
                    let messageToSend = '';

                    // Add project context if in canvas mode with selected project
                    let projectContextToSend = null;
                    if (isCanvasMode && canvasProjectContext && canvasProjectContext.project) {
                        projectContextToSend = {...canvasProjectContext};

                        // Try to load files from IndexedDB - check all services if no specific one is selected
                        let filesLoaded = false;
                        let serviceName = canvasProjectContext.selectedMicroservice?.name;

                        // Fallback: get service name from selectedResource (always load from localStorage)
                        if (!serviceName) {
                            const selectedResource = loadSelectedResourceFromStorage();
                            if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                serviceName = selectedResource.data.name;
                                // Also update the context with the loaded resource
                                projectContextToSend.selectedResource = selectedResource;
                                // eslint-disable-next-line no-console
                                devLog(`📌 Using service name from mia_selectedResource: ${serviceName}`);
                            }
                        }

                        if (serviceName) {
                            // We have a specific service selected, load its files
                            try {
                                // eslint-disable-next-line no-console
                                devLog(`🔍 Loading files for selected microservice: ${serviceName}`);
                                // eslint-disable-next-line no-console
                                devLog(`   Project ID: ${canvasProjectContext.projectId}`);

                                const filesFromDb = await indexedDbFileStorage.getAllFiles(
                                    canvasProjectContext.projectId,
                                    serviceName
                                );

                                // eslint-disable-next-line no-console
                                devLog(`📂 Retrieved ${filesFromDb?.length || 0} files from IndexedDB`);

                                if (filesFromDb && filesFromDb.length > 0) {
                                    // Filter out deleted files for run_sse context
                                    const activeFiles = filesFromDb.filter(file => file.state !== 'deleted');
                                    const allFilesWithContent = activeFiles.map(file => ({
                                        path: file.filePath,
                                        content: file.content
                                    }));
                                    projectContextToSend.allFiles = allFilesWithContent;

                                    // CRITICAL FIX: Update canvasProjectContext state with allFiles AND selectedMicroservice
                                    // so that file request handlers can access the files and service name
                                    // Include full service data with repository URLs for Pull/Push operations
                                    const fullServiceData = canvasProjectContext.configuration?.services?.[serviceName];
                                    const microserviceInfo = fullServiceData ? {
                                        ...fullServiceData,
                                        name: serviceName
                                    } : {
                                        name: serviceName,
                                        type: canvasProjectContext.configuration?.services?.[serviceName]?.type
                                    };

                                    setCanvasProjectContext(prev => ({
                                        ...prev,
                                        allFiles: allFilesWithContent,
                                        selectedMicroservice: microserviceInfo
                                    }));

                                    // Also update projectContextToSend for backend
                                    projectContextToSend.selectedMicroservice = microserviceInfo;

                                    filesLoaded = true;
                                    // eslint-disable-next-line no-console
                                    devLog(`📂 Loaded ${filesFromDb.length} files from IndexedDB for AI context`);
                                    // eslint-disable-next-line no-console
                                    devLog(`   Sample files: ${filesFromDb.slice(0, 3).map(f => f.filePath).join(', ')}`);
                                }
                            } catch (error) {
                                // eslint-disable-next-line no-console
                                console.error('Failed to load files from IndexedDB:', error);
                            }
                        }

                        // If no service selected or no files found, try to find ANY service with files
                        if (!filesLoaded && canvasProjectContext.configuration?.services) {
                            // eslint-disable-next-line no-console
                            devLog(`🔍 No service selected or no files found, searching all services...`);
                            const services = Object.values(canvasProjectContext.configuration.services);

                            for (const service of services) {
                                try {
                                    const filesFromDb = await indexedDbFileStorage.getAllFiles(canvasProjectContext.projectId, service.name);

                                    if (filesFromDb && filesFromDb.length > 0) {
                                        // Filter out deleted files for run_sse context
                                        const activeFiles = filesFromDb.filter(file => file.state !== 'deleted');
                                        const allFilesWithContent = activeFiles.map(file => ({
                                            path: file.filePath,
                                            content: file.content
                                        }));

                                        projectContextToSend.allFiles = allFilesWithContent;
                                        // Auto-set the microservice so file requests work
                                        // Include full service data with repository URLs for Pull/Push operations
                                        projectContextToSend.selectedMicroservice = {
                                            ...service,
                                            name: service.name,
                                            type: service.type
                                        };
                                        // Also set selectedResource as fallback for file requests
                                        projectContextToSend.selectedResource = {
                                            type: 'service',
                                            data: service
                                        };

                                        // CRITICAL FIX: Update canvasProjectContext state with allFiles, selectedMicroservice AND selectedResource
                                        // so that file request handlers can access the files and service name
                                        setCanvasProjectContext(prev => ({
                                            ...prev,
                                            allFiles: allFilesWithContent,
                                            selectedMicroservice: projectContextToSend.selectedMicroservice,
                                            selectedResource: projectContextToSend.selectedResource
                                        }));

                                        filesLoaded = true;
                                        // eslint-disable-next-line no-console
                                        devLog(`📂 Auto-loaded ${filesFromDb.length} files from service: ${service.name}`);
                                        // eslint-disable-next-line no-console
                                        devLog(`   Sample files: ${filesFromDb.slice(0, 3).map(f => f.filePath).join(', ')}`);
                                        break; // Stop after finding first service with files
                                    }
                                } catch (error) {
                                    // Continue to next service
                                }
                            }
                        }

                        if (!filesLoaded) {
                            // eslint-disable-next-line no-console
                            console.warn(`⚠️ No files found in IndexedDB for project ${canvasProjectContext.projectId}`);
                            // eslint-disable-next-line no-console
                            console.warn(`   Make sure to Pull files from the Canvas before chatting with AI`);
                        }

                        // Start with user request - MOST IMPORTANT
                        messageToSend = `[USER REQUEST]\n${userRequest}\n\n---\n\n`;

                        // Build condensed context
                        const canvasContext = [];
                        canvasContext.push('[CANVAS MODE CONTEXT]');

                        // Build context summary for display
                        const contextInfo = [];
                        if (canvasProjectContext.tenant) {
                            contextInfo.push(`Tenant: ${canvasProjectContext.tenant.name}`);
                            canvasContext.push(`Tenant: ${canvasProjectContext.tenant.name} (${canvasProjectContext.tenant.id})`);
                        }
                        if (canvasProjectContext.project) {
                            contextInfo.push(`Project: ${canvasProjectContext.project.name}`);
                            canvasContext.push(`Project: ${canvasProjectContext.project.name} (${canvasProjectContext.project.id})`);
                        }
                        if (canvasProjectContext.revision) {
                            contextInfo.push(`Revision: ${canvasProjectContext.revision.name}`);
                            canvasContext.push(`Revision: ${canvasProjectContext.revision.name}`);
                        }
                        canvasContext.push('');
                        canvasContext.push('🚫 FORBIDDEN: GitLab API tools (get_file_content, create_file, etc.)');
                        canvasContext.push('✅ REQUIRED: Use Mia-Platform MCP tools, generate unified diffs');
                        canvasContext.push('');

                        // Add selected resource context (service, endpoint, etc.)
                        if (canvasProjectContext.selectedResource) {
                            const resource = canvasProjectContext.selectedResource;
                            contextInfo.push(`Selected ${resource.type}: ${resource.name}`);

                            // For collections, include the full JSON definition
                            if (resource.type === 'collection' && resource.data) {
                                const collectionJson = JSON.stringify(resource.data, null, 2);
                                const collectionContext = `[CURRENT COLLECTION]\nName: ${resource.name}\nDefinition:\n\`\`\`json\n${collectionJson}\n\`\`\`\n`;
                                canvasContext.push(collectionContext);

                                // Also include list of all collections for reference
                                if (canvasProjectContext.collections && Object.keys(canvasProjectContext.collections).length > 1) {
                                    const allCollectionNames = Object.keys(canvasProjectContext.collections);
                                    canvasContext.push(`All Collections: ${allCollectionNames.join(', ')}`);
                                }
                            }
                        }

                        // If no specific resource selected, provide project overview with collections
                        if (!canvasProjectContext.selectedResource && canvasProjectContext.configuration) {
                            const config = canvasProjectContext.configuration;
                            const configSummary = [];
                            if (config.services) configSummary.push(`Services: ${Object.keys(config.services).length}`);
                            if (config.endpoints) configSummary.push(`Endpoints: ${Object.keys(config.endpoints).length}`);
                            if (canvasProjectContext.collections) {
                                const collectionCount = Object.keys(canvasProjectContext.collections).length;
                                configSummary.push(`Collections: ${collectionCount}`);

                                // Include collection names for context
                                if (collectionCount > 0) {
                                    const collectionNames = Object.keys(canvasProjectContext.collections);
                                    canvasContext.push(`Available Collections: ${collectionNames.join(', ')}`);
                                }
                            }
                            if (configSummary.length > 0) {
                                canvasContext.push(`Project Overview: ${configSummary.join(', ')}`);
                            }
                        }

                        // Add selected microservice context when viewing source code
                        if (canvasProjectContext.selectedMicroservice) {
                            const microservice = canvasProjectContext.selectedMicroservice;
                            contextInfo.push(`Microservice: ${microservice.name}`);

                            // Add microservice to context array
                            canvasContext.push('');
                            canvasContext.push('[CURRENT MICROSERVICE]');
                            canvasContext.push(`Name: ${microservice.name}`);
                            canvasContext.push(`Type: ${microservice.type || 'custom'}`);
                            if (microservice.repositoryUrl) {
                                canvasContext.push(`Repository: ${microservice.repositoryUrl}`);
                            }
                        }

                        // Add selected file context with content - HIGHLIGHTED AS SOURCE CODE TO MODIFY
                        if (canvasProjectContext.selectedFile) {
                            const file = canvasProjectContext.selectedFile;
                            contextInfo.push(`Viewing File: ${file.path || file.name}`);

                            // Use helper function to get fresh content from IndexedDB
                            const fileContext = await buildFileContextWithFreshContent(
                                file,
                                canvasProjectContext.projectId,
                                canvasProjectContext.selectedMicroservice?.name,
                                false // DON'T include line numbers - breaks diff application
                            );
                            if (fileContext) {
                                // Store for later - will be added in SOURCE CODE section
                                canvasContext.selectedFileContext = fileContext;
                            }
                        }

                        // Add complete file tree context (condensed)
                        if (canvasProjectContext.allFiles && canvasProjectContext.allFiles.length > 0) {
                            canvasContext.push('');
                            canvasContext.push(`[FILES - ${canvasProjectContext.allFiles.length} total]`);

                            // Group files by directory for more compact display
                            const filesByDir = {};
                            canvasProjectContext.allFiles.forEach(f => {
                                const parts = f.path.split('/');
                                const dir = parts.length > 1 ? parts.slice(0, -1).join('/') : '.';
                                if (!filesByDir[dir]) filesByDir[dir] = [];
                                filesByDir[dir].push(parts[parts.length - 1]);
                            });

                            // Show condensed grouped structure
                            Object.keys(filesByDir).sort().forEach(dir => {
                                const files = filesByDir[dir];
                                if (dir === '.') {
                                    canvasContext.push(`Root: ${files.join(', ')}`);
                                } else {
                                    canvasContext.push(`${dir}/: ${files.join(', ')}`);
                                }
                            });

                            // Build list of key files to include full content
                            const keyFiles = [];

                            // ALWAYS include the currently selected file first (if any) - but ONLY if it's a code file
                            if (canvasProjectContext.selectedFile && canvasProjectContext.selectedFile.path) {
                                const selectedFilePath = canvasProjectContext.selectedFile.path;
                                // Skip binary files even if selected
                                if (isCodeFile(selectedFilePath)) {
                                    const selectedFileData = canvasProjectContext.allFiles.find(f => f.path === selectedFilePath);
                                    if (selectedFileData && selectedFileData.content) {
                                        keyFiles.push(selectedFileData);
                                    }
                                }
                            }

                            // Then add other important files (up to 15 total, excluding node_modules, lock files, and binary files)
                            const otherFiles = canvasProjectContext.allFiles
                                .filter(f =>
                                    !f.path.includes('node_modules') &&
                                    isCodeFile(f.path) &&  // Filters out binary files, lock files, and other excluded files
                                    f.content &&
                                    f.path !== (canvasProjectContext.selectedFile?.path || '')  // Don't duplicate selected file
                                )
                                .slice(0, 14);  // Leave room for selected file

                            keyFiles.push(...otherFiles);

                            // Count files that were filtered out
                            const filteredFiles = canvasProjectContext.allFiles.filter(f => !isCodeFile(f.path)).length;
                            if (filteredFiles > 0) {
                                // eslint-disable-next-line no-console
                                devLog(`🚫 Filtered out ${filteredFiles} files (binary files, lock files, etc.) from AI context`);
                            }

                            // DON'T send file contents upfront - AI will request them on-demand via tools
                            // This dramatically reduces initial token usage

                            // Include ALL code files in the available list (not just the 15 key files)
                            // This gives AI complete visibility of what files exist and their metadata
                            canvasContext.keyFilesAvailable = canvasProjectContext.allFiles
                                .filter(f => isCodeFile(f.path) && f.content)
                                .map(f => ({
                                    path: f.path,
                                    lines: f.content.split('\n').length,
                                    extension: f.path.split('.').pop()
                                }));

                            // eslint-disable-next-line no-console
                            devLog(`📦 Project context prepared: ${canvasProjectContext.allFiles.length} files total, ${canvasContext.keyFilesAvailable.length} code files available (AI can request content on-demand)`);
                        } else if (canvasProjectContext.project && canvasProjectContext.selectedMicroservice) {
                            // WARN: No files loaded - AI will have no file context
                            // eslint-disable-next-line no-console
                            console.warn(`⚠️ NO FILES IN PROJECT CONTEXT! Project: ${canvasProjectContext.project.name}, Service: ${canvasProjectContext.selectedMicroservice.name}. AI will have NO file context. User needs to click Pull to fetch files first.`);
                        }

                        // Build final message structure with clear sections
                        const contextSections = [];

                        // 1. Configuration and project info (non-code)
                        if (canvasContext.length > 1) {
                            contextSections.push(canvasContext.join('\n'));
                        }

                        // 2. SOURCE CODE section - clearly separated and highlighted
                        const sourceCodeSections = [];

                        // Add selected file (primary focus) - ONLY if user is viewing it
                        if (canvasContext.selectedFileContext) {
                            sourceCodeSections.push('[🎯 SOURCE CODE TO MODIFY]');
                            sourceCodeSections.push(canvasContext.selectedFileContext);
                        }

                        // Add available files summary (AI can request content via tools)
                        if (canvasContext.keyFilesAvailable && canvasContext.keyFilesAvailable.length > 0) {
                            if (sourceCodeSections.length === 0) {
                                sourceCodeSections.push('[📝 AVAILABLE SOURCE FILES]');
                                sourceCodeSections.push('Use tools to request file content when needed:');
                            } else {
                                sourceCodeSections.push('');
                                sourceCodeSections.push('[📚 ADDITIONAL FILES AVAILABLE]');
                                sourceCodeSections.push('Request content via tools when needed:');
                            }
                            sourceCodeSections.push('');
                            canvasContext.keyFilesAvailable.forEach(f => {
                                sourceCodeSections.push(`- ${f.path} (${f.lines} lines, .${f.extension})`);
                            });
                        }

                        // Append context sections
                        if (contextSections.length > 0) {
                            messageToSend += contextSections.join('\n');
                        }

                        // Append source code section with clear separator
                        if (sourceCodeSections.length > 0) {
                            messageToSend += '\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n';
                            messageToSend += sourceCodeSections.join('\n');
                        }
                    } else {
                        // No canvas mode - just use the user request
                        // In Compose mode, include tenant context so the AI knows which tenant to operate in
                        if (hideCanvas) {
                            const composeTenant = miaFlowAuth.getSelectedTenant();
                            if (composeTenant) {
                                const tenantLine = `Tenant: ${composeTenant.name || composeTenant.tenantId} (id: ${composeTenant.tenantId})`;
                                messageToSend = `[CONTEXT]\n${tenantLine}\nNote: No project is selected. Operate on the tenant level or provide general guidance.\n\n[USER REQUEST]\n${userRequest}`;
                            } else {
                                messageToSend = userRequest;
                            }
                        } else {
                            messageToSend = userRequest;
                        }
                    }

                    // Add AI mode instruction at the beginning of the message
                    if (aiMode === 'plan') {
                        messageToSend = `[PLAN MODE ACTIVE]\n⚠️ CRITICAL INSTRUCTION: You are currently in PLAN MODE. In this mode, you MUST NOT create, modify, deploy, or execute anything. You should ONLY provide information, explanations, suggestions, and planning guidance. Do not use any tools that would create or modify resources (projects, services, files, deployments, etc.). Only use read-only tools to gather information and provide helpful guidance to the user. You MAY use the curl_request_tool to make read-only HTTP requests (e.g. GET) to inspect or query APIs and retrieve information.\n\n${messageToSend}`;
                    }

                    // Add custom AI instructions at the very top (after all mode instructions)
                    // Only inject as text when there is NO active playbook — when a playbook is active
                    // the BE fetches all resources (agents, skills, specs, prompts) from the catalog
                    // via intentId + intentInstructionsOnly, so we must not duplicate them here.
                    if (customAIInstructions && !customAIInstructionsPlaybookData) {
                        messageToSend = `[CUSTOM AI INSTRUCTIONS]\n${customAIInstructions}\n\n${messageToSend}`;
                    }

                    const activeTenantId = miaFlowAuth.getSelectedTenant()?.tenantId || null;

                    // Map selected tool IDs (tool.id = metadata.name slug) to runtimeName for the backend
                    // Use ref to avoid stale closure — selectedTools is not in the useCallback dep array
                    const runtimeNameById = new Map(availableToolsRef.current.map(t => [t.id, t.runtimeName]));
                    const selectedToolRuntimeNames = (selectedToolsRef.current || [])
                        .map(id => runtimeNameById.get(id) || id)
                        .filter(Boolean);

                    const sendResult = await AdkClient.sendMessage(
                        currentSessionId,
                        messageToSend,
                        controller.signal,
                        projectContextToSend,
                        isCanvasMode,
                        aiMode,
                        filesToSend,
                        selectedToolRuntimeNames,
                        selectedModel,
                        null,
                        activeTenantId
                    );

                    // Clear attached files after sending
                    setAttachedFiles([]);

                    if (!sendResult.success) {
                    setIsLoading(false);
                    setAbortController(null);
                    const errorMessage = Messages.createErrorMessage(sendResult.message);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                    return;
                }

                const response = sendResult.response;
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let accumulatedText = '';
                let stopProcessingEvents = false;
                let intermediateMessageCreated = false;
                // Track whether any partial=true streaming chunks have been received.
                // Used to detect ADK-style responses where the final partial=false event
                // contains the COMPLETE text (not just the last token), which would
                // otherwise cause the response to appear doubled.
                let hasSeenPartialTrue = false;

                // Store browser session metadata when detected from start_browser tool
                let pendingBrowserSession = null;

                // Don't create an intermediate message yet - wait until we start receiving content
                // This prevents replacing/hiding previous messages that contain diagrams or content

                // Queue for pending interception promises
                const pendingInterceptions = [];

                try {
                    // Parse SSE frames and update chat incrementally (text, functionCall, functionResponse, error)
                    const parser = createParser({
                        onEvent: (event) => {
                            try {
                                const eventData = JSON.parse(event.data);

                                // Handle diffspec events for automatic code merging
                                if (eventData.type === 'diffspec') {
                                    // eslint-disable-next-line no-console
                                    devLog('📝 [SSE-Main] Diffspec event received:', {
                                        file: eventData.file_path,
                                        diffLength: eventData.diff_content?.length,
                                        conversationId: eventData.conversation_id
                                    });

                                    // Dispatch custom event for useAutoDiffApplier hook
                                    window.dispatchEvent(new CustomEvent('diffspec', {
                                        detail: eventData
                                    }));

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request events (AI requesting files from IndexedDB)
                                if (eventData.type === 'file_request') {
                                    // eslint-disable-next-line no-console
                                    devLog('📂 [SSE-Main] File request event received:', {
                                        file: eventData.file_path,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        requestedServiceName: eventData.service_name,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load file from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            // eslint-disable-next-line no-console
                                            devLog('🔄 [File Request] Starting async handler for:', eventData.file_path);

                                            const projectId = canvasProjectContext?.projectId;
                                            // Use service_name from the event if specified, otherwise use current context
                                            let serviceName = eventData.service_name || canvasProjectContext?.selectedMicroservice?.name;

                                            // eslint-disable-next-line no-console
                                            devLog('🔍 [File Request] Context check:', {
                                                projectId,
                                                serviceName,
                                                requestedServiceName: eventData.service_name,
                                                hasCanvasContext: !!canvasProjectContext
                                            });

                                            // Fallback to selectedResource if no serviceName yet
                                            if (!serviceName) {
                                                // eslint-disable-next-line no-console
                                                devLog('🔍 [File Request] No serviceName from canvas context, trying selectedResource...');
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                    // eslint-disable-next-line no-console
                                                    devLog('✅ [File Request] Found serviceName from selectedResource:', serviceName);
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [File Request] Cannot load file - missing project or service context', {
                                                    projectId,
                                                    serviceName
                                                });

                                                // eslint-disable-next-line no-console
                                                devLog('📤 [File Request] Posting error response to backend...');

                                                // POST error back to backend
                                                const errorResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName || '',
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                devLog('✅ [File Request] Error response posted, status:', errorResponse.status);
                                                return;
                                            }

                                            // eslint-disable-next-line no-console
                                            devLog('🗄️ [File Request] Loading file from IndexedDB:', {
                                                projectId,
                                                serviceName,
                                                filePath: eventData.file_path
                                            });

                                            // Load file from IndexedDB
                                            const fileData = await indexedDbFileStorage.getFile(
                                                projectId,
                                                serviceName,
                                                eventData.file_path
                                            );

                                            // eslint-disable-next-line no-console
                                            devLog('📊 [File Request] IndexedDB query result:', {
                                                found: !!fileData,
                                                hasContent: !!(fileData?.content),
                                                contentLength: fileData?.content?.length || 0
                                            });

                                            if (fileData && fileData.content) {
                                                // eslint-disable-next-line no-console
                                                devLog(`✅ [File Request] Loaded file from IndexedDB: ${eventData.file_path} (${fileData.content.length} chars)`);

                                                // eslint-disable-next-line no-console
                                                devLog(`📤 [File Request] Posting file content to backend: ${config.agentUrl}/api/canvas-files/submit`);

                                                // POST file content back to backend
                                                const postResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: fileData.content,
                                                        service_name: serviceName,
                                                        success: true
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                devLog(`✅ [File Request] Posted file content, status: ${postResponse.status}, ok: ${postResponse.ok}`);

                                                // eslint-disable-next-line no-console
                                                devLog(`📤 Posted file content back to backend for request ${eventData.request_id}`);
                                            } else {
                                                // eslint-disable-next-line no-console
                                                console.error(`❌ [File Request] File not found in IndexedDB: ${eventData.file_path}`);

                                                // eslint-disable-next-line no-console
                                                devLog('📤 [File Request] Posting error response (file not found) to backend...');

                                                // POST error back to backend
                                                const errorResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName,
                                                        success: false,
                                                        error: `File '${eventData.file_path}' not found in IndexedDB`
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                devLog(`✅ [File Request] Error response posted, status: ${errorResponse.status}`);
                                            }
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [File Request] Error loading file from IndexedDB:', error);

                                            // POST error back to backend
                                            try {
                                                // eslint-disable-next-line no-console
                                                devLog('📤 [File Request] Posting error response (exception) to backend...');

                                                const errorResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: eventData.service_name || '',
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                devLog(`✅ [File Request] Exception error response posted, status: ${errorResponse.status}`);
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [File Request] Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle bash_script_execution events (execute bash commands on IndexedDB files)
                                if (eventData.type === 'bash_script_execution') {
                                    // eslint-disable-next-line no-console
                                    devLog('🖥️ [Bash Execution] Received bash script execution request:', {
                                        executionId: eventData.execution_id,
                                        script: eventData.script,
                                        conversationId: eventData.conversation_id
                                    });

                                    // Execute script asynchronously
                                    (async () => {
                                        try {
                                            // Get project context - try multiple sources
                                            let projectId = null;
                                            let serviceName = null;

                                            // 1. Try from event data (if service_name provided by backend)
                                            if (eventData.service_name) {
                                                serviceName = eventData.service_name;
                                            }

                                            // 2. Try from canvasProjectContext state
                                            if (canvasProjectContext) {
                                                projectId = canvasProjectContext.projectId;
                                                if (!serviceName) {
                                                    serviceName = canvasProjectContext.selectedMicroservice?.name;
                                                }
                                            }

                                            // 3. Fallback to IndexedDB (in case state not updated yet)
                                            if (!projectId || !serviceName) {
                                                try {
                                                    const savedContext = await indexedDbStorage.getItem('canvasProjectContext');
                                                    if (savedContext) {
                                                        projectId = projectId || savedContext.projectId;
                                                        serviceName = serviceName || savedContext.selectedMicroservice?.name;
                                                        // eslint-disable-next-line no-console
                                                        devLog('📦 [Bash Execution] Loaded context from IndexedDB:', {
                                                            projectId,
                                                            serviceName
                                                        });
                                                    }
                                                } catch (dbError) {
                                                    // eslint-disable-next-line no-console
                                                    console.warn('⚠️ [Bash Execution] Could not load from IndexedDB:', dbError);
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                throw new Error('Missing project or service context');
                                            }

                                            // eslint-disable-next-line no-console
                                            devLog('🔧 [Bash Execution] Initializing VirtualFileSystem...');

                                            // Create virtual filesystem and load files from IndexedDB
                                            const vfs = new VirtualFileSystem();
                                            await vfs.loadFromIndexedDB(projectId, serviceName);

                                            // eslint-disable-next-line no-console
                                            devLog('▶️ [Bash Execution] Executing script...');

                                            // Execute the script
                                            const result = await vfs.executeScript(eventData.script);

                                            // eslint-disable-next-line no-console
                                            devLog('✅ [Bash Execution] Script executed:', {
                                                exitCode: result.exit_code,
                                                modifiedFilesCount: result.modifiedFiles.length,
                                                stdout: result.stdout.substring(0, 100) + '...'
                                            });

                                            let userApproved = true;

                                            // If files were modified, ask for user confirmation
                                            if (result.modifiedFiles.length > 0) {
                                                // eslint-disable-next-line no-console
                                                devLog('⚠️ [Bash Execution] Files modified, requesting user confirmation...');

                                                userApproved = await showScriptExecutionConfirmation(
                                                    result.modifiedFiles,
                                                    eventData.script,
                                                    result.stdout
                                                );

                                                // eslint-disable-next-line no-console
                                                devLog(`👤 [Bash Execution] User ${userApproved ? 'approved' : 'rejected'} changes`);

                                                // If approved, sync changes to IndexedDB
                                                if (userApproved) {
                                                    // eslint-disable-next-line no-console
                                                    devLog('💾 [Bash Execution] Syncing changes to IndexedDB...');

                                                    await vfs.syncChangesToIndexedDB(result.modifiedFiles);

                                                    // eslint-disable-next-line no-console
                                                    devLog('✅ [Bash Execution] Changes synced to IndexedDB');
                                                }
                                            }

                                            // Cleanup
                                            vfs.cleanup();

                                            // POST results back to backend
                                            // eslint-disable-next-line no-console
                                            devLog('📤 [Bash Execution] Posting results to backend...');

                                            const postResponse = await authorizedFetch(`${config.agentUrl}/api/bash-execution/submit`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    execution_id: eventData.execution_id,
                                                    conversation_id: eventData.conversation_id,
                                                    success: true,
                                                    stdout: result.stdout,
                                                    stderr: result.stderr,
                                                    exit_code: result.exit_code,
                                                    files_modified: result.modifiedFiles.map(f => f.path),
                                                    user_approved: userApproved
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            devLog(`✅ [Bash Execution] Results posted, status: ${postResponse.status}`);

                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [Bash Execution] Error:', error);

                                            // Show error to user
                                            showScriptExecutionError(error.message);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/bash-execution/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        execution_id: eventData.execution_id,
                                                        conversation_id: eventData.conversation_id,
                                                        success: false,
                                                        error: error.message,
                                                        stdout: '',
                                                        stderr: error.message,
                                                        exit_code: 1,
                                                        files_modified: [],
                                                        user_approved: false
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Bash Execution] Failed to POST error to backend:', postError);
                                            }
                                        }
                                    })();

                                    return;
                                }

                                // Handle git_clone_completed events (repository cloned to workspace)
                                if (eventData.type === 'git_clone_completed') {
                                    // eslint-disable-next-line no-console
                                    devLog('📦 [SSE-Main] Git clone completed event received:', {
                                        owner: eventData.owner,
                                        repo: eventData.repo,
                                        clonePath: eventData.clone_path,
                                        filesCount: eventData.files_count,
                                        filePathsCount: eventData.file_paths?.length,
                                        requestId: eventData.request_id
                                    });

                                    // Dispatch custom event for file tree to refresh
                                    window.dispatchEvent(new CustomEvent('git_clone_completed', {
                                        detail: eventData
                                    }));

                                    // Show initial toast notification
                                    showToast(
                                        `✅ Repository ${eventData.owner}/${eventData.repo} cloned (${eventData.files_count} files). Indexing to project...`,
                                        'info'
                                    );

                                    // Start indexing files to IndexedDB in the background
                                    if (eventData.file_paths && eventData.file_paths.length > 0 && canvasProjectContext) {
                                        (async () => {
                                            try {
                                                const projectId = canvasProjectContext?.projectId;
                                                let serviceName = canvasProjectContext?.selectedMicroservice?.name;

                                                // Fallback to selectedResource if no selectedMicroservice
                                                if (!serviceName) {
                                                    const selectedResource = loadSelectedResourceFromStorage();
                                                    if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                        serviceName = selectedResource.data.name;
                                                    }
                                                }

                                                if (!projectId || !serviceName) {
                                                    // eslint-disable-next-line no-console
                                                    console.error('❌ Cannot index cloned files - missing project or service context');
                                                    showToast('⚠️ Cannot index files: No active project/service', 'warning');
                                                    return;
                                                }

                                                // Use source-code-for-analysis prefix so files appear under a dedicated folder
                                                const pathPrefix = `source-code-for-analysis/${eventData.repo}`;
                                                const clonePath = eventData.clone_path;
                                                const filePaths = eventData.file_paths;

                                                // eslint-disable-next-line no-console
                                                devLog(`📂 Indexing ${filePaths.length} files to IndexedDB with prefix: ${pathPrefix}`);

                                                // Batch process files (10 at a time to avoid overwhelming the backend)
                                                const batchSize = 10;
                                                let indexed = 0;

                                                for (let i = 0; i < filePaths.length; i += batchSize) {
                                                    const batch = filePaths.slice(i, i + batchSize);

                                                    await Promise.all(batch.map(async (filePath) => {
                                                        try {
                                                            // Fetch file content from backend
                                                            const response = await authorizedFetch(
                                                                `${config.agentUrl}/api/canvas-files/github-clone/${encodeURIComponent(clonePath)}?file_path=${encodeURIComponent(filePath)}`,
                                                                {
                                                                    method: 'GET',
                                                                    headers: getBeHeaders()
                                                                }
                                                            );

                                                            if (!response.ok) {
                                                                // eslint-disable-next-line no-console
                                                                console.warn(`⚠️  Failed to fetch ${filePath}: ${response.statusText}`);
                                                                return;
                                                            }

                                                            const data = await response.json();

                                                            // Skip binary files (images, videos, etc.) - not useful for code analysis
                                                            if (data.is_binary) {
                                                                // eslint-disable-next-line no-console
                                                                devLog(`⏭️  Skipping binary file: ${filePath}`);
                                                                return;
                                                            }

                                                            if (data.success && data.content) {
                                                                // Save to IndexedDB with prefixed path
                                                                const indexedPath = `${pathPrefix}/${filePath}`;
                                                                await indexedDbFileStorage.saveFile(
                                                                    projectId,
                                                                    serviceName,
                                                                    indexedPath,
                                                                    data.content,
                                                                    {
                                                                        state: 'original',
                                                                        isGitHubClone: true,
                                                                        sourceRepo: `${eventData.owner}/${eventData.repo}`
                                                                    }
                                                                );
                                                                indexed++;
                                                            }
                                                        } catch (error) {
                                                            // eslint-disable-next-line no-console
                                                            console.error(`❌ Error indexing ${filePath}:`, error);
                                                        }
                                                    }));

                                                    // Show progress
                                                    // eslint-disable-next-line no-console
                                                    devLog(`📊 Indexed ${indexed}/${filePaths.length} files...`);
                                                }

                                                // eslint-disable-next-line no-console
                                                devLog(`✅ Successfully indexed ${indexed} files from ${eventData.repo}`);

                                                // Show completion toast
                                                showToast(
                                                    `✅ Repository ${eventData.repo} indexed! ${indexed} files available in source-code-for-analysis/`,
                                                    'success'
                                                );

                                                // Trigger file tree refresh
                                                window.dispatchEvent(new CustomEvent('git_clone_indexed', {
                                                    detail: { ...eventData, indexed_count: indexed }
                                                }));

                                                // Check if there's a pending initial prompt from new project creation
                                                const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
                                                const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');
                                                const pendingFiles = sessionStorage.getItem('mia_pending_initial_files');

                                                if (pendingPrompt) {
                                                    devLog('🚀 Git clone indexed, triggering initial prompt:', pendingPrompt);

                                                    // Clear the pending flags
                                                    sessionStorage.removeItem('mia_pending_initial_prompt');
                                                    sessionStorage.removeItem('mia_pending_initial_mode');
                                                    sessionStorage.removeItem('mia_pending_initial_files');

                                                    // Wait for UI to settle, then trigger the coding prompt
                                                    setTimeout(() => {
                                                        const chatInput = document.querySelector('.message-input');
                                                        if (chatInput) {
                                                            // Attach files if any
                                                            if (pendingFiles) {
                                                                try {
                                                                    const filesArray = JSON.parse(pendingFiles);
                                                                    window.dispatchEvent(new CustomEvent('attach-files-to-chat', {
                                                                        detail: { files: filesArray }
                                                                    }));
                                                                } catch (e) {
                                                                    console.warn('Failed to parse pending files:', e);
                                                                }
                                                            }

                                                            // Set input and trigger submit
                                                            chatInput.value = pendingPrompt;
                                                            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                                                            chatInput.closest('form')?.dispatchEvent(submitEvent);

                                                            // Set flag to ask about Mia components after this response
                                                            sessionStorage.setItem('mia_ask_about_components_next', 'true');
                                                        }
                                                    }, 1000);
                                                }

                                            } catch (error) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Error indexing cloned files:', error);
                                                showToast('❌ Failed to index cloned repository', 'error');
                                            }
                                        })();
                                    }

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request_multiple events (AI requesting multiple files from IndexedDB)
                                if (eventData.type === 'file_request_multiple') {
                                    // eslint-disable-next-line no-console
                                    devLog('📂 [SSE-Main] Multiple files request event received:', {
                                        files: eventData.file_paths,
                                        fileCount: eventData.file_paths?.length,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        requestedServiceName: eventData.service_name,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load files from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            const projectId = canvasProjectContext?.projectId;
                                            // Use service_name from the event if specified, otherwise use current context
                                            let serviceName = eventData.service_name || canvasProjectContext?.selectedMicroservice?.name;

                                            // Fallback to selectedResource if no serviceName yet
                                            if (!serviceName) {
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Cannot load files - missing project or service context');
                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        service_name: serviceName || '',
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Load all files from IndexedDB
                                            const filesData = [];
                                            for (const filePath of eventData.file_paths) {
                                                const fileData = await indexedDbFileStorage.getFile(
                                                    projectId,
                                                    serviceName,
                                                    filePath
                                                );

                                                if (fileData && fileData.content) {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: fileData.content,
                                                        success: true
                                                    });
                                                    // eslint-disable-next-line no-console
                                                    devLog(`✅ Loaded file from IndexedDB: ${filePath} (${fileData.content.length} chars)`);
                                                } else {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: '',
                                                        success: false,
                                                        error: `File not found in IndexedDB`
                                                    });
                                                    // eslint-disable-next-line no-console
                                                    console.error(`❌ File not found in IndexedDB: ${filePath}`);
                                                }
                                            }

                                            // POST all files back to backend
                                            await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    files: filesData,
                                                    service_name: serviceName,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            devLog(`📤 Posted ${filesData.length} file(s) back to backend for request ${eventData.request_id}`);
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ Error loading files from IndexedDB:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        service_name: eventData.service_name || '',
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request_lines events (AI requesting specific line ranges from IndexedDB)
                                if (eventData.type === 'file_request_lines') {
                                    // eslint-disable-next-line no-console
                                    devLog('📂 [SSE-Main] File lines request event received:', {
                                        file: eventData.file_path,
                                        startLine: eventData.start_line,
                                        endLine: eventData.end_line,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        requestedServiceName: eventData.service_name,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load file lines from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            const projectId = canvasProjectContext?.projectId;
                                            let serviceName = eventData.service_name || canvasProjectContext?.selectedMicroservice?.name;

                                            // Fallback to selectedResource if no serviceName yet
                                            if (!serviceName) {
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [File Lines Request] Cannot load file - missing project or service context');

                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName || '',
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: eventData.start_line,
                                                        actual_end: eventData.end_line,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Load file from IndexedDB
                                            const fileData = await indexedDbFileStorage.getFile(
                                                projectId,
                                                serviceName,
                                                eventData.file_path
                                            );

                                            if (fileData && fileData.content) {
                                                // Extract requested line range
                                                const allLines = fileData.content.split('\n');
                                                const totalLines = allLines.length;

                                                // Adjust bounds silently if out of range (1-indexed)
                                                let actualStart = Math.max(1, eventData.start_line);
                                                let actualEnd = Math.min(totalLines, eventData.end_line);
                                                actualStart = Math.min(actualStart, totalLines);

                                                const wasAdjusted = (actualStart !== eventData.start_line) || (actualEnd !== eventData.end_line);

                                                // Extract lines (convert to 0-indexed for array)
                                                const extractedLines = allLines.slice(actualStart - 1, actualEnd);

                                                // Format with line numbers in diffspec style
                                                const numberedContent = extractedLines
                                                    .map((line, idx) => `${actualStart + idx}: ${line}`)
                                                    .join('\n');

                                                // eslint-disable-next-line no-console
                                                devLog(`✅ [File Lines Request] Extracted lines ${actualStart}-${actualEnd} from ${eventData.file_path}${wasAdjusted ? ' (adjusted)' : ''}`);

                                                // POST file lines back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: numberedContent,
                                                        service_name: serviceName,
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: actualStart,
                                                        actual_end: actualEnd,
                                                        total_lines: totalLines,
                                                        was_adjusted: wasAdjusted,
                                                        success: true
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                devLog(`📤 Posted file lines back to backend for request ${eventData.request_id}`);
                                            } else {
                                                // eslint-disable-next-line no-console
                                                console.error(`❌ [File Lines Request] File not found in IndexedDB: ${eventData.file_path}`);

                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName,
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: eventData.start_line,
                                                        actual_end: eventData.end_line,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        success: false,
                                                        error: `File '${eventData.file_path}' not found in IndexedDB`
                                                    })
                                                });
                                            }
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [File Lines Request] Error processing file lines:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: eventData.service_name || '',
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: eventData.start_line,
                                                        actual_end: eventData.end_line,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request_multiple_lines events (AI requesting multiple line ranges from IndexedDB)
                                if (eventData.type === 'file_request_multiple_lines') {
                                    // eslint-disable-next-line no-console
                                    devLog('📂 [SSE-Main] Multiple file lines request event received:', {
                                        fileRequests: eventData.file_requests,
                                        requestCount: eventData.file_requests?.length,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load file lines from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            const projectId = canvasProjectContext?.projectId;
                                            const defaultServiceName = canvasProjectContext?.selectedMicroservice?.name;

                                            if (!projectId) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Cannot load files - missing project context');
                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: 'Missing project context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Process all file line requests
                                            const filesData = [];
                                            for (const fileReq of eventData.file_requests) {
                                                const filePath = fileReq.file_path;
                                                const startLine = fileReq.start_line;
                                                const endLine = fileReq.end_line;
                                                let serviceName = fileReq.service_name || defaultServiceName;

                                                // Fallback to selectedResource if no serviceName
                                                if (!serviceName) {
                                                    const selectedResource = loadSelectedResourceFromStorage();
                                                    if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                        serviceName = selectedResource.data.name;
                                                    }
                                                }

                                                if (!serviceName) {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: '',
                                                        start_line: startLine,
                                                        end_line: endLine,
                                                        actual_start: startLine,
                                                        actual_end: endLine,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        service_name: '',
                                                        success: false,
                                                        error: 'Missing service context'
                                                    });
                                                    continue;
                                                }

                                                // Load file from IndexedDB
                                                const fileData = await indexedDbFileStorage.getFile(
                                                    projectId,
                                                    serviceName,
                                                    filePath
                                                );

                                                if (fileData && fileData.content) {
                                                    // Extract requested line range
                                                    const allLines = fileData.content.split('\n');
                                                    const totalLines = allLines.length;

                                                    // Adjust bounds silently if out of range (1-indexed)
                                                    let actualStart = Math.max(1, startLine);
                                                    let actualEnd = Math.min(totalLines, endLine);
                                                    actualStart = Math.min(actualStart, totalLines);

                                                    const wasAdjusted = (actualStart !== startLine) || (actualEnd !== endLine);

                                                    // Extract lines (convert to 0-indexed for array)
                                                    const extractedLines = allLines.slice(actualStart - 1, actualEnd);

                                                    // Format with line numbers in diffspec style
                                                    const numberedContent = extractedLines
                                                        .map((line, idx) => `${actualStart + idx}: ${line}`)
                                                        .join('\n');

                                                    filesData.push({
                                                        path: filePath,
                                                        content: numberedContent,
                                                        start_line: startLine,
                                                        end_line: endLine,
                                                        actual_start: actualStart,
                                                        actual_end: actualEnd,
                                                        total_lines: totalLines,
                                                        was_adjusted: wasAdjusted,
                                                        service_name: serviceName,
                                                        success: true
                                                    });

                                                    // eslint-disable-next-line no-console
                                                    devLog(`✅ Extracted lines ${actualStart}-${actualEnd} from ${filePath}${wasAdjusted ? ' (adjusted)' : ''}`);
                                                } else {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: '',
                                                        start_line: startLine,
                                                        end_line: endLine,
                                                        actual_start: startLine,
                                                        actual_end: endLine,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        service_name: serviceName,
                                                        success: false,
                                                        error: 'File not found in IndexedDB'
                                                    });
                                                    // eslint-disable-next-line no-console
                                                    console.error(`❌ File not found in IndexedDB: ${filePath}`);
                                                }
                                            }

                                            // POST all file lines back to backend
                                            await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple-lines`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    files: filesData,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            devLog(`📤 Posted ${filesData.length} file line range(s) back to backend for request ${eventData.request_id}`);
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ Error loading file lines from IndexedDB:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle syntax_check_request events (AI requesting Monaco syntax problems)
                                if (eventData.type === 'syntax_check_request') {
                                    // eslint-disable-next-line no-console
                                    devLog('🔍 [SSE-Main] Syntax check request received:', {
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id
                                    });

                                    (async () => {
                                        try {
                                            let projectId = canvasProjectContext?.projectId;
                                            let serviceName = canvasProjectContext?.selectedMicroservice?.name;

                                            if (!serviceName) {
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                }
                                            }

                                            // Fallback to values provided by the agent in the event (from project context)
                                            if (!projectId && eventData.project_id) {
                                                projectId = eventData.project_id;
                                                // eslint-disable-next-line no-console
                                                devLog('🔍 [Syntax Check] Using project_id from event:', projectId);
                                            }
                                            if (!serviceName && eventData.service_name) {
                                                serviceName = eventData.service_name;
                                                // eslint-disable-next-line no-console
                                                devLog('🔍 [Syntax Check] Using service_name from event:', serviceName);
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Syntax Check] Missing project or service context');
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Get all modified files from IndexedDB
                                            const modifiedFiles = await indexedDbFileStorage.getAllFiles(projectId, serviceName);

                                            if (!modifiedFiles || modifiedFiles.length === 0) {
                                                // eslint-disable-next-line no-console
                                                devLog('📭 [Syntax Check] No modified files to check');
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: true
                                                    })
                                                });
                                                return;
                                            }

                                            // Map file extension to Monaco language identifier
                                            const getMonacoLanguage = (filePath) => {
                                                const ext = (filePath.split('.').pop() || '').toLowerCase();
                                                const langMap = {
                                                    ts: 'typescript', tsx: 'typescript',
                                                    js: 'javascript', jsx: 'javascript',
                                                    mjs: 'javascript', cjs: 'javascript',
                                                    py: 'python', json: 'json', jsonc: 'json',
                                                    md: 'markdown', mdx: 'markdown',
                                                    css: 'css', scss: 'scss', less: 'less',
                                                    html: 'html', xml: 'xml', svg: 'xml',
                                                    go: 'go', java: 'java', rs: 'rust', kt: 'kotlin',
                                                    sh: 'shell', bash: 'shell', yaml: 'yaml', yml: 'yaml',
                                                    toml: 'toml', graphql: 'graphql', gql: 'graphql',
                                                    cs: 'csharp', cpp: 'cpp', c: 'c', rb: 'ruby',
                                                    php: 'php', swift: 'swift', dart: 'dart'
                                                };
                                                return langMap[ext] || 'plaintext';
                                            };

                                            // Get Monaco instance (cached after initial load by @monaco-editor/react)
                                            const { loader: monacoLoader } = await import('@monaco-editor/react');
                                            const monaco = await monacoLoader.init();

                                            // Brief pause to allow any in-flight diffspec patches to be fully applied
                                            await new Promise(resolve => setTimeout(resolve, 800));

                                            // Create/update Monaco models for all modified files
                                            // Track URIs of models we create so we can clean them up later
                                            const createdModelUris = [];

                                            for (const fileData of modifiedFiles) {
                                                const filePath = fileData.path;
                                                if (!filePath) continue;
                                                const content = fileData.content || '';
                                                const language = getMonacoLanguage(filePath);
                                                // Use file:/// URI so TypeScript language service resolves paths correctly
                                                const uri = monaco.Uri.parse(`file:///${filePath}`);

                                                let model = monaco.editor.getModel(uri);
                                                if (!model) {
                                                    model = monaco.editor.createModel(content, language, uri);
                                                    createdModelUris.push(uri.toString());
                                                } else if (model.getValue() !== content) {
                                                    // Update model content to reflect latest IndexedDB state
                                                    model.setValue(content);
                                                }
                                            }

                                            // Wait for Monaco language services to compute diagnostics
                                            // TypeScript/JavaScript services need a moment to analyse all models
                                            await new Promise(resolve => setTimeout(resolve, 2500));

                                            // Collect markers (errors/warnings) for each modified file
                                            const filesWithProblems = [];

                                            for (const fileData of modifiedFiles) {
                                                const filePath = fileData.path;
                                                if (!filePath) continue;
                                                const uri = monaco.Uri.parse(`file:///${filePath}`);
                                                const model = monaco.editor.getModel(uri);
                                                if (!model) continue;

                                                const markers = monaco.editor.getModelMarkers({ resource: uri });
                                                const problems = markers
                                                    .filter(m =>
                                                        m.severity === monaco.MarkerSeverity.Error ||
                                                        m.severity === monaco.MarkerSeverity.Warning
                                                    )
                                                    .map(m => ({
                                                        line: m.startLineNumber,
                                                        column: m.startColumn,
                                                        endLine: m.endLineNumber,
                                                        endColumn: m.endColumn,
                                                        message: m.message,
                                                        severity: m.severity === monaco.MarkerSeverity.Error ? 'error' : 'warning',
                                                        code: m.code ? String(m.code) : undefined
                                                    }));

                                                if (problems.length > 0) {
                                                    filesWithProblems.push({ file: filePath, problems });
                                                }
                                            }

                                            // Dispose only the temp models we created (not existing editor models)
                                            for (const uriStr of createdModelUris) {
                                                const tempModel = monaco.editor.getModel(monaco.Uri.parse(uriStr));
                                                if (tempModel) {
                                                    tempModel.dispose();
                                                }
                                            }

                                            // eslint-disable-next-line no-console
                                            devLog(`📊 [Syntax Check] ${filesWithProblems.length} file(s) with problems out of ${modifiedFiles.length} modified`);

                                            // POST syntax problems back to backend
                                            await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    files: filesWithProblems,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            devLog(`📤 [Syntax Check] Posted results to backend for request ${eventData.request_id}`);

                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [Syntax Check] Error during syntax check:', error);

                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Syntax Check] Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle project_context_request events (AI requesting project context)
                                if (eventData.type === 'project_context_request') {
                                    // eslint-disable-next-line no-console
                                    devLog('🌐 [SSE-Main] Project context request event received:', {
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        currentCanvasProjectId: canvasProjectContext?.projectId
                                    });

                                    // Send project context back to backend
                                    (async () => {
                                        try {
                                            // eslint-disable-next-line no-console
                                            devLog('🔄 [Project Context Request] Starting async handler');

                                            if (!canvasProjectContext) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Project Context Request] No project context available');

                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/project-context/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        project_context: {},
                                                        success: false,
                                                        error: 'No project context available'
                                                    })
                                                });
                                                return;
                                            }

                                            // Build the project context to send
                                            const projectContextToSend = {
                                                tenant: canvasProjectContext.tenant ? {
                                                    id: canvasProjectContext.tenant.id,
                                                    name: canvasProjectContext.tenant.name
                                                } : null,
                                                project: canvasProjectContext.project ? {
                                                    id: canvasProjectContext.project.id,
                                                    name: canvasProjectContext.project.name
                                                } : null,
                                                revision: canvasProjectContext.revision ? {
                                                    id: canvasProjectContext.revision.id,
                                                    name: canvasProjectContext.revision.name
                                                } : null,
                                                selectedMicroservice: canvasProjectContext.selectedMicroservice || null,
                                                selectedFile: canvasProjectContext.selectedFile || null,
                                                allFiles: canvasProjectContext.allFiles ?
                                                    canvasProjectContext.allFiles.map(file => ({
                                                        path: file.path,
                                                        lines: file.content ? file.content.split('\n').length : 0,
                                                        extension: file.path.split('.').pop()
                                                    })) : [],
                                                selectedResource: canvasProjectContext.selectedResource || null,
                                                configuration: canvasProjectContext.configuration || null,
                                                collections: canvasProjectContext.collections || null
                                            };

                                            // eslint-disable-next-line no-console
                                            devLog('📤 [Project Context Request] Posting project context to backend');

                                            // POST project context back to backend
                                            const postResponse = await authorizedFetch(`${config.agentUrl}/api/project-context/submit`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    project_context: projectContextToSend,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            devLog(`✅ [Project Context Request] Posted project context, status: ${postResponse.status}`);

                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [Project Context Request] Error sending project context:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/project-context/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        project_context: {},
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Project Context Request] Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle usage metadata if present
                                if (eventData.usageMetadata) {
                                    const usage = eventData.usageMetadata;
                                    // eslint-disable-next-line no-console
                                    devLog('📊 [SSE-Main] Token usage metadata received:', usage);

                                    // Update token counts
                                    if (usage.promptTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        devLog('[SSE-Main] Updating inputTokens by', usage.promptTokenCount);
                                        setInputTokens(prev => {
                                            const newValue = prev + usage.promptTokenCount;
                                            // eslint-disable-next-line no-console
                                            devLog('[SSE-Main] inputTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                    if (usage.candidatesTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        devLog('[SSE-Main] Updating outputTokens by', usage.candidatesTokenCount);
                                        setOutputTokens(prev => {
                                            const newValue = prev + usage.candidatesTokenCount;
                                            // eslint-disable-next-line no-console
                                            devLog('[SSE-Main] outputTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                    if (usage.totalTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        devLog('[SSE-Main] Setting totalTokens to', usage.totalTokenCount);
                                        // FIXED: Should accumulate, not replace, to track total session usage
                                        setTotalTokens(prev => {
                                            const newValue = prev + usage.totalTokenCount;
                                            // eslint-disable-next-line no-console
                                            devLog('[SSE-Main] totalTokens accumulated:', prev, '+', usage.totalTokenCount, '=', newValue);
                                            return newValue;
                                        });
                                    } else if (usage.promptTokenCount !== undefined && usage.candidatesTokenCount !== undefined) {
                                        // Calculate total if not provided
                                        // eslint-disable-next-line no-console
                                        devLog('[SSE-Main] Calculating totalTokens from input+output');
                                        setTotalTokens(prev => {
                                            const newValue = prev + usage.promptTokenCount + usage.candidatesTokenCount;
                                            // eslint-disable-next-line no-console
                                            devLog('[SSE-Main] totalTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                }

                                // Error frame from server -> surface and stop streaming
                                if (eventData.error) {
                                    setIsLoading(false);
                                    setConversationStatus('');

                                    // Handle rate limit errors with special formatting
                                    if (eventData.rate_limit_error) {
                                        const rateLimitMessage = Messages.createInfoMessage(
                                            `⏱️ ${eventData.error}\n\n${eventData.details || ''}\n\n${eventData.suggestion || 'Please wait 1 minute before trying again.'}`
                                        );
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, rateLimitMessage));
                                    }
                                    // Handle authentication errors differently
                                    else if (eventData.auth_required || eventData.error.toLowerCase().includes('authentication')) {
                                        const authMessage = Messages.createInfoMessage(
                                            `🔐 ${eventData.error}\n\n${eventData.details || ''}\n\n${eventData.suggestion || 'Please authenticate to access all features.'}`
                                        );
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, authMessage));
                                    } else {
                                        const errorMessage = Messages.createErrorMessage(eventData.error);
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                                    }

                                    stopProcessingEvents = true; // halt further SSE processing
                                    return;
                                }

                                // Content frame: may include parts for tool calls/results and/or text
                                if (eventData.content && eventData.content.parts && eventData.content.parts.length > 0) {
                                    for (const part of eventData.content.parts) {
                                        if (part.functionCall) {
                                            // Update status to show we're calling a tool
                                            const toolName = part.functionCall.name || 'Tool';
                                            setConversationStatus(`Calling ${toolName}`);

                                            // Create intermediate message if not created yet
                                            if (!intermediateMessageCreated) {
                                                intermediateMessageCreated = true;
                                                setMessages(prevMessages => {
                                                    const intermediateMessage = {
                                                        id: Date.now() + Math.random(),
                                                        text: '',
                                                        type: 'received',
                                                        isIntermediate: true,
                                                        reasoningSteps: [{
                                                            title: 'Understanding Your Request',
                                                            description: 'Analyzing your query to determine the best approach',
                                                            reasoning: 'Processing natural language input and identifying intent, entities, and required actions to provide an accurate response.',
                                                            startTime: Date.now()
                                                        }],
                                                        currentReasoningStep: 0
                                                    };
                                                    return Messages.addMessage(prevMessages, intermediateMessage);
                                                });
                                            }

                                            // Add reasoning step for function call with detailed information
                                            setMessages(prevMessages => {
                                                const args = part.functionCall.args || {};
                                                let description = `Executing ${toolName}`;
                                                let title = `Using ${toolName}`;
                                                if (toolName === 'curl_request') {
                                                    const method = (args.method || 'GET').toUpperCase();
                                                    const url = args.url || '';
                                                    try {
                                                        const parsed = new URL(url);
                                                        title = `Using ${toolName}: ${method} ${parsed.pathname}`;
                                                    } catch (_) {
                                                        title = `Using ${toolName}: ${method} ${url}`;
                                                    }
                                                    description = `${method} ${url}`;
                                                } else if (Object.keys(args).length > 0) {
                                                    const firstKey = Object.keys(args)[0];
                                                    const firstVal = String(args[firstKey]).substring(0, 80);
                                                    title = `Using ${toolName} — ${firstKey}: ${firstVal}`;
                                                    description = `${firstKey}: ${firstVal}`;
                                                }

                                                return Messages.addReasoningStep(prevMessages, {
                                                    title: title,
                                                    description: description,
                                                    reasoning: `The model determined that ${toolName} is the appropriate tool to handle this part of your request.`,
                                                    functionCall: {
                                                        name: part.functionCall.name,
                                                        args: part.functionCall.args || {}
                                                    }
                                                });
                                            });
                                        }

                                        if (part.functionResponse) {
                                            // Update status to show we're processing the tool response
                                            setConversationStatus('Processing Response');

                                            // Check if this is the start_browser tool - extract sessionId for browser viewer
                                            if (part.functionResponse.name === 'start_browser') {
                                                try {
                                                    let browserSessionId = null;
                                                    let browserQuality = 'medium';

                                                    // Parse the response to extract sessionId
                                                    const response = part.functionResponse.response;
                                                    // eslint-disable-next-line no-console
                                                    devLog('🔍 start_browser response structure:', response);

                                                    let result = null;

                                                    // Try multiple ways to extract the result
                                                    if (response && response.result) {
                                                        // Case 1: response.result exists
                                                        result = typeof response.result === 'string'
                                                            ? JSON.parse(response.result)
                                                            : response.result;
                                                    } else if (typeof response === 'string') {
                                                        // Case 2: response itself is a JSON string
                                                        result = JSON.parse(response);
                                                    } else if (response && typeof response === 'object') {
                                                        // Case 3: response is already the result object
                                                        result = response;
                                                    }

                                                    // eslint-disable-next-line no-console
                                                    devLog('🔍 Parsed result:', result);

                                                    if (result && result.success && result.sessionId) {
                                                        browserSessionId = result.sessionId;
                                                        browserQuality = result.quality || 'medium';

                                                        // eslint-disable-next-line no-console
                                                        devLog('🌐 Browser session detected:', browserSessionId, 'quality:', browserQuality);

                                                        // Store for attachment to message when it's created/updated
                                                        pendingBrowserSession = {
                                                            browserSessionId,
                                                            browserQuality
                                                        };
                                                        // eslint-disable-next-line no-console
                                                        devLog('✅ Stored pending browser session, will attach to message');
                                                    } else {
                                                        // eslint-disable-next-line no-console
                                                        console.warn('⚠️ Could not extract sessionId from result:', result);
                                                    }
                                                } catch (error) {
                                                    // eslint-disable-next-line no-console
                                                    console.error('❌ Error parsing browser session from functionResponse:', error);
                                                }
                                            }

                            // Check if this is a project-modifying MCP tool that requires canvas refresh
                            const shouldRefreshCanvas = isCanvasMode && canvasProjectContext && part.functionResponse.name;
                            const projectModifyingTools = [
                                'configuration_save',
                                'create_project_from_template',
                                'create_service_from_marketplace',
                                'create_collection',
                                'create_endpoints',
                                'deploy_project'
                            ];

                            const isProjectModifyingTool = projectModifyingTools.some(tool =>
                                part.functionResponse.name && part.functionResponse.name.includes(tool)
                            );                                            // Check if the response indicates success
                                            let isSuccessful = false;
                                            if (part.functionResponse.response) {
                                                const response = part.functionResponse.response;
                                                if (typeof response === 'string') {
                                                    // Check for explicit error/failure keywords
                                                    const hasError = response.toLowerCase().includes('error') ||
                                                                   response.toLowerCase().includes('failed') ||
                                                                   response.toLowerCase().includes('failure');
                                                    // Consider success if no explicit error (more lenient)
                                                    isSuccessful = !hasError;
                                                } else if (typeof response === 'object') {
                                                    // Check for success indicators or absence of error
                                                    isSuccessful = response.success !== false && !response.error;
                                                }
                                            } else {
                                                // If no response, assume success (tool executed without errors)
                                                isSuccessful = true;
                                            }

                                            // eslint-disable-next-line no-console
                                            devLog('🔍 Canvas refresh check:', {
                                                tool: part.functionResponse.name,
                                                isProjectModifying: isProjectModifyingTool,
                                                isSuccessful,
                                                shouldRefresh: shouldRefreshCanvas && isProjectModifyingTool && isSuccessful
                                            });

                                            // Trigger canvas refresh if it's a successful project-modifying operation
                                            if (shouldRefreshCanvas && isProjectModifyingTool && isSuccessful) {
                                                // Delay refresh slightly to allow backend to process
                                                setTimeout(() => {
                                                    if (canvasRef.current && canvasRef.current.handleRefresh) {
                                                        // eslint-disable-next-line no-console
                                                        devLog('🔄 Auto-refreshing canvas after MCP operation:', part.functionResponse.name);
                                                        canvasRef.current.handleRefresh();
                                                    }
                                                }, 1000);
                                            }

                                            // Dispatch event for service creation to trigger auto-pull
                                            if (part.functionResponse.name === 'create_service_from_marketplace' && isSuccessful) {
                                                // Extract project ID and service name from the response
                                                const response = part.functionResponse.response;
                                                let projectId = canvasProjectContext?.projectId;
                                                let serviceName = null;

                                                if (typeof response === 'string') {
                                                    try {
                                                        const parsed = JSON.parse(response);
                                                        serviceName = parsed.name || parsed.serviceName;
                                                    } catch (e) {
                                                        // Try to extract from text
                                                        const nameMatch = response.match(/name["\s:]+([a-z0-9-]+)/i);
                                                        if (nameMatch) serviceName = nameMatch[1];
                                                    }
                                                } else if (typeof response === 'object') {
                                                    serviceName = response.name || response.serviceName;
                                                }

                                                if (projectId && serviceName) {
                                                    // eslint-disable-next-line no-console
                                                    devLog(`📢 Dispatching mcpServiceCreated event for ${serviceName}`);
                                                    window.dispatchEvent(new CustomEvent('mcpServiceCreated', {
                                                        detail: {
                                                            projectId,
                                                            serviceName,
                                                            toolName: 'create_service_from_marketplace'
                                                        }
                                                    }));
                                                }
                                            }

                                            // Check if this is a canvas file request - mark for auto-response
                                            // Don't interrupt the stream, but prepare to auto-send files after completion
                                            if (pendingInterceptions.length > 0) {
                                                (async () => {
                                                    try {
                                                        // Wait for interceptions to complete (they fetch files from IndexedDB)
                                                        await Promise.all(pendingInterceptions);
                                                        pendingInterceptions.length = 0;

                                                        // Store the file content for auto-send after stream completes
                                                        const fileContent = part.functionResponse.response;

                                                        // eslint-disable-next-line no-console
                                                        devLog('📂 File request detected - will auto-send files after AI completes');

                                                        // Set a flag in the window object to trigger auto-send
                                                        window._pendingFileContent = fileContent;
                                                        window._pendingFileSessionId = sessionId;

                                                    } catch (error) {
                                                        // eslint-disable-next-line no-console
                                                        console.error('❌ Error preparing file content:', error);
                                                    }
                                                })();
                                            }

                                            // Check for MCP App metadata in function response
                                            if (part.functionResponse.response && typeof part.functionResponse.response === 'object') {
                                                const response = part.functionResponse.response;
                                                if (response._meta && response._meta.ui && response._meta.ui.resourceUri) {
                                                    // eslint-disable-next-line no-console
                                                    devLog('🎨 MCP App metadata detected:', response._meta.ui);

                                                    // Store MCP App metadata to attach to the message
                                                    if (!window._mcpAppMetadata) {
                                                        window._mcpAppMetadata = {};
                                                    }
                                                    window._mcpAppMetadata[sessionId] = {
                                                        resourceUri: response._meta.ui.resourceUri,
                                                        toolInput: part.functionCall || {},
                                                        toolResult: response,
                                                        hostContext: {
                                                            theme: document.documentElement.getAttribute('data-theme') || 'light',
                                                            locale: 'en'
                                                        }
                                                    };
                                                }
                                            }

                                            // Update reasoning step with result
                                            setMessages(prevMessages => {
                                                // Extract result summary
                                                let resultSummary = 'Tool executed successfully';
                                                if (part.functionResponse.response) {
                                                    const response = part.functionResponse.response;
                                                    if (typeof response === 'string') {
                                                        resultSummary = response.length > 100 ?
                                                            response.substring(0, 100) + '...' :
                                                            response;
                                                    } else if (typeof response === 'object') {
                                                        resultSummary = 'Received structured data response';
                                                    }
                                                }

                                                return Messages.updateCurrentReasoningStep(prevMessages, {
                                                    result: part.functionResponse.response || 'Success',
                                                    reasoning: `Tool completed. ${resultSummary}. Now synthesizing this information into the final response.`
                                                });
                                            });
                                        }

                                        let currentAccumulatedText = '';
                                        if (part.text) {
                            let textToProcess = part.text;
                                            if (!intermediateMessageCreated) {
                                                intermediateMessageCreated = true;
                                                setMessages(prevMessages => {
                                                    const intermediateMessage = {
                                                        id: Date.now() + Math.random(),
                                                        text: '',
                                                        type: 'received',
                                                        isIntermediate: true,
                                                        isStreaming: true,
                                                        reasoningSteps: [{
                                                            title: 'Understanding Your Request',
                                                            description: 'Analyzing your query to provide a response',
                                                            reasoning: 'Processing your request and preparing the response.',
                                                            startTime: Date.now()
                                                        }],
                                                        currentReasoningStep: 0,
                                                        // Attach browser session if it was detected from start_browser tool
                                                        ...(pendingBrowserSession || {})
                                                    };
                                                    // eslint-disable-next-line no-console
                                                    if (pendingBrowserSession) {
                                                        devLog('✅ Created intermediate message with browser session:', pendingBrowserSession);
                                                    }
                                                    return Messages.addMessage(prevMessages, intermediateMessage);
                                                });
                                            }

                                            // Update status to show we're generating response
                                            setConversationStatus('Generating Response');

                                            // Check if this is explicitly a final (non-partial) frame
                                            // Default to partial=true (streaming) if not specified
                                            const isPartial = eventData.partial !== false;

                                            // Accumulation strategy:
                                            // - partial=true events: always accumulate (streaming chunks).
                                            // - partial=false after partial=true: ADK/Claude style — the final
                                            //   event contains the COMPLETE text, not just the last token.
                                            //   We already have the full text from the streaming chunks, so
                                            //   skip the final event's text to avoid the response doubling.
                                            // - partial=false with no prior partial=true: Qwen/Ollama style —
                                            //   per-token partial=false events, so accumulate normally.
                                            if (isPartial) {
                                                hasSeenPartialTrue = true;
                                                accumulatedText += part.text;
                                            } else if (!hasSeenPartialTrue) {
                                                // Qwen/Ollama per-token mode: accumulate normally
                                                accumulatedText += part.text;
                                            }
                                            // else: ADK final event after streaming — skip to avoid doubling
                                            currentAccumulatedText = accumulatedText;

                                            if (!isPartial) {
                                                // Clear status when a final frame arrives.
                                                setConversationStatus('');
                                            }

                                        // Avoid UI updates during incomplete markdown structures to prevent poor rendering
                                        // Skip updates if we're inside an incomplete mermaid block or list item
                                        // BUT always update if this is the final frame (isPartial=false)
                                        const isIncompleteStructure = Messages.isInsideIncompleteMermaidBlock(currentAccumulatedText) ||
                                                                Messages.isInsideIncompleteListItem(currentAccumulatedText);

                                        const shouldSkipUpdate = isPartial && isIncompleteStructure;

                                        if (!shouldSkipUpdate) {
                                            setMessages(prevMessages => {
                                                // Always keep isIntermediate=true while the stream is active.
                                                // The message is finalised (isIntermediate→false, isStreaming→false)
                                                // only by the post-loop finalizer block.
                                                const updated = Messages.updateOrCreateIntermediateMessage(prevMessages, currentAccumulatedText, true);

                                                // Attach pending browser session if available (only once)
                                                if (pendingBrowserSession) {
                                                    // eslint-disable-next-line no-console
                                                    devLog('✅ Attaching browser session to message:', pendingBrowserSession);

                                                    const result = updated.map(msg => {
                                                        if (msg.isIntermediate || (!isPartial && msg.id === updated[updated.length - 1].id)) {
                                                            return {
                                                                ...msg,
                                                                ...pendingBrowserSession
                                                            };
                                                        }
                                                        return msg;
                                                    });

                                                    // Clear pending session after attaching to prevent re-attachment on every render
                                                    pendingBrowserSession = null;

                                                    return result;
                                                }

                                                // Attach MCP App metadata if available (when response is final)
                                                if (!isPartial && window._mcpAppMetadata && window._mcpAppMetadata[sessionId]) {
                                                    const mcpAppData = window._mcpAppMetadata[sessionId];
                                                    // eslint-disable-next-line no-console
                                                    devLog('✅ Attaching MCP App data to message:', mcpAppData);

                                                    return updated.map(msg => {
                                                        if (msg.isIntermediate) {
                                                            return {
                                                                ...msg,
                                                                mcpApp: mcpAppData
                                                            };
                                                        }
                                                        return msg;
                                                    });
                                                }

                                                return updated;
                                            });
                                        }

                                        // If canvas is open and this is the final frame, check for code updates
                                        if (isCanvasMode && !isPartial) {
                                            const codeUpdates = extractAllCodeUpdates(
                                                currentAccumulatedText,
                                                canvasProjectContext?.selectedFile?.path || null
                                            );

                                            if (codeUpdates && codeUpdates.length > 0) {
                                                // Collect modified files for summary display
                                                const modifiedFiles = [];

                                                // Process each code update
                                                for (const codeUpdate of codeUpdates) {
                                                    const eventDetail = {
                                                        filePath: codeUpdate.filePath,
                                                        projectId: canvasProjectContext?.projectId,
                                                        serviceName: canvasProjectContext?.selectedMicroservice?.name,
                                                        isStreaming: false
                                                    };

                                                        // Track modified file
                                                        modifiedFiles.push({
                                                            path: codeUpdate.filePath,
                                                            operation: codeUpdate.updateType || 'modified',
                                                            projectId: canvasProjectContext?.projectId,
                                                            serviceName: canvasProjectContext?.selectedMicroservice?.name
                                                        });

                                                        // Add appropriate fields based on update type
                                                        if (codeUpdate.isDiff && codeUpdate.updateType === 'diff') {
                                                            // Unified diff format - use custom diff provider
                                                            eventDetail.updateType = 'diff';
                                                            eventDetail.diffContent = codeUpdate.diffContent;
                                                            eventDetail.content = codeUpdate.diffContent;
                                                        } else if (codeUpdate.isPartialUpdate && codeUpdate.updateType === 'replace') {
                                                            // Partial replacement
                                                            eventDetail.updateType = 'replace';
                                                            eventDetail.oldString = codeUpdate.oldString;
                                                            eventDetail.newString = codeUpdate.newString;
                                                        } else {
                                                            // Full content replacement
                                                            eventDetail.updateType = 'full';
                                                            eventDetail.content = codeUpdate.content;
                                                        }

                                                        // Use setTimeout to ensure event is dispatched after current call stack
                                                        setTimeout(() => {
                                                            window.dispatchEvent(new CustomEvent('aiContentUpdate', {
                                                                detail: eventDetail,
                                                                bubbles: true
                                                            }));

                                                            // eslint-disable-next-line no-console
                                                            devLog(`✅ AI code ${codeUpdate.isDiff ? 'diff' : codeUpdate.isPartialUpdate ? 'partial' : 'full'} update dispatched:`, codeUpdate.filePath, {
                                                                updateType: eventDetail.updateType,
                                                                projectId: eventDetail.projectId,
                                                                serviceName: eventDetail.serviceName,
                                                                diffLength: eventDetail.diffContent?.length || 0
                                                            });
                                                        }, 0);
                                                    }

                                                    // Dispatch final event with all modified files for the loader to display
                                                    // Increased delay to ensure all diffs have time to process
                                                    if (modifiedFiles.length > 0) {
                                                        // eslint-disable-next-line no-console
                                                        devLog(`📊 Waiting for ${modifiedFiles.length} file(s) to process...`);

                                                        setTimeout(() => {
                                                            window.dispatchEvent(new CustomEvent('diffParserComplete', {
                                                                detail: {
                                                                    files: modifiedFiles,
                                                                    count: modifiedFiles.length
                                                                },
                                                                bubbles: true
                                                            }));

                                                            // eslint-disable-next-line no-console
                                                            devLog(`✅ All ${modifiedFiles.length} file update(s) dispatched and processing complete`);
                                                        }, 1000); // Increased delay to ensure all async diff applications complete
                                                    }

                                                    // Monaco editor will handle localStorage save via its event listener
                                                }

                                                // DON'T update canvas content when code updates were extracted
                                                // The files will be opened individually by MonacoFileEditor/RepositoryTree
                                                // Showing the full AI response in Monaco would be confusing

                                                // Only update canvas if NO code updates were found (pure documentation response)
                                                if (!codeUpdates || codeUpdates.length === 0) {
                                                    const hasCode = /```[\s\S]+```/g.test(currentAccumulatedText) ||
                                                                   /function\s+\w+\s*\(/g.test(currentAccumulatedText) ||
                                                                   /<\/?\w+[^>]*>/g.test(currentAccumulatedText);

                                                    setCanvasContent({
                                                        type: hasCode ? 'code' : 'document',
                                                        content: currentAccumulatedText,
                                                        title: 'Generated Content'
                                                    });
                                                }
                                            }
                                        }
                                    }
                                }
                            } catch (parseError) {
                                // Malformed SSE frame -> stop streaming and inform the user
                                if (event.data.trim()) {
                                    // Try to extract meaningful error information from the event data
                                    let errorMessage = 'Unable to process server response';
                                    let errorDetails = '';
                                    let isRateLimitError = false;

                                    try {
                                        const rawData = event.data.trim();

                                        // Log the full raw data for debugging (limit to 2000 chars)
                                        // eslint-disable-next-line no-console
                                        console.error('📋 Full error data:', rawData.substring(0, 2000));

                                        // Check if it's a rate limit error from the backend
                                        if (rawData.includes('RateLimitError') || rawData.includes('RESOURCE_EXHAUSTED') || rawData.includes('429')) {
                                            isRateLimitError = true;
                                            errorMessage = '⏱️ Rate Limit Exceeded';
                                            errorDetails = 'The AI service is currently experiencing high demand.';

                                            // Try to extract specific error message
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/)
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1];
                                            }
                                        }
                                        // Check for Vertex AI / model not found errors
                                        else if (rawData.includes('NOT_FOUND') || rawData.includes('404') || rawData.includes('not found')) {
                                            errorMessage = '🚫 Model Configuration Error';

                                            // Try to extract the full error message
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/)
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1];
                                            } else {
                                                // Show more of the raw error for 404s
                                                errorDetails = rawData.substring(0, 1000);
                                            }

                                            // Add helpful context
                                            if (!errorDetails.includes('fix')) {
                                                errorDetails += '\n\nThe AI model specified in the backend configuration may not be available. Please check the backend logs for details.';
                                            }
                                        }
                                        // Check for authentication errors
                                        else if (rawData.includes('AuthenticationError') || rawData.includes('auth') || rawData.includes('401') || rawData.includes('403')) {
                                            errorMessage = '🔐 Authentication Error';
                                            errorDetails = 'Authentication is required. Please check your credentials and try again.';

                                            // Try to extract more details
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/)
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1];
                                            }
                                        }
                                        // Check for other error types
                                        else if (rawData.includes('Error') || rawData.includes('Exception')) {
                                            // Try to extract error class name
                                            const errorTypeMatch = rawData.match(/(\w+Error|\w+Exception)/);
                                            if (errorTypeMatch && errorTypeMatch[1]) {
                                                errorMessage = `${errorTypeMatch[1].replace(/([A-Z])/g, ' $1').trim()}`;
                                            }

                                            // Try to extract error message - be more aggressive
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/) ||
                                                                rawData.match(/message:\s*([^,}\n]+)/);
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1].trim();
                                            }

                                            // If still no details, try to extract from error object
                                            if (!errorDetails) {
                                                const errorObjMatch = rawData.match(/"error":\s*\{([^}]+)\}/);
                                                if (errorObjMatch && errorObjMatch[1]) {
                                                    errorDetails = errorObjMatch[1].replace(/"/g, '').trim();
                                                }
                                            }
                                        }

                                        // If we couldn't parse anything useful, show more of the raw data
                                        if (!errorDetails && rawData.length < 1000) {
                                            errorDetails = rawData;
                                        } else if (!errorDetails) {
                                            errorDetails = rawData.substring(0, 1000) + '...';
                                        }
                                    } catch (e) {
                                        // If all parsing fails, stick with generic message
                                        // eslint-disable-next-line no-console
                                        console.error('Error parsing backend error:', e);
                                    }

                                    // If it's a rate limit error, trigger retry
                                    if (isRateLimitError) {
                                        // eslint-disable-next-line no-console
                                        devLog('🔄 Rate limit error detected - triggering retry');
                                        stopProcessingEvents = true;
                                        // Store error info for retry handler
                                        parseError.isRateLimitError = true;
                                        parseError.errorMessage = errorMessage;
                                        parseError.errorDetails = errorDetails;
                                        throw parseError; // Re-throw to be caught by outer retry logic
                                    }

                                    // For non-rate-limit errors, show the error and stop
                                    setIsLoading(false);
                                    setConversationStatus('');

                                    const displayMessage = errorDetails ?
                                        `${errorMessage}\n\n${errorDetails}` :
                                        errorMessage;

                                    const finalErrorMessage = Messages.createErrorMessage(displayMessage);
                                    setMessages(prevMessages => Messages.addMessage(prevMessages, finalErrorMessage));
                                    stopProcessingEvents = true; // halt further SSE processing
                                    return;
                                }
                            }
                        },
                        onError: (error) => {
                            setIsLoading(false);
                            setConversationStatus('');
                            const errorMessage = Messages.createErrorMessage(`Stream parsing error: ${error.message}`);
                            setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                            stopProcessingEvents = true; // halt further SSE processing
                        }
                    });

                    do {
                        const {done, value} = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, {stream: true});
                        parser.feed(chunk);
                    } while (!stopProcessingEvents);

                    // Finalise the last streaming message: clear isStreaming/isIntermediate flags
                    // so the bubble is no longer treated as in-progress.
                    if (intermediateMessageCreated) {
                        setMessages(prevMessages => {
                            const last = prevMessages[prevMessages.length - 1];
                            if (last && last.type === 'received' && (last.isStreaming || last.isIntermediate)) {
                                return prevMessages.slice(0, -1).concat([{
                                    ...last,
                                    isIntermediate: false,
                                    isStreaming: false
                                }]);
                            }
                            return prevMessages;
                        });
                    }
                } catch (streamError) {
                    // Handle stream reading errors (e.g., ERR_INCOMPLETE_CHUNKED_ENCODING)
                    setIsLoading(false);
                    setAbortController(null);
                    setConversationStatus('');

                    // Check if this was an abort (user stopped generation)
                    if (streamError.name === 'AbortError') {
                        const stoppedMessage = Messages.createInfoMessage('Response generation stopped. Feel free to ask another question.');
                        setMessages(prevMessages => Messages.addMessage(prevMessages, stoppedMessage));
                        return;
                    }

                    // Log full error details for debugging
                    // eslint-disable-next-line no-console
                    console.error('📋 Stream error details:', {
                        name: streamError.name,
                        message: streamError.message,
                        stack: streamError.stack?.substring(0, 500)
                    });

                    let errorMessage = 'Stream reading error: Connection to server was interrupted';

                    // Try to extract more meaningful error information
                    if (streamError.message) {
                        // Check for specific error patterns
                        if (streamError.message.includes('JSON') || streamError.message.includes('parse')) {
                            errorMessage = 'Error parsing server response';

                            // If there's a JSON parsing error, it might indicate a backend error was sent
                            if (streamError.message.includes('position')) {
                                errorMessage += '. The server may have sent an error message.';
                            }
                        } else if (streamError.message.includes('network') || streamError.message.includes('fetch')) {
                            errorMessage = 'Network error: Unable to connect to server';
                        } else if (streamError.message.includes('timeout')) {
                            errorMessage = 'Request timeout: Server took too long to respond';
                        }

                        // Append the original error message for context (limit length)
                        const errorDetail = streamError.message.length > 500
                            ? streamError.message.substring(0, 500) + '...'
                            : streamError.message;
                        errorMessage += ` (${errorDetail})`;
                    }

                    // If we've already accumulated some text, it means the agent was working
                    if (accumulatedText) {
                        errorMessage = 'Server connection interrupted. The response may be incomplete.';
                    }

                    const errorMsg = Messages.createErrorMessage(errorMessage);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMsg));
                } finally {
                    reader.releaseLock();
                }

                // Success - end loading state and exit retry loop
                setIsLoading(false);
                setConversationStatus('');
                setAbortController(null);

                // Check if we should ask about Mia components setup after this response
                const shouldAskAboutComponents = sessionStorage.getItem('mia_ask_about_components_next');
                if (shouldAskAboutComponents === 'true') {
                    sessionStorage.removeItem('mia_ask_about_components_next');

                    // Wait a moment, then append a follow-up message about Mia components
                    setTimeout(() => {
                        setMessages(prev => [
                            ...prev,
                            {
                                id: `system-${Date.now()}`,
                                role: 'assistant',
                                content: '\n\n---\n\n**🎯 Next Steps: Mia Platform Components**\n\nYour frontend is ready! Would you like me to set up additional Mia Platform components for your project?\n\n- 🌐 **API Gateway** (e.g., Envoy) - Route and manage API traffic\n- 📊 **CRUD Service** - Create database collections and REST APIs\n- 🔐 **Authentication** - Add user authentication and authorization\n- 📈 **Monitoring** - Set up logging and metrics\n- 💾 **Database** - Configure MongoDB or other data stores\n\nJust let me know what components you need, or say "skip" to continue coding!',
                                timestamp: new Date().toISOString(),
                                text: '\n\n---\n\n**🎯 Next Steps: Mia Platform Components**\n\nYour frontend is ready! Would you like me to set up additional Mia Platform components for your project?\n\n- 🌐 **API Gateway** (e.g., Envoy) - Route and manage API traffic\n- 📊 **CRUD Service** - Create database collections and REST APIs\n- 🔐 **Authentication** - Add user authentication and authorization\n- 📈 **Monitoring** - Set up logging and metrics\n- 💾 **Database** - Configure MongoDB or other data stores\n\nJust let me know what components you need, or say "skip" to continue coding!',
                                type: 'received'
                            }
                        ]);
                    }, 800);
                }

                // Check if there's pending file content to auto-send
                if (window._pendingFileContent && window._pendingFileSessionId === sessionId) {
                    const fileContent = window._pendingFileContent;
                    delete window._pendingFileContent;
                    delete window._pendingFileSessionId;

                    // eslint-disable-next-line no-console
                    devLog('📤 Auto-sending file content to AI...');

                    // Brief delay to let UI update
                    setTimeout(async () => {
                        // Ensure fileContent is a string
                        const contentStr = typeof fileContent === 'string' ? fileContent : JSON.stringify(fileContent);

                        // Add user message showing we're sending files
                        const userMessage = Messages.createUserMessage(contentStr);
                        setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));

                        // Re-trigger send with file content
                        setInputValue(contentStr);
                        setTimeout(() => {
                            // Manually trigger send
                            document.querySelector('.chat-input-submit')?.click();
                        }, 50);
                    }, 500);
                }

                break; // Exit retry loop on success

            } catch (error) {
                // Check if this is a rate limit error that should be retried
                const isRateLimitError = error.isRateLimitError ||
                    (error.message && (error.message.includes('RateLimitError') ||
                     error.message.includes('RESOURCE_EXHAUSTED') ||
                     error.message.includes('429')));

                const isLastAttempt = attempt === maxRetries - 1;

                if (isRateLimitError && !isLastAttempt) {
                    // Calculate exponential backoff delay: 2^(attempt+1) seconds
                    const delaySeconds = Math.pow(2, attempt + 1);

                    // eslint-disable-next-line no-console
                    devLog(`⏳ Rate limit error - retrying in ${delaySeconds}s (attempt ${attempt + 2}/${maxRetries})...`);

                    setConversationStatus(`⏱️ Rate limit reached. Retrying in ${delaySeconds} seconds (attempt ${attempt + 2}/${maxRetries})...`);

                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, delaySeconds * 1000));

                    // Reset status for next attempt
                    setConversationStatus('Retrying request...');

                    // Continue to next iteration of retry loop
                    continue;
                }

                // Non-retriable error or max retries exceeded
                setIsLoading(false);
                setAbortController(null);
                setConversationStatus('');

                // Check if this was an abort (user stopped generation)
                if (error.name === 'AbortError') {
                    return; // Don't show error message, we already handled it
                }

                // Show appropriate error message
                let errorMessage = error.message;
                if (isRateLimitError && isLastAttempt) {
                    errorMessage = '⏱️ Rate limit error persists after multiple retries. Please try again in a few minutes.';
                }

                const errorMsg = Messages.createErrorMessage(errorMessage);
                setMessages(prevMessages => Messages.addMessage(prevMessages, errorMsg));
                break; // Exit retry loop on error
            }
            } // End retry loop
        }
    }, [inputValue, setMessages, setIsLoading, sessionId, canvasProjectContext, setCanvasContent, extractCodeUpdate, filesModifiedInCanvas, buildFileContextWithFreshContent, extractAllCodeUpdates, sessionFilesSent, attachedFiles]);

    /**
     * Adds the current input to the message queue to be sent after the active generation completes.
     * Keyboard shortcut: Alt+Enter (⌥Enter)
     */
    const handleAddToQueue = useCallback(() => {
        if (typeof inputValue === 'string' && inputValue.trim()) {
            setMessageQueue(prev => [...prev, { inputValue: inputValue.trim(), attachedFiles: [...attachedFiles] }]);
            setInputValue('');
            setAttachedFiles([]);
            setShowSendOptions(false);
        }
    }, [inputValue, attachedFiles]);

    /**
     * Stops the current generation and immediately sends the typed message.
     */

    /**
     * Sends the typed message as a steering input while generation continues.
     * When not generating this is equivalent to a regular send.
     * Keyboard shortcut: Enter
     */
    const handleSteerWithMessage = useCallback(() => {
        setShowSendOptions(false);
        handleSendMessage();
    }, [handleSendMessage]);

    // Process message queue: when generation stops, send the next queued message
    useEffect(() => {
        if (!isLoading && messageQueue.length > 0) {
            const [next, ...rest] = messageQueue;
            setMessageQueue(rest);
            setInputValue(next.inputValue);
            setAttachedFiles(next.attachedFiles);
            // Use a timeout so the state updates propagate before sending
            setTimeout(() => {
                handleSendMessage();
            }, 100);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    // Effect to handle AI message requests from Monaco editor error tooltips
    useEffect(() => {
        const handleAIMessageRequest = (event) => {
            devLog('📨 Received sendAIMessage event:', event.detail);
            const { message, context } = event.detail;

            if (message) {
                devLog('✍️ Setting input value:', message);
                // Set the input value with the AI request
                setInputValue(message);

                // Trigger send after state update
                const sendTimeout = setTimeout(() => {
                    devLog('🚀 Calling handleSendMessage...');
                    // Create a synthetic form submission event
                    const syntheticEvent = {
                        preventDefault: () => {},
                        stopPropagation: () => {}
                    };
                    handleSendMessage(syntheticEvent);
                }, 100);

                return () => clearTimeout(sendTimeout);
            } else {
                devLog('⚠️ No message in sendAIMessage event');
            }
        };

        window.addEventListener('sendAIMessage', handleAIMessageRequest);

        return () => {
            window.removeEventListener('sendAIMessage', handleAIMessageRequest);
        };
    }, [handleSendMessage, setInputValue]);

    // Handle file selection

    // Read file as base64
    const readFileAsBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // Extract base64 content (remove data:*/*;base64, prefix)
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Remove attached file

    // Trigger file input click

    // Handler for opening Save Prompt modal

    // Handler for saving prompt to marketplace
    const handleSavePrompt = useCallback(async (promptData) => {
        try {
            await miaPlatformDataService.saveAIPrompt(promptData);
            showToast(`Prompt "${promptData.name}" saved successfully!`, 'success');
        } catch (error) {
            showToast(`Failed to save prompt: ${error.message}`, 'error');
            throw error; // Re-throw so modal can handle it
        }
    }, []);

    // Handler for opening save message modal

    // Handler for saving message to marketplace
    const handleSaveMessage = useCallback(async (messageData) => {
        try {
            await miaPlatformDataService.saveAIMessage(messageData);
            showToast(`Message "${messageData.name}" saved successfully!`, 'success');
        } catch (error) {
            showToast(`Failed to save message: ${error.message}`, 'error');
            throw error; // Re-throw so modal can handle it
        }
    }, []);

    // Success callback for file save
    handleSaveMessage.successCallback = (message) => {
        showToast(message, 'success');
    };

    // Drag and drop handlers




    // Format file size for display
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    /**
     * Sends a message directly with the provided text (for shortcuts/quick actions).
     * This bypasses the inputValue state to ensure immediate sending.
     */

    /**
     * Saves the current conversation (name, messages, session) to the backend.
     * If it's a new conversation, the backend will assign and return a new ID.
     * Auto-generates title from first user message if not set.
     */
    const handleSaveConversation = useCallback(async () => {
        // Safeguard: Don't save if we're updating an existing conversation but have no messages
        // This prevents race conditions from overwriting the database with empty data
        if (conversationId && messages.length === 0) {
            console.warn('Prevented saving empty messages array for existing conversation');
            return;
        }

        // Auto-generate title from first message if not already set
        let titleToSave = convName;
        if (!titleToSave && messages.length > 0) {
            // Find first user message
            const firstUserMessage = messages.find(msg => msg.type === 'sent');
            if (firstUserMessage) {
                // Take first 50 characters as title
                titleToSave = firstUserMessage.text.substring(0, 50);
                if (firstUserMessage.text.length > 50) {
                    titleToSave += '...';
                }
                setConvName(titleToSave);
            }
        }

        // Filter out intermediate messages before saving (keep only final messages)
        const finalMessages = messages.filter(msg => !msg.isIntermediate);

        const appName = AdkClient.overrideAppName || AdkClient.APP_NAME;
        const convMode = hideCanvas ? 'compose' : 'code';
        const convMetadata = { mode: convMode };
        if (!hideCanvas && canvasProjectContext?.projectId) {
            convMetadata.projectId = canvasProjectContext.projectId;
            convMetadata.projectName = canvasProjectContext.project?.name || canvasProjectContext.name || canvasProjectContext.projectId;
            convMetadata.tenantId = params.tenantId || canvasProjectContext?.tenant?.tenantId || null;
        }
        // Track playbook association so it can be restored when the memory is reopened
        if (customAIInstructionsPlaybookData?.itemId) {
            convMetadata.playbookItemId = customAIInstructionsPlaybookData.itemId;
            if (!convMetadata.tenantId) {
                convMetadata.tenantId = customAIInstructionsPlaybookData.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null;
            }
        }
        const result = await Conversations.saveConversation(conversationId, titleToSave, finalMessages, sessionId, selectedTools, appName, convMetadata);
        if (result.success) {
            const wasNewConversation = !conversationId;
            // eslint-disable-next-line no-console
            devLog('💾 Conversation saved', {
                wasNewConversation,
                conversationId: result.conversationId,
                messageCount: finalMessages.length
            });
            // This is mia-flow-be's own legacy chat-history id landing on
            // top of the real ADK/VFS session id — not a real conversation
            // switch — so don't let it wipe chatBoundProjectId/
            // chatHasActivity (see that reset effect's own doc, above, for
            // the live-confirmed regression this guards).
            skipNextConversationResetRef.current = true;
            setConversationId(result.conversationId);

            // Update URL with conversationId if this was a new conversation
            // This prevents the useEffect from thinking user navigated away and clearing messages
            if (wasNewConversation && result.conversationId) {
                // IMPORTANT: Update the ref BEFORE navigate to prevent clearing
                prevUrlConversationIdRef.current = result.conversationId;
                // eslint-disable-next-line no-console
                devLog('🔗 Updating URL with conversationId:', result.conversationId);
                const currentPath = location.pathname;
                navigate(`${currentPath}?conversationId=${result.conversationId}`, { replace: true });
            }

            // Set session tools on first save (lock in the tools for this session)
            if (!sessionTools) {
                setSessionTools(selectedTools);
                setShowToolChangeNotice(false);
            }

            // Dispatch event to notify sidebar to refresh
            window.dispatchEvent(new CustomEvent('conversationSaved', {
                detail: {conversationId: result.conversationId}
            }));
        } else {
            showToast(`Failed to save conversation: ${result.message}`, 'error');
        }
    }, [conversationId, convName, messages, sessionId, showToast, navigate, location.pathname, selectedTools, sessionTools]);

    // Auto-save new conversations once (after first complete exchange)
    useEffect(() => {
        // Prevent duplicate saves using ref
        if (isSavingRef.current) {
            return;
        }

        const performAutoSave = async () => {
            // Only save new conversations (no conversationId yet)
            // Existing conversations are not updated (PATCH removed)
            if (!conversationId && messages.length >= 2) {
                // Guard: skip if no new messages were added this session
                // (e.g. messages were just restored from localStorage)
                if (messages.length <= restoredMessageCountRef.current) {
                    return;
                }

                // Auto-save new conversations ONLY after first complete exchange
                // (user message + non-intermediate assistant response)
                // This prevents multiple saves during streaming
                const lastMessage = messages[messages.length - 1];
                const hasUserMessage = messages.some(msg => msg.type === 'sent');
                const hasCompleteResponse = lastMessage &&
                                            lastMessage.type === 'received' &&
                                            !lastMessage.isIntermediate;

                // Only save when we have a complete response (not intermediate streaming updates)
                if (hasUserMessage && hasCompleteResponse) {
                    isSavingRef.current = true;
                    await handleSaveConversation();
                    // Don't reset flag here - it will be reset when conversationId is set
                }
            }
        };

        performAutoSave();
    }, [messages, conversationId, handleSaveConversation]);

    // Keep input focused after state changes
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [isLoading]);

    // Controlled component handler for the text input

    // Auto-resize textarea logic
    useLayoutEffect(() => {
        if (inputRef.current) {
            // Reset height to allow shrinking
            inputRef.current.style.height = 'auto';

            // Calculate scrollHeight (max 200px or similar to avoid taking full screen)
            const scrollHeight = inputRef.current.scrollHeight;
            const maxHeight = 200; // Limit max growth

            // Set new height based on content
            inputRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;

            // Enable scrolling if content exceeds maxHeight
            inputRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
        }
    }, [inputValue]);

    // Auto-resize textarea logic
    useLayoutEffect(() => {
        if (inputRef.current) {
            // Reset height to allow shrinking
            inputRef.current.style.height = 'auto';

            // Calculate scrollHeight (max 200px or similar to avoid taking full screen)
            const scrollHeight = inputRef.current.scrollHeight;
            const maxHeight = 200; // Limit max growth

            // Set new height based on content
            inputRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;

            // Enable scrolling if content exceeds maxHeight
            inputRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
        }
    }, [inputValue]);

    // Select a spec-kit slash command from the autocomplete menu
    const handleSlashCommandSelect = useCallback((command) => {
        setInputValue(command + ' ');
        setShowSlashMenu(false);
        setSlashMenuIndex(0);
        try { localStorage.setItem('chatInputValue', command + ' '); } catch { /* ignore */ }
        setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
    }, [setInputValue, inputRef]);

    // Sends message on Enter key press (but allows Shift+Enter for newlines).
    const handleKeyDown = useCallback((event) => {
        // Slash command menu navigation
        if (showSlashMenu) {
            const cmds = filterSpeckitCommands(inputValue, catalogSpecCommands.length ? catalogSpecCommands : undefined);
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSlashMenuIndex(i => Math.min(i + 1, cmds.length - 1));
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSlashMenuIndex(i => Math.max(i - 1, 0));
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setShowSlashMenu(false);
                return;
            }
            if (event.key === 'Tab') {
                const cmd = cmds[slashMenuIndex];
                if (cmd) {
                    event.preventDefault();
                    handleSlashCommandSelect(cmd.command);
                    return;
                }
            }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (event.altKey) {
                handleAddToQueue();
            } else if (isLoading) {
                handleSteerWithMessage();
            } else {
                handleSendMessage();
            }
        }
    }, [showSlashMenu, inputValue, slashMenuIndex, handleSlashCommandSelect, handleSendMessage, handleAddToQueue, isLoading, handleSteerWithMessage]);

    // "Fix with AI" (a preview's build error), a file's errors, a pipeline job: the message goes into the project chat
    // as a `pendingMessage` — sent even mid-conversation — and Chat is turned on first if it is off. It used to go
    // through `pendingChatMessage` -> the widget's `initialMessage`, which the widget ignores once the conversation has
    // started (always the case with `?conversationId=` in the URL): the click did nothing. See utils/askAiInChat.js.
    // `{ prefill: true }` (the Data tab's "Ask the AI to add it"): the prompt is written into the chat's input for the
    // person to finish and send, not sent.
    const askAIInProjectChat = (prompt, { prefill = false } = {}) => (!chatFeatureOn && !chatAvailable ? (showToast('Flow’s assistant is not included in your plan', 'info'), null) : askAiInChat(prompt, {
        setPendingMessage: setPendingAskAIMessage,
        setPendingDraft: setPendingAskAIDraft,
        prefill,
        chatOn: chatFeatureOn,
        turnOnChat: setChatFeatureOn,
        onChatTurnedOn: () => showToast(prefill ? 'Chat is on: finish the message and send it' : 'Chat is on: the error was sent to the assistant', 'info'),
        showChatPane: compact && !hideCanvas ? () => setCompactPane('chat') : undefined,
    }));
    const askAIInProjectChatRef = useRef(askAIInProjectChat);
    askAIInProjectChatRef.current = askAIInProjectChat;

    // Listen for Ask AI from logs event. PreviewLogs.jsx now sends
    // {message, logText} instead of one merged prompt string — `message` is
    // a short, normal user-facing chat message; `logText` is the raw log
    // dump, which goes to the agent via `pageContext` (ChatWidget's own
    // `state_delta.page_context` mechanism — injected into the agent's
    // system instructions, never rendered as message text) instead of being
    // pasted into the visible transcript. See `pendingAskAIPageContext`
    // passed down to VfsChatPanel below.
    //
    // Uses `pendingMessage` (VfsChatPanel's `pendingMessage` prop -> the
    // vendored widget's own `pendingMessage`, mia-chatbot-widget 0.2.0-5+),
    // NOT `pendingChatMessage`/`initialMessage` — task #158's real root
    // cause: the widget's `initialMessage` effect deliberately refuses to
    // send once the conversation has already started (`if (hasStarted)
    // return`, by design, so a remount doesn't resend into an ongoing
    // conversation). "Ask AI" is clicked mid-conversation almost always —
    // the whole point is reacting to errors from code the agent just wrote
    // — so routing it through `initialMessage` silently no-op'd in exactly
    // the common case. `pendingMessage` is a new widget capability added
    // specifically for this: injects into an ALREADY-active conversation,
    // keyed by a fresh `id` per click so it always fires.
    useEffect(() => {
        const handleAskAIFromLogs = (event) => {
            const {message, prompt, logText} = event.detail || {};
            // `prompt` fallback: tolerate an older/other caller that still
            // sends the single-string shape.
            const chatMessage = message || prompt;
            if (chatMessage) {
                setPendingAskAIMessage({ id: `ask-ai-${Date.now()}`, text: chatMessage });
            }
            if (logText) {
                setPendingAskAIPageContext({
                    page: 'preview-logs',
                    path: location.pathname,
                    summary: 'The user clicked "Ask AI" on their preview\'s current build/runtime logs.',
                    data: { previewErrors: logText },
                });
                // One-shot: ChatWidget's own doc says pageContext "travels
                // with every message while the user stays on the page" — it
                // is designed as a persistent "what screen is the user on"
                // description, not a one-off payload. Clear it shortly after
                // so it doesn't keep attaching this one click's error dump to
                // every unrelated later turn in the same conversation.
                setTimeout(() => setPendingAskAIPageContext(null), 4000);
            }
        };

        window.addEventListener('askAIFromLogs', handleAskAIFromLogs);

        return () => {
            window.removeEventListener('askAIFromLogs', handleAskAIFromLogs);
        };
    }, []);

    // Listen for Ask AI from file errors event
    useEffect(() => {
        const handleAskAIFromFile = (event) => {
            const {filePath, fileContent, errorDetails} = event.detail || {};
            if (filePath && fileContent) {
                const ext = filePath.split('.').pop();
                const prompt = `Fix the following errors on file "${filePath}" by providing the corrected code.\n\n**Errors:**\n${errorDetails}\n\n**Current file content:**\n\`\`\`${ext}\n${fileContent}\n\`\`\`\n\n`;

                askAIInProjectChatRef.current(prompt);
            }
        };

        window.addEventListener('askAIFromFile', handleAskAIFromFile);

        return () => {
            window.removeEventListener('askAIFromFile', handleAskAIFromFile);
        };
    }, []);

    // Listen for Ask AI from pipeline jobs event
    useEffect(() => {
        const handleAskAIFromPipeline = (event) => {
            const {jobName, jobStatus, jobStage, jobLog, prompt} = event.detail || {};
            if (prompt) {
                askAIInProjectChatRef.current(prompt);
            }
        };

        window.addEventListener('askAIFromPipeline', handleAskAIFromPipeline);

        return () => {
            window.removeEventListener('askAIFromPipeline', handleAskAIFromPipeline);
        };
    }, []);

    // Handle initial prompt from Home page navigation
    useEffect(() => {
        const initialPrompt = location.state?.initialPrompt;
        const mode = location.state?.mode;
        const initialFiles = location.state?.initialFiles;

        if (initialPrompt && !messages.length) {
            // `pendingChatMessage` itself is now seeded synchronously from
            // this same `location.state` in its own lazy useState
            // initializer (see that declaration's comment) — no longer set
            // here, so /chat's very first render already has it instead of
            // a second render after this effect fires. This effect still
            // handles everything else that needs to happen once per
            // Home->chat handoff: aiMode, chatHasActivity, attached-file
            // conversion.
            //
            // BUG FIX (live-reproduced via Playwright, real regression):
            // `chatHasActivity` used to only ever become true via
            // onActivityStart — VfsChatPanel's callback, itself fired from
            // the vendored widget's own onChatStarted, which only fires
            // once the widget has actually started processing the message
            // internally. That leaves a real gap between "Home hands off a
            // pending prompt to /chat" (this exact moment) and "the widget
            // gets around to firing onChatStarted" — a live-measured ~170ms+
            // window where `chatHasActivity` is still false, so goToCode's
            // own "chat has activity, wait for the real project" guard (`if
            // (chatHasActivity) { showToast(...); return; }`) is ALSO
            // skipped — falling straight through to creating a brand-new,
            // wrong ephemeral project instead of the one this prompt is
            // about to build. Set it synchronously here instead: the
            // instant a prompt is queued to auto-send, activity is
            // imminent, well before the widget's own async confirmation.
            setChatHasActivity(true);

            // Canvas is always enabled, just set AI mode based on user selection
            if (mode === 'code') {
                setAiMode('agent');
            } else if (mode === 'chat') {
                setAiMode('plan');
            } else if (mode === 'agent') {
                // Home's "Agent" flow (task #26): preset the in-chat toggle to full
                // autonomy, same aiMode the Code flow uses, but with no project
                // created up front — the agent creates one itself only if/when it
                // needs to write files.
                setAiMode('agent');
            }

            // Convert raw File objects to the expected {name, type, size, content, isImage, isPDF} format
            const loadFiles = async () => {
                if (initialFiles && initialFiles.length > 0) {
                    const converted = [];
                    for (const file of initialFiles) {
                        // Already converted (has a content property)
                        if (file.content !== undefined) {
                            converted.push(file);
                            continue;
                        }
                        try {
                            const content = await readFileAsBase64(file);
                            const isImage = file.type.startsWith('image/');
                            const isPDF = file.type === 'application/pdf';
                            const isText = !isImage && !isPDF;
                            converted.push({
                                name: file.name,
                                type: file.type || 'text/plain',
                                size: file.size,
                                content,
                                isImage,
                                isPDF,
                                isText,
                            });
                        } catch {
                            console.error('Failed to read file:', file.name);
                        }
                    }
                    if (converted.length > 0) {
                        setAttachedFiles(converted);
                    }
                }

                // Set the input value
                setInputValue(initialPrompt);

                // Do NOT also call handleSendMessage() here — the comment
                // right above this block already says the legacy
                // `messages`/`handleSendMessage` transcript is unreachable in
                // the real app (chat always renders through VfsChatPanel),
                // but nothing actually stopped this function from falling
                // through into it: there was no `return` after
                // `setPendingChatMessage`, so BOTH the correct delivery
                // (VfsChatPanel's `initialMessage`) AND this legacy call ran
                // on every Home->chat navigation. Live-confirmed real bug
                // this caused: the legacy pipeline's own SSE/diff-extraction
                // path (`extractAllCodeUpdates` -> `aiContentUpdate` ->
                // Canvas.jsx's `monacoFileChanged` handler) went on to
                // process hundreds of unrelated files (including stale
                // `.npm/_cacache/...` entries from old local IndexedDB
                // state) as if they were live AI diffs, in an effectively
                // unbounded loop, alongside the real, correct VfsChatPanel
                // conversation running normally in parallel.

                // Live-confirmed severe regression: `pendingChatMessage` was
                // NEVER cleared back to null after being consumed here — it
                // became VfsChatPanel's `initialMessage` prop, read ONCE per
                // mount (same non-reactive contract as `initialSessionId`,
                // see that file's own comment). Every time the agent's own
                // vfs_create_project redirect (task #142's handler, in
                // VfsChatPanel.jsx) remounted VfsChatPanel under a new
                // project id, the STILL-SET pendingChatMessage got re-read
                // as a fresh `initialMessage` and auto-resent verbatim — the
                // agent, starting a brand-new empty session with no memory
                // of its own prior project, would often create YET ANOTHER
                // project, remount again, resend again. Confirmed live: a
                // single "create a wordle game" produced 15 chained
                // vfs_create_project calls a few seconds apart before the
                // test's own timeout cut it off — a real, unbounded loop
                // that burns API calls and never lets the conversation
                // settle. Remember which project this prompt belongs to —
                // the clearing effect below (search
                // `pendingChatMessageProjectIdRef`) fires the moment the
                // project actually changes away from it, which is exactly
                // when VfsChatPanel is about to remount. A wall-clock
                // setTimeout was tried first and is deliberately NOT used
                // here: the agent's own redirect can fire well under a
                // second after the turn starts (live-confirmed), so any
                // fixed delay either clears too early (silently swallowing
                // the prompt on the very redirect it was meant to survive —
                // also live-confirmed) or risks not clearing in time again.
                // Ref write, not a reactive read — deliberately NOT added to
                // this effect's deps below (see the eslint-disable there):
                // doing so would re-run this whole "consume the initial
                // Home prompt" effect on every later project switch, which
                // is exactly the double-delivery this fix exists to prevent.
                pendingChatMessageProjectIdRef.current = canvasProjectContext?.projectId;

                // Clear the state to prevent re-triggering. Real live bug: this
                // used to navigate to bare `location.pathname`, which silently
                // DROPS the URL's query string — for Home's no-confident-
                // playbook-match fallback (task #138), that's `/chat?
                // conversationId=<id>`, and stripping it here (seconds before
                // VfsChatPanel's own conversation even finishes streaming)
                // meant a LATER redirect — e.g. the agent deciding to build
                // anyway and calling vfs_create_project (task #142's
                // auto-redirect) — captured `location.search` as already empty
                // and landed on `/code/:tenantId/:newProjectId` with no
                // conversationId. VfsChatPanel then remounted with a brand-new
                // deterministic session id instead of resuming the real one,
                // discarding the conversation that had JUST happened.
                // Confirmed live: "create an app" showed the user's message
                // briefly, then reset to an empty "Code Generation Playbook"
                // chat on a totally different, freshly-created project.
                // Preserve the query string — only the router STATE (the
                // initialPrompt payload) needs clearing, never the search.
                navigate(location.pathname + location.search, { replace: true, state: {} });
            };

            loadFiles();
        }
        // canvasProjectContext?.projectId intentionally excluded — see the
        // ref-write comment above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, messages.length, handleSendMessage, navigate]);

    // Clears `pendingChatMessage` the moment the current project actually
    // changes away from the one it was set for — see the initialPrompt
    // effect above for why a timer can't do this reliably. Deliberately
    // skips the very first render after the ref is set (projectId hasn't
    // moved yet, nothing to clear) and does nothing once already null.
    useEffect(() => {
        if (
            pendingChatMessage &&
            // Truthy check, not just `!== null`: canvasProjectContext.projectId
            // is still unresolved (undefined) at the exact moment the
            // initialPrompt effect stashes it into the ref — a fresh Home
            // navigation's project context hasn't loaded yet when the prompt
            // is set. `undefined !== null` is true, so a naive null-check
            // let this effect fire on the FIRST real resolution of
            // projectId (mistaking "project context just finished loading"
            // for "project changed away from origin") and wiped
            // pendingChatMessage before VfsChatPanel ever mounted with a
            // real project id — live-confirmed: the message was never sent
            // at all, zero ADK events, no POST in the backend log.
            pendingChatMessageProjectIdRef.current &&
            canvasProjectContext?.projectId &&
            canvasProjectContext.projectId !== pendingChatMessageProjectIdRef.current
        ) {
            setPendingChatMessage(null);
            pendingChatMessageProjectIdRef.current = null;
        }
    }, [pendingChatMessage, canvasProjectContext?.projectId]);

    // Listen for populate-chat-input events from Canvas component
    // This populates the input with the initial prompt and either auto-sends (for home-created projects) or shows a banner
    useEffect(() => {
        const handlePopulateChatInputEvent = (event) => {
            const { message, showSendPrompt, autoSend, setAskAboutComponents } = event.detail;
            if (message) {
                devLog('📝 [MainChat] Received populate-chat-input event:', message.substring(0, 100), autoSend ? '(auto-send)' : '');

                // Auto-send message for home-created projects. The real chat surface
                // is always VfsChatPanel's ChatWidget (see the initialPrompt effect's
                // own comment above) — it has no `.send-button-icon` (that class only
                // ever existed in the legacy, unrendered MainChat transcript), so a
                // querySelector+click here unconditionally failed with "Send button
                // not found". Route through the same `pendingChatMessage` ->
                // `initialMessage` prop every other auto-send path in this file uses
                // instead of simulating a DOM click.
                if (autoSend) {
                    // Set flag to ask about Mia components after response if requested
                    if (setAskAboutComponents) {
                        sessionStorage.setItem('mia_ask_about_components_next', 'true');
                    }

                    setPendingChatMessage(message);
                } else {
                    setInputValue(message);
                }

                if (!autoSend && showSendPrompt) {
                    // Show banner to highlight send button if requested (legacy behavior)
                    setShowSendPromptBanner(true);

                    // Set flag to ask about Mia components after response if requested
                    if (setAskAboutComponents) {
                        sessionStorage.setItem('mia_ask_about_components_next', 'true');
                    }
                }
            }
        };

        window.addEventListener('populate-chat-input', handlePopulateChatInputEvent);
        return () => {
            window.removeEventListener('populate-chat-input', handlePopulateChatInputEvent);
        };
    }, [setInputValue]);

    // After config git pull completes, auto-send the pending brainstorming prompt
    useEffect(() => {
        const handleConfigPullCompleted = (event) => {
            const { success, serviceName: pulledService } = event.detail || {};
            if (!success) return;

            // Only react to the Project Configuration pull
            if (pulledService !== 'Project Configuration' && pulledService !== 'project-configuration') return;

            const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
            const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');

            if (pendingPrompt && pendingMode === 'brainstorming') {
                sessionStorage.removeItem('mia_pending_initial_prompt');
                sessionStorage.removeItem('mia_pending_initial_mode');

                const brainstormingPrompt = `Create a file named brainstorming.md that answers the following user request: "${pendingPrompt}"\n\nIn brainstorming.md, explore what is available in the Mia Platform catalog/marketplace to satisfy the request. Structure the document with:\n1. **Request Summary**: What the user wants to achieve\n2. **Available Mia Platform Components**: Relevant services, plugins, templates from the Mia catalog that can be used\n3. **Suggested Architecture**: How to combine these components to fulfill the request\n4. **Next Steps**: Concrete actions to implement the solution using Mia Platform`;

                devLog('🧠 [Brainstorming] Config pull completed, auto-triggering brainstorming prompt');

                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('populate-chat-input', {
                        detail: { message: brainstormingPrompt, autoSend: true }
                    }));
                }, 500);
            }
        };

        window.addEventListener('gitPullCompleted', handleConfigPullCompleted);
        return () => {
            window.removeEventListener('gitPullCompleted', handleConfigPullCompleted);
        };
    }, []);

    // Handler for Canvas mode (canvas is always enabled, so this is a no-op)

    // handleProjectContextChange / syncUrlWithCanvasSelections / the
    // "reconstruct URL from localStorage" effect all now come from
    // useCanvasProjectContext() above (DDD refactor Phase 4).

    // Listen for diff application failures and auto-send correct content to AI
    useEffect(() => {
        const handleDiffApplicationFailed = async (event) => {
            const {filePath, fileName, currentContent, currentLineCount, error} = event.detail;

            // eslint-disable-next-line no-console
            console.warn('🔄 Diff application failed - auto-recovering by sending correct content to AI');
            // eslint-disable-next-line no-console
            console.warn('   File:', filePath);
            // eslint-disable-next-line no-console
            console.warn('   Current lines:', currentLineCount);
            // eslint-disable-next-line no-console
            console.warn('   Error:', error);

            if (!sessionId) {
                // eslint-disable-next-line no-console
                console.warn('⚠️ Cannot auto-recover: no active session');
                return;
            }

            // Send a system message to AI with the correct file content
            const recoveryMessage = `⚠️ DIFF APPLICATION FAILED - Context Mismatch Detected

File: ${filePath}
Current file size: ${currentLineCount} lines
Error: ${error}

The diff you generated couldn't be applied because the file has a different number of lines than expected.
This usually happens when the diff was based on an outdated or incorrect version of the file.

Here is the ACTUAL current content of the file (${currentLineCount} lines):

\`\`\`${fileName?.split('.').pop() || 'text'}
${currentContent}
\`\`\`

IMPORTANT: Base your new diff on the ACTUAL content above (${currentLineCount} lines). 
Double-check that the line numbers in your diff match the actual file line count.`;

            // Add red failure message to chat with clickable file link
            const failureMessage = Messages.createErrorMessage(
                `Failed to apply changes to [${fileName}](file://${filePath})\n\n` +
                `Error: ${error}\n\n` +
                `Click the filename to view the current file content. I'll regenerate the changes based on the actual file content.`
            );

            setMessages(prevMessages => Messages.addMessage(prevMessages, failureMessage));

            // Send the recovery message to AI (but don't show it to user)
            try {
                await AdkClient.sendMessage(
                    sessionId,
                    recoveryMessage,
                    null, // No abort signal
                    canvasProjectContext,
                    true, // isCanvasMode
                    aiMode // Pass AI mode
                );
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('❌ Failed to send recovery message to AI:', err);
            }
        };

        window.addEventListener('diffApplicationFailed', handleDiffApplicationFailed);
        return () => {
            window.removeEventListener('diffApplicationFailed', handleDiffApplicationFailed);
        };
    }, [sessionId, canvasProjectContext, setMessages]);

    // Every canvas-mode project routes chat through adk-be-app + the
    // chatbot-widget package (VfsChatPanel) — no more legacy chat-container
    // fallback. A project that isn't vfs-backed yet just has an empty/no-op
    // vfs_* tool surface until the backfill below (or a real write) gives it
    // one; the chat UI itself never depended on that.
    const isVfsBackedChat = isCanvasMode && Boolean(canvasProjectContext?.projectId);

    // Silent backfill of a legacy (never vfs-backed) project into vfs-service
    // the moment its chat is opened — see indexedDbFileStorage.js's
    // backfillLegacyProjectToVfs doc comment and the control-plane-split
    // architecture note's "Full mia-flow-be decommission" section. No-ops
    // immediately (no network calls) if the project is already vfs-backed
    // or this browser has nothing cached for it. Purely a data migration now
    // (populates vfs-service so the agent has real files to work with) —
    // it no longer gates which chat UI renders, so no re-render trigger is
    // needed on success.
    useEffect(() => {
        const projectId = canvasProjectContext?.projectId;
        if (!isCanvasMode || !projectId || vfsClient.isVfsBackedProject(projectId)) {
            return;
        }
        indexedDbFileStorage.backfillLegacyProjectToVfs(projectId);
    }, [isCanvasMode, canvasProjectContext?.projectId]);

    // Task #139: Agent mode has no right-hand canvas content of its own —
    // #134 already folded workflow-run capability into this same chat (the
    // task #143: the merged Chat/Code/Agent switcher (#129/#133/#139) is
    // reverted to just Chat/Code — the /run <workflow> slash command from
    // #134 stays available inside the chat regardless, per explicit product
    // direction ("agent is included in chat"). No more agent-mode layout
    // branch; Canvas is always visible in the split layout again.

    // Task #(chat-is-default): the Chat<->Code Segmented toggle is gone —
    // Chat is the only real "home" now, and Code is a one-way destination
    // reached either by this same click-to-switch logic (still available as
    // a single button, see headerActions below) or automatically the moment
    // the agent's first vfs_get_preview_url call actually succeeds. The
    // embedded ChatColumn/ChatPreviewPanel split view that used to show a
    // preview inline in Chat is removed entirely (ChatPreviewPanel.jsx/.css
    // deleted) — Chat mode never shows a preview any more, it switches to
    // Code instead. Hoisted out of the old per-render headerActions IIFE
    // (still the exact
    // same logic that used to be goToMode's 'code' branch) so BOTH the
    // header button AND onShowExternalPreview's chat-mode branch below can
    // call it — the whole point of the auto-switch is that it reuses this
    // one, already-hardened navigation path (project-bound guard, "Code
    // must never create a new project once chat has activity" rule, etc.)
    // instead of a second, divergent copy of it.
    const goToCodeTenantId = canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null;
    const goToCodeProjectId = canvasProjectContext?.projectId || chatBoundProjectId;
    const withConversationId = (path) => conversationId
        ? `${path}${path.includes('?') ? '&' : '?'}conversationId=${encodeURIComponent(conversationId)}`
        : path;
    // Dispatches to App.jsx's own listener (a persistent ancestor that
    // survives this route swap, unlike this component) instead of calling
    // navigate() directly — covers the Chat->Code transition with a brief
    // fade instead of the abrupt unmount/mount flash a plain navigate()
    // produces. See App()'s own comment on the 'flow-navigate-with-fade'
    // listener for the full rationale.
    const navigateToCodeWithFade = (path, opts) => {
        window.dispatchEvent(new CustomEvent('flow-navigate-with-fade', { detail: { path, opts } }));
    };
    const goToCode = () => {
        if (!hideCanvas) return; // already in Code — nothing to do
        if (goToCodeProjectId && goToCodeTenantId) {
            navigateToCodeWithFade(withConversationId(`/code/${goToCodeTenantId}/${goToCodeProjectId}`));
            return;
        }
        if (!goToCodeTenantId) {
            navigateToCodeWithFade(withConversationId('/code/'));
            return;
        }
        (async () => {
            // See the original goToMode's own comment (git history) for why
            // this short poll + chatHasActivity guard exist — unchanged.
            for (let attempt = 0; attempt < 10 && !chatBoundProjectIdRef.current; attempt++) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
            const boundProjectId = chatBoundProjectIdRef.current;
            if (boundProjectId) {
                navigateToCodeWithFade(withConversationId(`/code/${goToCodeTenantId}/${boundProjectId}`));
                return;
            }
            if (chatHasActivity) {
                showToast('Still setting up your project — try Code again in a moment', 'info');
                return;
            }
            try {
                const { projectId: newProjectId, name } = await createQuickVfsProject(goToCodeTenantId);
                miaFlowAuth.setSelectedProject({ projectId: newProjectId, name });
                navigate(withConversationId(`/code/${goToCodeTenantId}/${newProjectId}`), { replace: true });
            } catch (error) {
                console.error('Failed to create project for Code switch:', error);
                showToast('Failed to create a new project — pick an existing one instead', 'error');
                navigate('/projects');
            }
        })();
    };

    const openReadyProject = () => {
        // The app that was built: the service the preview came from, else the project's first real service (not the
        // project root). With neither, the project opens on its Dashboard.
        const registered = vfsClient.getVfsServiceNames(goToCodeProjectId).filter((name) => name && name !== '.');
        const hintedService = openProjectServiceName && openProjectServiceName !== '.' ? openProjectServiceName : null;
        const opened = openProject({
            project: { projectId: goToCodeProjectId, tenantId: goToCodeTenantId },
            navigate,
            conversationId,
            // The chat reports "." for the project root when the preview did not name a service: that is no service to open
            // (in a URL it vanishes and shifts "main" into the service slot), so the first real service is used, and "app" —
            // the default of the preview tool — when none is registered yet.
            serviceName: (hintedService && registered.includes(`services/${hintedService}`) ? `services/${hintedService}` : hintedService)
                || registered.find((name) => name.startsWith('services/')) || registered[0]
                || (openProjectServiceName === '.' ? 'app' : null),
            onInvalid: () => showToast('Could not open the project', 'error'),
        });
        if (opened) { setOpenProjectPromptDismissed(true); }
    };
    // A project file mentioned in the chat was clicked: open the project on its Files view with that file selected
    // (`?view=files&preview=yes&path=...`). From the plain chat that is a switch to Code; inside Code only the URL changes
    // and the open Files view follows it.
    const openFileInCode = (path) => {
        if (!goToCodeProjectId || !goToCodeTenantId) {
            showToast('This chat has no project yet — the file will be openable once it does', 'info');
            return;
        }
        const encodedPath = String(path).split('/').map(encodeURIComponent).join('/');
        const conversation = conversationId ? `conversationId=${encodeURIComponent(conversationId)}&` : '';
        const target = `/code/${goToCodeTenantId}/${goToCodeProjectId}?${conversation}view=files&preview=yes&path=${encodedPath}`;
        if (hideCanvas) {
            navigateToCodeWithFade(target);
        } else {
            navigate(target);
        }
    };
    const openProjectPromptVisible = hideCanvas && openProjectReady && !openProjectPromptDismissed && !!goToCodeProjectId;
    // Counts down once the agent has finished; at zero the project opens by itself. Pressing the button opens it at once,
    // and dismissing the prompt stops the countdown.
    const openProjectCountingDown = openProjectPromptVisible && openProjectTurnDone;
    const openProjectSeconds = useCountdown({ active: openProjectCountingDown, seconds: OPEN_PROJECT_COUNTDOWN_SECONDS, onDone: openReadyProject });

    return (
        <>
            {compact && !hideCanvas && (
                <CompactProjectBar onNavigate={navigate} onPaneChange={paneSwitch ? setCompactPane : null} pane={paneSwitch ? compactPane : 'project'} />
            )}
            <div
                // BUG FIX: this reused the "main-content" class name of the
                // real outer app shell (App.jsx's canvas-portal-root div,
                // the one App.css's catalog-style-shell rules — margin-left,
                // border-radius, box-shadow — target). Being a totally
                // different element (the chat column INSIDE Code/Canvas
                // mode, nested inside that real outer shell) it inherited
                // those rules too: a second, unwanted 12px margin-left
                // pushing this panel further right than the resize handle/
                // Canvas (both positioned against the OUTER .main-content's
                // own left edge) expected, plus rounded corners/shadow it
                // was never meant to have — the real cause of the reported
                // chat/preview visual overlap. Renamed so it only ever
                // matches its own dedicated rules (see App.css).
                className={`canvas-mode-chat-panel canvas-active ${isFullscreen ? 'fullscreen-mode' : ''}`}
                style={{
                    width: hideCanvas || compact ? '100%' : (isFullscreen ? '100vw' : `${chatSidebarWidth}px`),
                    zIndex: isFullscreen ? 9999999999 : 'auto',
                    ...(chatHidden ? { display: 'none' } : {}),
                }}
            >
                {/* Toast Notification */}
                {toast && (
                    <Toast
                        message={toast.message}
                        onClose={hideToast}
                        type={toast.type}
                    />
                )}

                {/* URL Validation Error - Missing Tenant */}
                {urlValidationError && urlValidationError.type === 'missing_tenant' && (
                    <div className="chat-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <div style={{
                        maxWidth: '600px',
                        padding: '40px',
                        textAlign: 'center',
                        background: 'var(--bg-secondary)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            margin: '0 auto 24px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px'
                        }}>
                            ⚠️
                        </div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: 'var(--text-primary)'
                        }}>
                            Tenant Required
                        </h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--text-secondary)',
                            marginBottom: '32px',
                            lineHeight: '1.6'
                        }}>
                            {urlValidationError.message}<br/>
                            Please contact your administrator to be added to a tenant.
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            style={{
                                padding: '12px 32px',
                                fontSize: '16px',
                                fontWeight: '500',
                                color: 'white',
                                background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                            }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            {urlValidationError.action}
                        </button>
                    </div>
                </div>
            )}

            {/* Main Chat Content - Always show except for critical tenant error.
                For a project-bound door (!hideCanvas, i.e. /code) the composer
                must not mount until canvasProjectContext.projectId is real —
                mounting VfsChatPanel earlier sends its vfs_* tool calls (and
                the x-mia-vfs-project header) with a literally-undefined
                project id, which vfs-service then rejects deep inside a tool
                call ("Unknown project: undefined") instead of surfacing a
                clear "still setting up" state. The plain /chat door
                (hideCanvas=true) is never project-bound, so it renders
                immediately as before. */}
            {(!urlValidationError || urlValidationError.type !== 'missing_tenant') && (
                (hideCanvas || canvasProjectContext?.projectId) && !chatHidden ? (
                    <VfsChatPanel
                        // Force a full remount whenever the bound project
                        // actually changes (hideCanvas's plain /chat door has
                        // no project — 'no-project' there is intentionally
                        // stable, matching that route's own project-less
                        // session id). Without this, React reuses the SAME
                        // component instance (and the vendored ChatWidget's
                        // OWN internal session/conversation state) across a
                        // project switch — its `initialSessionId` prop is
                        // exactly that, an "initial" value the widget reads
                        // once at mount, never reactively. Live-confirmed
                        // regression: navigating Home -> a genuine new build
                        // prompt correctly created a brand-new project (URL
                        // updated) while the chat panel kept showing an
                        // entirely unrelated OLD conversation from a much
                        // earlier project — the widget never remounted, so
                        // it never re-read the new project's session id.
                        // Task #157 originally keyed on project+service too, for the
                        // reason described above (switching services used to leave
                        // the widget on the OLD service's stale `initialSessionId`).
                        // Task #211 REVERSES that: explicit product correction —
                        // "the session is related to the project not to a single
                        // service, dont change it" — and it turned out to also be a
                        // real, live-confirmed concurrency bug, not just a UX
                        // quirk: including the service name here meant simply
                        // changing the Preview panel's service dropdown (Canvas.jsx)
                        // remounted this ENTIRE chat widget mid-conversation. If the
                        // current turn's agent execution was still actively running
                        // (a detached task, see adk-be-app's detached_execution.py)
                        // when that remount fired, the widget re-attached/re-created
                        // a session while the old execution was still appending
                        // events to it — live-confirmed via adk-be-app logs: two
                        // `session_attach: client attaching to active run` calls ~7s
                        // apart for the same session_id, immediately followed by
                        // `detached_execution: agent execution raised` and
                        // `AGENT_ERROR: The session has been modified in storage
                        // since it was loaded`, losing tool-call results and failing
                        // the turn. VfsChatPanel.jsx's own `initialSessionId` no
                        // longer embeds the service name either (fixed alongside
                        // this) — a project now has exactly one chat session,
                        // period, regardless of which service is selected for
                        // preview.
                        //
                        // Also keys on the URL's conversationId (task #227):
                        // a client-side Chat -> Code switch for an ALREADY-
                        // bound project (canvasProjectContext.projectId ===
                        // whatever the plain /chat door had already bound)
                        // used to leave this key UNCHANGED across that
                        // navigation, so the widget kept its Chat-door
                        // mount's OWN initialSessionId instead of ever
                        // reading the real conversationId now in the /code
                        // URL — live-confirmed: Code mode rendered the
                        // correct project/preview (those come from
                        // canvasProjectContext, not this widget) but an
                        // EMPTY chat transcript, while a hard refresh of the
                        // exact same URL loaded the real transcript fine
                        // (a fresh mount reads the URL's conversationId
                        // correctly the first time — this key trick is what
                        // gets a WARM instance to do the same on subsequent
                        // navigations). Folding the URL's own conversationId
                        // into the key forces the same fresh-mount behavior
                        // a refresh already gets, without weakening the
                        // project-change or service-change guards above.
                        key={hideCanvas ? 'no-project' : `${canvasProjectContext?.projectId}::${new URLSearchParams(location.search).get('conversationId') || ''}`}
                        vfsProjectId={canvasProjectContext?.projectId}
                        // Only a REAL service's name may become the agent-facing
                        // `x-mia-vfs-service` header (see VfsChatPanel.jsx). Canvas's
                        // `selectedMicroservice` also covers its synthetic
                        // "Project Configuration" pseudo-resource (type: 'configuration'
                        // — a display-only dashboard fallback, not a real vfs path); that
                        // literal name used to flow straight through here into the
                        // header, and the agent's own tools then tried to treat
                        // "Project Configuration" as a real vfs-service path
                        // ("Path does not exist: Project Configuration/..."). Task #144.
                        // NOTE: `type` here is the raw service object's OWN `type` field
                        // (Console's microservice `type`, or 'custom' for a vfs-only
                        // service — see Canvas.jsx's loadConfiguration/formatMicroservice),
                        // NOT the outer selectedResource wrapper's `type` ('service' vs
                        // 'configuration'). Comparing it to the literal string 'service'
                        // excluded every vfs-backed service (always 'custom') along with
                        // the real pseudo-resource this comment describes — so the header
                        // silently never reached the agent for ANY vfs-backed project.
                        // The actual thing being excluded is the 'configuration'
                        // pseudo-resource, so exclude exactly that instead.
                        // Fallback for the brand-new-project race: right after Home
                        // creates a project, this chat panel mounts and the FIRST
                        // message can be auto-sent before canvasProjectContext's own
                        // async load (loadConfiguration -> selectedMicroservice) has
                        // resolved — leaving vfsServiceName undefined for that one
                        // turn, so the agent's plain-path writes land at the vfs
                        // project ROOT instead of `services/app/` (live-confirmed:
                        // index.html/style.css/script.js written next to .mia/,
                        // no services/ folder at all). quickProjectCreation.js
                        // already registers 'services/app' as a synchronous
                        // localStorage pointer at creation time specifically so a
                        // real value is available before that async load finishes —
                        // use it whenever selectedMicroservice hasn't resolved yet.
                        vfsServiceName={canvasProjectContext?.selectedMicroservice && canvasProjectContext.selectedMicroservice.type !== 'configuration' ? canvasProjectContext.selectedMicroservice.name : vfsClient.getVfsServiceNames(canvasProjectContext?.projectId)[0]}
                        // `pendingChatMessage` defaults to `null` (no pending message), but
                        // the vendored ChatWidget's own mount effect calls .trim() on this
                        // prop unconditionally with no null guard — passing null crashes it
                        // with "Cannot read properties of null (reading 'trim')" on every
                        // mount that has no pending message (i.e. almost always). `undefined`
                        // is the correct "not provided" value for its PropTypes.string (not
                        // .isRequired) contract; this doesn't touch the vendored package.
                        initialMessage={pendingChatMessage || undefined}
                        pageContext={pendingAskAIPageContext || workspacePageContext}
                        pendingMessage={pendingAskAIMessage}
                        pendingDraft={pendingAskAIDraft}
                        tenantId={canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null}
                        onNewConversation={onNewConversation}
                        onNewChatInProject={handleNewChatInProject}
                        onFreshConversationStarted={handleFreshConversationStarted}
                        // task #81 — lets VfsChatPanel jump the user straight to
                        // Canvas's Preview tab once the agent deliberately calls
                        // vfs_get_preview_url and gets back a real, ready URL.
                        // Canvas (portal'd below, same canvasRef used by the
                        // existing "Force Refresh" control) exposes
                        // showExternalPreview via useImperativeHandle for exactly
                        // this — see its own comment for why the normal
                        // console-microservice-keyed preview flow doesn't cover
                        // a brand-new vfs-only project. A no-op when Canvas isn't
                        // mounted (the plain /chat door, hideCanvas=true).
                        onShowExternalPreview={(serviceName, url) => {
                            // Chat mode (hideCanvas): no embedded preview
                            // here at all any more — the first real preview
                            // is the signal to switch straight to Code
                            // instead (goToCode, defined above this render),
                            // where Canvas resolves the same preview through
                            // its own normal flow. Code mode: unchanged,
                            // still jumps Canvas's own Preview tab directly.
                            if (hideCanvas) {
                                // Offer the switch instead of forcing it: a button, "Open the project", shown below.
                                if (serviceName) { setOpenProjectServiceName(serviceName); }
                                setOpenProjectReady(true);
                                return;
                            }
                            canvasRef.current?.showExternalPreview?.({ serviceName, url });
                        }}
                        // Synchronous clear, in the SAME tick as VfsChatPanel's
                        // own navigate() call for an agent-initiated
                        // vfs_create_project redirect — see that call site's
                        // comment for why the effect-based clear (watching
                        // canvasProjectContext.projectId) was too late: it's a
                        // passive effect that fires after commit, but the
                        // remount the redirect causes reads `initialMessage`
                        // synchronously during that same render. Live-confirmed
                        // without this: a single vfs_create_project call could
                        // still cascade into 6+ chained project creations a few
                        // seconds apart, same failure mode as the original bug.
                        onProjectRedirect={() => setPendingChatMessage(null)}
                        // Task #164 fix: fires the instant VfsChatPanel learns
                        // a real vfs project id for THIS conversation (agent
                        // called vfs_create_project), synchronously — not
                        // gated on the turn completing like its own redirect.
                        // Lets goToMode below fall back to the right project
                        // if the user switches to Code mid-turn.
                        onProjectBound={(id) => {
                            chatBoundProjectIdRef.current = id;
                            setChatBoundProjectId(id);
                        }}
                        // See chatHasActivity's own doc above — disables the
                        // Code option below while a project binding is
                        // imminent but not there yet, instead of racing a
                        // fallback against however long that takes.
                        onActivityStart={() => setChatHasActivity(true)}
                        onTurnComplete={() => setOpenProjectTurnDone(true)}
                        onOpenFile={openFileInCode}
                        onCodeReady={() => {
                            setChatCodeReady(true);
                            // Whatever the agent built (app, slides, document, artifact) now exists as a project: offer
                            // the switch, not only when a live preview URL appeared.
                            if (hideCanvas) { setOpenProjectReady(true); }
                            // See correctConversationIdInUrl's own BUG FIX
                            // comment — this is the second, later-firing
                            // check that catches project.json's
                            // conversationId once it's actually been
                            // written, after the effect's one early check
                            // may have run too soon to see it.
                            correctConversationIdInUrl(chatBoundProjectId);
                        }}
                        // No embedded preview exists in Chat mode any more —
                        // always true there so the floating "Open Preview"
                        // fallback button never shows either (nothing
                        // preview-related renders in Chat, matching the
                        // auto-switch-to-Code behavior in onShowExternalPreview
                        // above). Always false in Code mode (hideCanvas is
                        // false there), same as before this change — Code's
                        // own floating-button behavior is unaffected.
                        previewEmbedded={hideCanvas}
                        hideCanvas={hideCanvas}
                        // Chat/Code toggle removed (see goToCode's own doc
                        // above this render), and — explicit product
                        // correction — no manual button either: showing
                        // "Code" before any code exists (a brand-new,
                        // empty conversation) doesn't make sense. The ONLY
                        // way into Code from here is automatic, the instant
                        // the agent's first vfs_get_preview_url call
                        // succeeds (onShowExternalPreview above, calling
                        // this same goToCode). Code mode's own embedded
                        // chat panel gets no header control here either
                        // (FlowSidebar's separate "Chat" nav icon is the
                        // way back, and it deliberately starts a fresh
                        // conversation rather than resuming this one — see
                        // NavigationSidebar.jsx).
                        headerActions={null}
                    />
                ) : (
                    !showProjectSelector && <div className="chat-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                            <p>Setting up your project…</p>
                        </div>
                    </div>
                )
            )}

            {/* AI Instructions Modal */}
            <AIInstructionsModal
                isOpen={isAIInstructionsModalOpen}
                onClose={() => setIsAIInstructionsModalOpen(false)}
                onApply={handleApplyAIInstructions}
                currentInstructions={customAIInstructions}
                currentPlaybookData={customAIInstructionsPlaybookData}
                tenantId={canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null}
            />

            {/* Save Prompt Modal */}
            <SavePromptModal
                isOpen={isSavePromptModalOpen}
                onClose={() => {
                    setIsSavePromptModalOpen(false);
                    // Don't clear inputValue here as it might be the current input
                }}
                onSave={handleSavePrompt}
                promptText={inputValue}
                tenantId={canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null}
            />

            {/* Save Message Modal */}
            <SaveMessageModal
                isOpen={isSaveMessageModalOpen}
                onClose={() => {
                    setIsSaveMessageModalOpen(false);
                    setMessageToSave('');
                }}
                onSave={handleSaveMessage}
                messageText={messageToSave}
                tenantId={canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null}
                projectId={canvasProjectContext?.projectId || null}
                serviceName={canvasProjectContext?.selectedMicroservice?.name || null}
            />

            {/* Script Execution Confirmation Modal */}
            <Modal
                isOpen={scriptExecutionModal.isOpen}
                onClose={scriptExecutionModal.onCancel || (() => setScriptExecutionModal({ isOpen: false }))}
                title={scriptExecutionModal.title}
                message={scriptExecutionModal.message}
                type={scriptExecutionModal.type || 'confirm'}
                confirmText={scriptExecutionModal.confirmText}
                cancelText={scriptExecutionModal.cancelText}
                onConfirm={scriptExecutionModal.onConfirm}
                onCancel={scriptExecutionModal.onCancel}
                showCancel={scriptExecutionModal.showCancel}
                isDangerous={scriptExecutionModal.isDangerous}
                isLoading={scriptExecutionModal.isLoading}
            >
                {scriptExecutionModal.children}
            </Modal>

            {/* Resize Handle */}
            {!hideCanvas && !chatHidden && !compact && <div
                ref={resizeHandleRef}
                className="chat-resize-handle"
                style={{ left: `${chatSidebarWidth}px` }}
                onMouseDown={(e) => {
                    e.preventDefault();
                    resizeHandleRef.current.dataset.dragging = 'true';
                    document.body.style.cursor = 'ew-resize';
                    document.body.style.userSelect = 'none';
                    // See ChatColumn's identical handler for the full
                    // explanation — this handle sits right next to
                    // Canvas's own preview iframe, so dragging right
                    // (enlarging) crosses into it and silently kills
                    // mousemove tracking without this.
                    document.body.classList.add('resizing-chat-sidebar');
                }}
            />}

            {/* Project Selector - When projectId is missing. Full-viewport rather than
                offset by chatSidebarWidth: that offset is a JS state value that can drift
                from the chat panel's real rendered width, which used to make this overlay
                start mid-panel and visually collide with the chat transcript behind it.
                There's nothing useful to show in either pane without a project anyway, so
                a simple full-screen modal (same idiom as Modal/TenantSwitchOverlay) removes
                the whole class of bug instead of chasing the width math. */}
            {/* task #126: the "Select a Project" full-page interstitial (mode="select")
                was removed per explicit user instruction — its trigger (useCanvasProjectContext's
                createOnTheFly catch block) now redirects to /projects instead of setting
                showProjectSelector true. showProjectSelector/setShowProjectSelector are kept
                (always false now) rather than threading their removal through every consumer. */}

            {/* A project from the URL that could not be opened: not found / not shared (404, 403), or the sandbox did not answer */}
            {!hideCanvas && urlValidationError && urlValidationError.type !== 'missing_tenant' && canvasPortalTarget && createPortal(
                <ProjectOpenError error={urlValidationError} />,
                canvasPortalTarget
            )}


            {/* Compose → Code picker – full page, replaces chat area */}

            {/* Canvas Component - Show on right side when project is selected.
                Portal'd to document.body: Canvas relies on `position: absolute`
                resolving `left`/`width: calc(100% - chatSidebarWidth)` against a
                FULL-WIDTH containing block (see Canvas.css's own comment on
                .canvas-container) — but this div is scoped to the chat column
                itself (width: chatSidebarWidth), so nesting Canvas inside it
                made that containing block only chatSidebarWidth wide, computing
                Canvas's own width as ~0 (a genuinely invisible panel, not a
                loading state). Same fix ProjectPickerModal already uses, for
                the exact same reason (see its own comment a few lines up). */}
            {!hideCanvas && !urlValidationError && !showProjectSelector && canvasPortalTarget && createPortal(
                <Canvas
                    content={canvasContent}
                    isVisible={true}
                    chatSidebarWidth={chatHidden || compact ? 0 : chatSidebarWidth}
                    projectContext={canvasProjectContext}
                    onNavigateHome={() => navigate('/')}
                    onClose={() => {
                        // Canvas is always on, but we can navigate home on close
                        navigate('/');
                    }}
                   onProjectContextChange={handleProjectContextChange}
                   ref={canvasRef}
                   initialMicroservice={params.microservice ? decodeServiceNameFromUrl(params.microservice) : params.microservice}
                   initialBranch={params.branch}
                   initialTab={params.tab}
                   onSelectionChange={syncUrlWithCanvasSelections}
                   onNewConversation={onNewConversation}
                   onShowHistory={() => navigate('/history')}
                   onAskAI={askAIInProjectChat}
                />,
                canvasPortalTarget
            )}

            {/* Mobile Canvas Drawer - Provides mobile access to Canvas features */}
            <MobileCanvasDrawer
                isOpen={isMobileCanvasDrawerOpen}
                onClose={() => setIsMobileCanvasDrawerOpen(false)}
            />

            {/* Mobile Code Page - Full-screen mobile coding experience */}
            <MobileCodePage
                isOpen={isMobileCodePageOpen}
                onClose={() => setIsMobileCodePageOpen(false)}
                onAskAI={(prompt) => {
                    setInputValue(prompt);
                    setIsMobileCodePageOpen(false);
                    // Focus on input after a short delay
                    setTimeout(() => {
                        const input = document.querySelector('.chat-input');
                        if (input) {
                            input.focus();
                        }
                    }, 300);
                }}
            />

            {/* Mobile Preview Page - Full-screen mobile preview experience */}
            <MobilePreviewPage
                isOpen={isMobilePreviewPageOpen}
                onClose={() => setIsMobilePreviewPageOpen(false)}
                onAskAI={(prompt) => {
                    setInputValue(prompt);
                    setIsMobilePreviewPageOpen(false);
                    // Focus on input after a short delay
                    setTimeout(() => {
                        const input = document.querySelector('.chat-input');
                        if (input) {
                            input.focus();
                        }
                    }, 300);
                }}
            />


            </div>
            {openProjectPromptVisible && createPortal(
                <div className="open-project-prompt" role="status">
                    <span className="open-project-prompt__text">
                        {openProjectCountingDown
                            ? `Your project is ready — opening in ${openProjectSeconds}s`
                            : 'Your project is ready'}
                    </span>
                    <button
                        type="button"
                        className="open-project-prompt__open"
                        onClick={() => openReadyProject()}
                    >
                        Open the project
                    </button>
                    <button
                        type="button"
                        className="open-project-prompt__close"
                        aria-label={openProjectCountingDown ? 'Stay here' : 'Dismiss'}
                        title={openProjectCountingDown ? 'Stay here' : 'Dismiss'}
                        onClick={() => setOpenProjectPromptDismissed(true)}
                    >
                        ×
                    </button>
                </div>,
                document.body
            )}
        </>
    );
}

// Main App component with routing
function App() {
    const location = useLocation();
    const navigate = useNavigate();

    // Determine if animations should be disabled (on pages other than login and home)
    const shouldDisableAnimations = useMemo(() => {
        const path = location.pathname;
        // Enable animations only on login, register, and home pages
        return path !== '/login' && path !== '/register' && path !== '/';
    }, [location.pathname]);

    // Smooths the Chat→Code transition (live-reported: felt like a hard page
    // reload) — the two live under different routes matched by the SAME `/*`
    // catch-all below, so the route swap unmounts VfsChatPanel's whole tree
    // and mounts Canvas's whole tree with nothing in between, an abrupt flash
    // rather than a page anyone would call smooth. `goToCode()` (App.jsx,
    // inside MainChat — a CHILD of the `/*` route that itself unmounts on
    // this exact transition, so it can't own this state itself) dispatches
    // `flow-navigate-with-fade` instead of calling navigate() directly; this
    // component is the nearest ancestor that survives every internal route
    // change, so it's the one place a covering fade can actually persist
    // across the swap. A plain custom window event (mirroring the existing
    // `show-toast` event elsewhere in this file) rather than Context: the
    // caller and this listener are already this decoupled in practice (a
    // child many routes deep asking its way-up ancestor to navigate), and a
    // Context provider here would need to wrap the same broad tree this
    // event already reaches for free.
    const [routeFadeActive, setRouteFadeActive] = useState(false);
    useEffect(() => {
        const handleNavigateWithFade = (event) => {
            const { path, opts } = event.detail || {};
            if (!path) return;
            setRouteFadeActive(true);
            setTimeout(() => {
                navigate(path, opts);
                // Held a moment after navigating too, so the destination
                // route's own first paint (data still loading, layout
                // settling) is hidden behind the fade instead of flashing
                // through it — same reasoning as Home's own fade-out delay.
                setTimeout(() => setRouteFadeActive(false), 330);
            }, 300);
        };
        window.addEventListener('flow-navigate-with-fade', handleNavigateWithFade);
        return () => window.removeEventListener('flow-navigate-with-fade', handleNavigateWithFade);
    }, [navigate]);

    return (
        <div className={shouldDisableAnimations ? 'disable-animations' : ''}>
            {/* Side-effect component: redirects to /bff/login when no active session */}
            <RedirectToLogin />
            {/* A session that expires while the page is open: said once, then sign in again (utils/sessionExpiry.js) */}
            <SessionExpiredNotice />
            {/* role="status": exempts this from the `.disable-animations`
                kill-switch above (App.css) — the same escape hatch the vfs
                chat sending-overlay uses (VfsChatPanel.jsx), needed for the
                same reason: this transition itself is the whole point on
                every route this fires on, not a decorative extra. */}
            <div className={`flow-route-transition-overlay${routeFadeActive ? ' active' : ''}`} role="status">
                <span className="flow-route-transition-label">Switching to code mode</span>
            </div>
            <Routes>
                {/* Public Routes - accessible without authentication */}
                <Route element={<Login />} path="/login" />
                <Route element={<Register />} path="/register" />
                <Route element={<AuthCallback />} path="/auth/callback" />
                <Route element={<AuthCallback />} path="/auth/github/callback" />
                <Route element={<AuthCallback />} path="/auth/mia-platform/callback" />

                {/* Browser Stream Viewer Standalone - can be opened in separate window */}
                <Route element={<BrowserStreamViewerStandalone />} path="/browser-stream-viewer" />

                {/* Collaboration invite route */}
                <Route element={<JoinCollaboration />} path="/join/:token" />

                {/* Protected Routes - require authentication */}
                <Route element={<MainApp />} path="/*" />
            </Routes>
        </div>
    );
}

// App wrapper with Sidebar - Protected content only
/** Phone only (< 767px, CSS): the global search's magnifier, next to the hamburger. */
function MobileSearchButton() {
    const { openSearch } = useSearch();
    return (
        <button aria-label="Search everything" className="flow-search-mobile-toggle" onClick={() => openSearch()} type="button">
            <MagnifyingGlassIcon size={18} />
        </button>
    );
}

function MainApp() {
    const {isAuthenticated, isLoading, user, authorizedFetch} = useAuth();
    // Selected authz organization (populates x-mia-acl-context) — the catalog scope.
    const {selectedOrganization} = useSelectedOrganization();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    // Sandbox-first: wake the person's sandbox as soon as Flow opens, so it is up by the time a page needs it (an asleep
    // sandbox takes a few seconds). The overview request itself wakes it; its answer is not needed here.
    useEffect(() => {
        if (config.sandboxFirst && isAuthenticated) { getSandbox().catch(() => {}); }
    }, [isAuthenticated]);

    // A sandbox put to sleep stays asleep on Home and Settings; a page that uses it ends that (utils/sandboxSleep.js).
    useEffect(() => { noteSandboxPage(location.pathname); }, [location.pathname]);

    // The live notifications (bell, banner, Activity badge; utils/livePoll.js) ask only while someone is signed in.
    useEffect(() => {
        setLivePollSignedIn(isAuthenticated);
    }, [isAuthenticated]);

    // Global toast, mounted for MainApp's entire lifetime (every route,
    // Home included) — every call site below dispatches the 'show-toast'
    // window CustomEvent, but until now NOTHING anywhere in this app
    // actually listened for it. MainChat (the Chat/Code canvas view) has
    // its own SEPARATE local toast/showToast/<Toast> for its own direct
    // showToast(...) calls, correctly scoped to when it's mounted — but a
    // failure from Home's own build-prompt handler below (e.g. project
    // creation racing ahead of tenant resolution) fires while Home, not
    // MainChat, is on screen, so that local toast was never in the tree to
    // show anything. Live-confirmed: the console.error always fired
    // correctly, but nothing ever appeared, leaving a typed prompt just
    // sitting there unsent with zero explanation. This is the fix: one
    // instance, always mounted, catches every dispatch regardless of route.
    const [globalToast, setGlobalToast] = useState(null);
    useEffect(() => {
        const handleShowToast = (e) => {
            const { message, type } = e.detail || {};
            if (message) setGlobalToast({ message, type: type || 'error' });
            // Only that an error was shown, never what it said.
            if (message && (type || 'error') === 'error') { friction('toast', true); }
        };
        window.addEventListener('show-toast', handleShowToast);
        return () => window.removeEventListener('show-toast', handleShowToast);
    }, []);

    // Memoize searchParams to prevent unnecessary re-renders
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const conversationId = searchParams.get('conversationId');

    // Extract tenant ID from URL if present (e.g., /b933f1ef-5b8e-4adf-a346-24a3b03d13e8)
    const tenantIdFromUrl = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        // Check if first segment looks like a UUID (tenant ID)
        if (pathSegments.length > 0) {
            const firstSegment = pathSegments[0];
            // UUID regex pattern
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            // Known routes that should not be treated as tenant IDs
            const knownRoutes = ['chat', 'compose', 'playbook', 'projects', 'catalog', 'templates', 'learn', 'discover', 'tools', 'auth', 'join', 'browser-stream-viewer', 'memories'];
            if (uuidPattern.test(firstSegment) && !knownRoutes.includes(firstSegment)) {
                return firstSegment;
            }
        }
        return null;
    }, [location.pathname]);


    // Navigation sidebar collapse state — the sidebar is always rendered (see
    // shouldShowNavSidebar below); collapsing to an icon rail is as far as it
    // ever goes, so there's always a way back to home / another project.
    const [isNavSidebarCollapsed, setIsNavSidebarCollapsed] = useState(() => {
        // Load nav sidebar collapsed state from localStorage, default to false
        return localStorage.getItem('navSidebarCollapsed') === 'true';
    });
    // Tracks whether the CURRENT collapsed state was set automatically by the
    // "entering /code" effect below (as opposed to the user's own toggle) —
    // see that effect's comment for why this exists.
    const autoCollapsedByCodeViewRef = useRef(false);

    // Mobile nav drawer: below Mobile.css's own 767px breakpoint, FlowSidebar
    // no longer stays visible as an inline icon rail (that was the deliberate
    // "no mobile nav affordance" call from the sidebar-widget migration, see
    // FlowSidebar.jsx's comment) — it's off-canvas by default, toggled by a
    // hamburger button (CSS-hidden above 767px, see App.css) and closed via
    // its own backdrop, a nav click, or any route change below.
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    // Tenant management state (src/hooks/useTenantSelection.js)
    const {
        tenants,
        selectedTenant,
        isTenantSwitching,
        setIsTenantSwitching,
        switchingToTenantName,
        selectTenantSilently,
    } = useTenantSelection({isAuthenticated, isLoading, tenantIdFromUrl});

    // BUG FIX (task #140): this used to hold a pending auto-selected-playbook
    // confirmation (task #26's "Continue with X / Just chat instead / Choose
    // a different playbook" toast) that paused routing for up to 5s awaiting
    // user input. Per explicit instruction ("autoselect from home must be
    // skipped, just select silently and go") — and since task #138 already
    // proved the backend's selectPlaybook classification is reliable (a real
    // confidence-scored match vs. a trustworthy `selected: null` for
    // non-build queries) — that confirmation step is gone; a real match now
    // proceeds immediately via proceedWithSelected(), no popup, no timer.
    // Left as dead state removal marker only if something below still
    // references it (nothing should — see handleStartChatFromHome).

    // In-app browser overlay (task #26): lets external links opened from
    // assistant chat messages render inside Flow (an iframe overlay with a
    // back button) instead of jumping to a brand new browser tab. Holds
    // { isOpen, history: string[], currentIndex } or null when closed.
    // Any part of the app can open it without prop-drilling by dispatching
    // window.dispatchEvent(new CustomEvent('open-in-app-browser', {detail: {url}})),
    // mirroring the existing 'show-toast' CustomEvent pattern used elsewhere
    // in this file.
    const [inAppBrowser, setInAppBrowser] = useState(null);
    useEffect(() => {
        const handleOpenInAppBrowser = (event) => {
            const url = event.detail?.url;
            if (!url) {
                return;
            }
            setInAppBrowser((prev) => {
                if (prev?.isOpen) {
                    // Opening another link while the overlay is already open:
                    // push it onto the history (dropping any entries past the
                    // current one) so "back" can return to the previous page.
                    const newHistory = [...prev.history.slice(0, prev.currentIndex + 1), url];
                    return {isOpen: true, history: newHistory, currentIndex: newHistory.length - 1};
                }
                return {isOpen: true, history: [url], currentIndex: 0};
            });
        };
        window.addEventListener('open-in-app-browser', handleOpenInAppBrowser);
        return () => window.removeEventListener('open-in-app-browser', handleOpenInAppBrowser);
    }, []);

    // Playbooks state — sidebar quick-launch items
    const [sidebarPlaybooks, setSidebarPlaybooks] = useState([]);

    // Git connection state — true if any git provider is connected
    const [hasGitConnection, setHasGitConnection] = useState(false);

    // Project creation modal state
    const [isMobilePreviewPageOpen, setIsMobilePreviewPageOpen] = useState(false);
    const [mobileActiveView, setMobileActiveView] = useState('chat');

    // Auto-collapse the sidebar to its icon rail on entering the code view, to
    // give the canvas room by default — a one-time default, not a lock: after
    // this, the user's own expand/collapse toggle (handleToggleNavSidebarCollapse)
    // is respected normally, including while still in the code view.
    //
    // BUG FIX (visual regression, reported via screenshot on /chat): this
    // auto-collapse used to be one-directional only — it collapsed the
    // sidebar to 72px on entering /code but never re-expanded it, so a
    // session that ever visited /code (e.g. a build, or a playbook launch)
    // and then navigated to /chat (e.g. via the nav's own Chat icon, or
    // History reopening a conversation) kept the icon-rail sidebar for the
    // rest of the session. /chat has no canvas to make room for, so a
    // leftover 72px rail there just reads as "the sidebar disappeared" next
    // to a fresh session's normal 260px width — matches the reported
    // screenshot's header ("New conversation September 10, 2026 | Code
    // Generation Playbook"), consistent with a conversation reached from a
    // /code playbook session (not live-verified — traced from this effect's
    // code, see the diagnosis in this task's report). `autoCollapsedByCodeViewRef` tracks whether the
    // CURRENT collapsed state was set by this effect (vs by the user's own
    // toggle, which must still be respected exactly as before — the comment
    // above's "not a lock" guarantee) so leaving /code can safely undo only
    // its own automatic collapse.
    useEffect(() => {
        if (location.pathname.startsWith('/code')) {
            setIsNavSidebarCollapsed(true);
            autoCollapsedByCodeViewRef.current = true;
        } else if (autoCollapsedByCodeViewRef.current) {
            setIsNavSidebarCollapsed(false);
            autoCollapsedByCodeViewRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname.startsWith('/code')]);

    // Check git provider connection on mount
    useEffect(() => {
        if (!isAuthenticated || isLoading) return;
        Promise.all([
            GitLabAuth.isAuthenticated(),
            GitHubAuth.isAuthenticated(),
            BitbucketAuth.isAuthenticated(),
            AzureDevOpsAuth.isAuthenticated(),
        ])
            .then((results) => setHasGitConnection(results.some(Boolean)))
            .catch(() => setHasGitConnection(false));
    }, [isAuthenticated, isLoading]);

    // Re-check git connection whenever the Connectors page reports a status change
    useEffect(() => {
        if (!isAuthenticated || isLoading) return;
        const handleGitConnectionChanged = () => {
            Promise.all([
                GitLabAuth.isAuthenticated(),
                GitHubAuth.isAuthenticated(),
                BitbucketAuth.isAuthenticated(),
                AzureDevOpsAuth.isAuthenticated(),
            ])
                .then((results) => setHasGitConnection(results.some(Boolean)))
                .catch(() => {});
        };
        window.addEventListener('gitConnectionChanged', handleGitConnectionChanged);
        return () => window.removeEventListener('gitConnectionChanged', handleGitConnectionChanged);
    }, [isAuthenticated, isLoading]);

    // NOTE: the one-shot "seed IndexedDB from vfs-service if empty" effect that
    // used to live here has been superseded by Canvas.jsx's persistent vfs-service
    // tree-sync connection (see src/utils/vfsSync.js), which performs a full
    // reconciliation (not just a seed-when-empty) every time it connects —
    // including the very first connect for this project/service — so a fresh
    // tab or one reopened after the browser was closed is covered without a
    // separate, possibly-racing fetch here.

    // Load playbooks for sidebar whenever the selected authz org changes.
    // Scope comes from x-mia-acl-context (via getBffHeaders), not a tenantId query param.
    useEffect(() => {
        if (!isAuthenticated) return;
        const baseUrl = (typeof config !== 'undefined' && config.bffUrl) || window.location.origin;
        const qs = new URLSearchParams({ page: '1', perPage: '100' });
        fetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, { headers: getBffHeaders() })
            .then(r => r.json())
            .then(result => {
                if (result.success && result.data) setSidebarPlaybooks(result.data.items || []);
            })
            .catch(() => {});
    }, [isAuthenticated, selectedOrganization?.tenant]);

    // Refresh sidebar playbooks when the Playbook catalog signals a change
    useEffect(() => {
        const refresh = () => {
            const baseUrl = (typeof config !== 'undefined' && config.bffUrl) || window.location.origin;
            const qs = new URLSearchParams({ page: '1', perPage: '100' });
            fetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, { headers: getBffHeaders() })
                .then(r => r.json())
                .then(result => { if (result.success && result.data) setSidebarPlaybooks(result.data.items || []); })
                .catch(() => {});
        };
        window.addEventListener('mia-flow-playbooks-changed', refresh);
        return () => window.removeEventListener('mia-flow-playbooks-changed', refresh);
    }, []);

    // Handler for toggling navigation sidebar collapse
    const handleToggleNavSidebarCollapse = useCallback(() => {
        // A deliberate user action always wins — clear the "this was an
        // automatic /code collapse" flag so the leave-/code effect above
        // never overrides what the user just chose.
        autoCollapsedByCodeViewRef.current = false;
        setIsNavSidebarCollapsed(prev => {
            const newValue = !prev;
            localStorage.setItem('navSidebarCollapsed', String(newValue));
            return newValue;
        });
    }, []);

    // Handle OAuth callback at root path (when redirect_uri is base URL)
    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        // Only process if we have OAuth callback parameters
        if (code && state) {
            // eslint-disable-next-line no-console
            devLog('🔄 [FE] OAuth callback detected at root path:', {
                code: code.substring(0, 20) + '...',
                state,
                full_search: location.search,
                will_redirect_to: `/auth/mia-platform/callback${location.search}`
            });
            // Navigate to dedicated callback handler WITH query parameters
            navigate(`/auth/mia-platform/callback${location.search}`, {replace: true});
        } else if (error) {
            // eslint-disable-next-line no-console
            console.error('❌ [FE] OAuth error received:', error);
            // Still redirect to callback handler to show error
            navigate(`/auth/mia-platform/callback${location.search}`, {replace: true});
        }
    }, [searchParams, navigate, location.search]);

    // Home page handlers
    // Smooths the Home→Chat handoff (live-reported: the instant cut from
    // Home's hero straight to the chat page felt abrupt) — ONLY for this
    // automatic, omnibar-triggered navigation, not every navigate() in the
    // app. `navigate()` unmounts Home the instant it's called, so a real
    // fade needs the unmount itself delayed slightly: this flips
    // `homeFadingOut` (passed to Home as `fadeOut`, drives a CSS opacity
    // transition — Home.css) and waits for that transition to actually play
    // before calling the real navigate(). Reset back to false right after
    // navigating (not on arrival at '/', which would fire before the fade
    // even starts and cancel it) so a later, unrelated visit to Home always
    // starts unfaded.
    const [homeFadingOut, setHomeFadingOut] = useState(false);
    const navigateWithHomeFade = useCallback((path, opts) => {
        setHomeFadingOut(true);
        // Matches Home.css's own .home-container transition duration (320ms)
        // plus a small buffer — the original 220ms window was shorter than
        // the fade itself, live-reported as no visible fade at all.
        setTimeout(() => {
            navigate(path, opts);
            setHomeFadingOut(false);
        }, 350);
    }, [navigate]);

    const handleStartChatFromHome = useCallback(async (prompt, mode = 'code', files = [], fullStackTemplate = null, playbookOptions = {}) => {
        try {
            // task #115: the old mode === 'code' branch here (Console/Catalog
            // project creation via miaPlatformDataService.generateProjectName +
            // createProject, driving the ProjectCreationModal wizard) predated
            // the vfs-vfs architecture and was never removed when it was
            // superseded — it created a real Console project via a legacy BFF
            // API while ALSO racing a separate vfs-service provisioning step
            // for the same id, producing live "vfs-service returned no files"
            // build errors and a recursive-iframe preview (confirmed via a real
            // repro). Every 'code'-mode playbook call site now routes through
            // 'agent' mode instead (see proceedWithSelected/onLaunchIntent
            // below) — the agent's own vfs_create_project tool call is the
            // single source of truth for project creation, matching every
            // other creation path in this app.
            if (mode === 'chat' && prompt && prompt.trim()) {
                // Chat mode: navigate directly to /chat, no project creation —
                // but still a brand-new conversation every time (see
                // newChatConversationId's own doc for why the id is required).
                navigateWithHomeFade(`/chat?conversationId=${newChatConversationId()}`, { state: { initialPrompt: prompt, mode: 'chat', initialFiles: files } });
                setIsNavSidebarCollapsed(true);
            } else if (mode === 'agent' && prompt && prompt.trim()) {
                // Agent mode (task #26): try to auto-select an existing AI Playbook that
                // fits the user's free-form goal before falling back to a plain
                // autonomous chat. This is the "smart routing" layer — it must never
                // regress today's behavior, only add a step on top of it.
                //
                // Tenant resolution mirrors the 'code' branch above (same strategy,
                // not duplicated logic-wise — just re-reading the same state).
                let tenantId = selectedTenant?.tenantId || selectedTenant?.id;
                if (!tenantId && tenants.length > 0) {
                    const firstTenant = tenants[0];
                    tenantId = firstTenant.tenantId || firstTenant.id;
                    selectTenantSilently(firstTenant);
                    devLog('⚡ [Agent mode] Auto-selected first tenant:', tenantId);
                }

                // If the user submits before the tenants list has finished its
                // first load (very plausible: typing a request the instant the
                // page appears, before anything else has settled), `tenants` is
                // still empty here and tenantId stays undefined — every branch
                // below would call createQuickVfsProject(undefined) and
                // get a guaranteed 400 from the backend ("tenantId and
                // projectName are required"), landing on the generic "Failed to
                // create a new project" toast with no indication of why or what
                // to do (live-confirmed: the prompt just sits there unsent,
                // looking like nothing happened once that toast fades). Fail
                // honestly instead of attempting a call that can't succeed.
                if (!tenantId) {
                    window.dispatchEvent(new CustomEvent('show-toast', {
                        detail: { message: '⏳ Still setting up your workspace — try sending that again in a moment', type: 'info' }
                    }));
                    return;
                }

                // BUG FIX (task #118): this used to navigate to `/chat?conversationId=...`,
                // a deliberately project-less route (see newChatConversationId's own doc:
                // its vfsProjectId resolves to the constant "vfs-undefined" there — VfsChatPanel,
                // the ONLY component with the "FE silently pre-creates an vfs vfs
                // project" logic (see its handleToolCall doc comment), is rendered exclusively
                // on the /code/:tenantId/:projectId route, never on /chat). A build prompt from
                // Home landing on /chat meant the agent's session had no project bound at all —
                // confirmed live: vfs_write_file/vfs_list_tree returned "Unknown project:
                // undefined" and the agent asked the user to name a project instead of building
                // autonomously, defeating the entire point of this flow. Fix: mirror
                // handleResetConversation's own working pattern ("New" button) — pre-create a
                // real vfs project via createQuickVfsProject, then land on
                // /code/:tenantId/:projectId (NOT bare /code — bare /code's own vfs-create
                // effect in useCanvasProjectContext checks for a previously-stored project FIRST
                // and would silently reopen that instead of starting fresh, wrong for a Home
                // build prompt, which must always be a new project).
                const fallbackToAgentChat = () => {
                    // BUG FIX (task #219 — reorders task #118's original fix):
                    // task #118 made this pre-create a real vfs project via
                    // createQuickVfsProject BEFORE navigating, because at the
                    // time a project-less /chat landed the agent with no
                    // project bound at all ("Unknown project: undefined"),
                    // defeating autonomous building. That root cause is now
                    // independently fixed (VfsChatPanel omits the
                    // x-mia-vfs-project header entirely rather than sending a
                    // literal "undefined" string, and vfs_ensure_project
                    // cleanly self-creates on a genuinely project-less first
                    // call — see VfsChatPanel.jsx's buildHeaders and
                    // vfs_project_tools.py's vfs_ensure_project). Pre-creating
                    // here had its own real cost: the project's id/URL slug
                    // was minted from a placeholder "Untitled Project
                    // <timestamp>" name BEFORE the agent ever ran, so it
                    // never reflected the app's real purpose — confirmed
                    // live, a project named "Digital Payroll Vault" kept the
                    // slug "untitledproject28091127630703" forever. Mirroring
                    // the mode === 'chat' branch above (same call, same
                    // no-project guarantee) instead lets the agent's own
                    // FIRST vfs_ensure_project call create the project, with
                    // a real, synthesized name (see that tool's own updated
                    // instructions) — the slug is only ever minted from a
                    // real name now. onProjectBound (VfsChatPanel.jsx) picks
                    // up the real project id the moment the agent creates it,
                    // exactly like it already does for the plain /chat door
                    // today.
                    navigateWithHomeFade(`/chat?conversationId=${newChatConversationId()}`, { state: { initialPrompt: prompt, mode: 'agent', initialFiles: files } });
                    setIsNavSidebarCollapsed(true);
                };

                // Task #205: Home's own PlaybookPicker (adk-be-app playbook catalog,
                // NOT the Catalog "AI Playbooks" selectPlaybook() below — see that
                // component's own doc) lets the user override Auto with a specific
                // playbook name. Left on 'auto' this is a no-op — falls straight
                // through to the existing selectPlaybook auto-routing, unchanged.
                // An explicit choice skips selectPlaybook entirely (the user already
                // decided).
                //
                // BUG FIX (task #219 — same reorder as fallbackToAgentChat above):
                // no more pre-created project/localStorage seeding here —
                // ?playbookId=<name> in the URL is VfsChatPanel's own documented
                // fallback for a genuinely project-less first turn (see its
                // playbookChoice/resolvedPlaybookKey initializers: `if
                // (!vfsProjectId) { return playbookIdFromUrl || 'auto'; }`), and
                // it self-seeds the real localStorage key automatically the
                // moment a real project exists (its own effect, keyed on
                // vfsProjectId + playbookIdFromUrl) — no manual seeding needed.
                if (playbookOptions?.playbookName && playbookOptions.playbookName !== 'auto') {
                    navigateWithHomeFade(`/chat?playbookId=${encodeURIComponent(playbookOptions.playbookName)}&conversationId=${newChatConversationId()}`, { state: { initialPrompt: prompt, mode: 'agent', initialFiles: files } });
                    setIsNavSidebarCollapsed(true);
                    return;
                }

                // Playbooks left out by the person's plan (docs/SANDBOX_FIRST.md): nothing to auto-pick, the default
                // assistant takes the prompt. Unknown (older backend, sandbox-first off) counts as included.
                if (!playbooksInPlan()) {
                    fallbackToAgentChat();
                    return;
                }

                try {
                    const selectionResult = await miaPlatformDataService.selectPlaybook(prompt, tenantId);
                    // mia-flow-bff's selectAiPlaybook now ALWAYS returns a real `selected`
                    // object (a deterministic default when nothing confidently matched —
                    // see its own doc comment), so `selected` truthiness can no longer be
                    // used as the "is this even a build request" signal the way `selected
                    // === null` used to be (BUG FIX task #138's whole point). `confidence`
                    // is the real signal now: 0 means "no confident playbook match" (route
                    // to plain chat, exactly as before), regardless of whether a default
                    // object came back alongside it.
                    const selected = selectionResult?.selected;
                    const isConfidentMatch = selected && selectionResult.confidence >= 0.5;

                    if (isConfidentMatch) {
                        // Same routing onLaunchIntent already uses for a manually-picked
                        // playbook of this mode (see onLaunchIntent prop below). Duplicated
                        // here rather than factored into a shared helper: onLaunchIntent is
                        // an inline prop closing over handleStartChatFromHome itself, so
                        // extracting a helper both could share would create a circular
                        // useCallback dependency between the two — not worth the risk for
                        // ~10 lines of routing logic.
                        const proceedWithSelected = async () => {
                            try {
                                sessionStorage.setItem('mia_active_playbook', JSON.stringify(selected));
                            } catch (_) { /* ignore */ }

                            if (selected.mode === 'both') {
                                try {
                                    if (prompt) sessionStorage.setItem('mia_playbook_home_prompt', prompt);
                                } catch (_) { /* ignore */ }
                                navigate(`/playbook/${selected.itemId}`);
                                setIsNavSidebarCollapsed(true);
                            } else {
                                // BUG FIX (task #95): this used to pass mode: 'chat' here, which
                                // the initial-prompt effect below maps to setAiMode('plan') — i.e.
                                // it hard-injects "[PLAN MODE ACTIVE] ... MUST NOT create, modify,
                                // deploy, or execute anything" into every message (see aiMode ===
                                // 'plan' handling near the sendMessage call). That mapping was
                                // designed for the OLD manual mode-selector's explicit "Chat" option
                                // (the user deliberately asking for read-only Q&A) — it was never
                                // meant to apply to an *auto-selected* playbook whose Catalog
                                // miaFlow.mode just happens to be 'chat' (which is also the default
                                // for any playbook that hasn't configured miaFlow.mode at all, see
                                // mia-flow-bff's normalizeV2Item). Auto-selecting a 'chat'-mode
                                // playbook (or one defaulting to it) for a genuine build prompt from
                                // Home was silently downgrading the whole conversation to plan-only
                                // with no way to actually build — use 'agent' instead so this keeps
                                // full autonomy (same as the no-match fallback below and as an
                                // existing project's chat panel), while still passing playbookId so
                                // the BE fetches that playbook's own specialized instructions/skills.
                                //
                                // BUG FIX (task #219 — reorders task #118's fix, same as
                                // fallbackToAgentChat above): no more pre-created project.
                                // ?playbookId=<itemId> is VfsChatPanel's own documented
                                // fallback for a project-less first turn (playbookChoice/
                                // resolvedPlaybookKey read playbookIdFromUrl when
                                // !vfsProjectId) — the manual localStorage seed this used to
                                // do here was working around an OLDER gap in that same
                                // fallback, since closed. The agent's own first
                                // vfs_ensure_project call now mints the project (with a real,
                                // synthesized name/slug) and onProjectBound picks it up,
                                // exactly like the plain /chat door already does today.
                                navigateWithHomeFade(`/chat?playbookId=${selected.itemId}&conversationId=${newChatConversationId()}`, {
                                    state: { initialPrompt: prompt, mode: 'agent' }
                                });
                                setIsNavSidebarCollapsed(true);
                            }
                        };

                        // BUG FIX (task #140): used to pause here with a "Continue with
                        // X / Just chat instead / Choose a different playbook" toast
                        // (task #26) and a 5s auto-continue timer. Per explicit
                        // instruction ("autoselect from home must be skipped, just
                        // select silently and go") — and since task #138 already
                        // proved selectPlaybook's classification is reliable — a real
                        // match now proceeds immediately, no confirmation, no delay.
                        await proceedWithSelected();
                    } else {
                        // BUG FIX (task #138): `selected === null` here is NOT "we
                        // couldn't tell" — it's the BFF/BE's *LLM-backed, trustworthy*
                        // classification that this prompt isn't a build/dev request at
                        // all (confirmed live: "What is the weather like in Rome today?"
                        // returns {selected: null, confidence: 0, reasoning: "...a
                        // general knowledge query unrelated to any of the available
                        // playbooks"}). Routing this to fallbackToAgentChat() — which
                        // unconditionally calls createQuickVfsProject and lands on
                        // /code/... — created a real project and forced a doomed build
                        // attempt (vfs-service returned no files) for a PURE QUESTION,
                        // even though the actual chat answer streamed back correctly in
                        // the background. Home's own placeholder ("Ask a question, or
                        // describe what you want to build...") explicitly promises both
                        // paths work — trust the backend's real "not build-related"
                        // signal and go to plain chat instead, mirroring the mode ===
                        // 'chat' branch above verbatim (same call, same no-project
                        // guarantee). A genuine build request either matches a playbook
                        // (proceedWithSelected, unaffected by this change) or, if it
                        // doesn't match any CONFIGURED playbook but is still clearly
                        // build-flavored, is handled by the catch block below exactly as
                        // before (that's a classification-call FAILURE, a different,
                        // genuinely unknown case — not this one).
                        navigateWithHomeFade(`/chat?conversationId=${newChatConversationId()}`, { state: { initialPrompt: prompt, mode: 'chat', initialFiles: files } });
                        setIsNavSidebarCollapsed(true);
                    }
                } catch (selectionError) {
                    // Playbook selection call failed entirely (network/API error, not a
                    // real "no match" classification) — genuinely unknown intent, so
                    // degrade to today's build-fallback behavior rather than blocking
                    // the user. Deliberately NOT changed by task #138: unlike the
                    // `else` branch above, this path never got a real answer from the
                    // backend about build-vs-chat intent to trust.
                    console.warn('⚠️ [Agent mode] Playbook auto-selection failed, falling back to plain agent chat:', selectionError);
                    fallbackToAgentChat();
                }
            } else {
                // For non-code modes, just navigate normally
                navigate('/code', { state: { initialPrompt: prompt, mode } });
                setIsNavSidebarCollapsed(true);
            }

        } catch (error) {
            console.error('Error starting chat:', error);
            window.dispatchEvent(new CustomEvent('show-toast', {
                detail: { message: '❌ Error starting chat', type: 'error' }
            }));
        }
    }, [navigate, selectedTenant, tenants, setIsNavSidebarCollapsed, selectTenantSilently]);



    const handleSelectProjectFromHome = useCallback((project) => {
        devLog('🎯 handleSelectProjectFromHome called with:', project);
        openProject({
            project,
            navigate,
            onInvalid: () => {
                console.error('❌ No project ID found in project object:', project);
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: { message: '❌ Invalid project: missing project ID', type: 'error' }
                }));
            },
        });
    }, [navigate, user]);

    const handleSelectTemplate = useCallback((template) => {
        // Navigate to chat and start with template
        navigate('/code');
        setTimeout(() => {
            // Send message about template
            const prompt = `Create a new project based on the "${template.name}" template`;
            const inputElement = document.querySelector('.message-input');
            if (inputElement) {
                inputElement.value = prompt;
                const event = new Event('submit', { bubbles: true, cancelable: true });
                inputElement.closest('form')?.dispatchEvent(event);
            }
        }, 100);
    }, [navigate]);

    // Mock projects and templates data (replace with real API calls)
    const [projects, setProjects] = useState([]);
    const [templates, setTemplates] = useState([]);

    useEffect(() => {
        // Load projects from IndexedDB or Mia Platform
        const loadProjects = async () => {
            try {
                // Try to get Mia Platform projects
                // For now, return empty array - integrate with actual Mia Platform API when available
                // The recent projects will be populated from the BFF MongoDB storage
                setProjects([]);

                // Load templates
                setTemplates([
                    {
                        name: 'React SPA Starter',
                        description: 'Modern React single-page application with routing and state management',
                        preview: null,
                        tags: ['React', 'Vite', 'TypeScript']
                    },
                    {
                        name: 'Next.js Blog',
                        description: 'Server-side rendered blog with markdown support',
                        preview: null,
                        tags: ['Next.js', 'MDX', 'Tailwind']
                    },
                    {
                        name: 'Node.js REST API',
                        description: 'Express-based REST API with database integration',
                        preview: null,
                        tags: ['Node.js', 'Express', 'MongoDB']
                    },
                    {
                        name: 'E-commerce Store',
                        description: 'Full-stack e-commerce platform with cart and checkout',
                        preview: null,
                        tags: ['React', 'Node.js', 'Stripe']
                    },
                    {
                        name: 'Dashboard Admin',
                        description: 'Admin dashboard with charts and data tables',
                        preview: null,
                        tags: ['React', 'Charts', 'Material-UI']
                    },
                    {
                        name: 'Landing Page',
                        description: 'Responsive marketing landing page',
                        preview: null,
                        tags: ['HTML', 'CSS', 'JavaScript']
                    }
                ]);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load projects:', error);
            }
        };

        loadProjects();
    }, []);

    // Guards against double-firing when the "+"/New button is clicked twice
    // in a row while a project is already being created.
    const isCreatingNewProjectRef = useRef(false);

    const handleResetConversation = useCallback(() => {
        // Dispatch a custom event to signal intentional reset — resets
        // chat-local state (messages/session/sessionId) immediately.
        window.dispatchEvent(new Event('forceNewChat'));

        // The chat inside a project no longer comes here: its "New chat", `/new`
        // and `/clear` start a fresh conversation in the SAME project
        // (handleNewChatInProject in MainChat). What reaches this is a chat
        // with no project bound: on doors other than Code (e.g. /chat's
        // compose mode) it just resets the chat, as before.
        if (!location.pathname.startsWith('/code')) {
            if (location.search.includes('conversationId')) {
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.delete('conversationId');
                const newSearch = newSearchParams.toString();
                navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
            }
            return;
        }

        if (isCreatingNewProjectRef.current) {
            return;
        }

        const tenantId = params.tenantId
            || selectedTenant?.tenantId
            || selectedTenant?.id
            || miaFlowAuth.getSelectedTenant()?.tenantId;

        if (!tenantId) {
            console.warn('No tenant available — cannot create a new project for "New"');
            return;
        }

        isCreatingNewProjectRef.current = true;

        // No dedicated "creating a new project" UI here — the VfsChatPanel
        // guard (App.jsx, MainChat render) already shows a "Setting up your
        // project…" placeholder for exactly this window (canvasProjectContext
        // not yet loaded for the new URL), which is a better fit for this
        // quick, prompt-less action than the multi-step AI-naming creation
        // modal (isCreationModalOpen/creationStep) used for chat-driven
        // project creation.
        createQuickVfsProject(tenantId)
            .then(({ projectId: newProjectId, name }) => {
                miaFlowAuth.setSelectedProject({ projectId: newProjectId, name });
                // Drop conversationId/project-specific query params and replace
                // history so the back button doesn't return to the old project.
                navigate(`/code/${tenantId}/${newProjectId}`, { replace: true });
            })
            .catch((error) => {
                console.error('Failed to create a new project for "New":', error);
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: { message: '⚠️ Failed to create a new project', type: 'error' }
                }));
            })
            .finally(() => {
                isCreatingNewProjectRef.current = false;
            });
    }, [location.pathname, location.search, navigate, params.tenantId, selectedTenant]);


    // Today's Home (prompt, chips, recent projects and sessions): the Home route, and in the sandbox-first experience
    // the Home item shown while Chat is on (docs/SANDBOX_FIRST.md).
    const legacyHome = (
        <Home
                fadeOut={homeFadingOut}
                onStartChat={handleStartChatFromHome}
                onSelectProject={handleSelectProjectFromHome}
                onSelectTemplate={handleSelectTemplate}
                onNavigate={(path) => navigate(path)}
                projects={projects}
                templates={templates}
                user={user}
                selectedTenant={selectedTenant}
                hasGitConnection={hasGitConnection}
                playbooks={sidebarPlaybooks.filter(i => i.showOnHome)}
                onLaunchIntent={async (playbook, prompt) => {
                    try {
                        sessionStorage.setItem('mia_active_playbook', JSON.stringify(playbook));
                    } catch (_) { /* ignore */ }
                    if (playbook.mode === 'both') {
                        // Let the PlaybookLauncher show the chat/code chooser
                        try {
                            if (prompt) sessionStorage.setItem('mia_playbook_home_prompt', prompt);
                        } catch (_) { /* ignore */ }
                        navigate(`/playbook/${playbook.itemId}`);
                        setIsNavSidebarCollapsed(true);
                    } else {
                        // task #115: 'code' used to trigger a separate Console/
                        // Catalog project-creation wizard (ProjectCreationModal)
                        // that predated the vfs-vfs architecture — it
                        // created a real Console project via a legacy BFF API
                        // while ALSO racing a separate vfs-service provisioning
                        // step for the same id, producing "vfs-service returned
                        // no files" errors. Every playbook mode now routes
                        // through the same full-autonomy agent chat; the agent's
                        // own vfs_create_project tool call is the single source
                        // of truth for project creation. mode: 'agent' (not
                        // 'chat' — see the identical fix in proceedWithSelected
                        // above/task #95) so this doesn't get hard-downgraded
                        // into read-only Plan Mode.
                        //
                        // BUG FIX (task #219 — reorders task #118's fix, same as
                        // handleStartChatFromHome's fallbackToAgentChat/
                        // proceedWithSelected above): route through the
                        // project-less /chat door instead of pre-creating. That
                        // door's own project-less-turn handling (VfsChatPanel's
                        // header omission + vfs_ensure_project's clean self-create)
                        // has since made the /code-only requirement task #118
                        // documented obsolete — and pre-creating had its own real
                        // cost, minting the project's id/URL slug from a
                        // placeholder name before the agent ever ran. ?playbookId=
                        // is VfsChatPanel's own documented fallback for this exact
                        // project-less case.
                        let launchTenantId = selectedTenant?.tenantId || selectedTenant?.id;
                        if (!launchTenantId && tenants.length > 0) {
                            const firstTenant = tenants[0];
                            launchTenantId = firstTenant.tenantId || firstTenant.id;
                            selectTenantSilently(firstTenant);
                        }
                        if (!launchTenantId) {
                            console.warn('No tenant available — cannot create a new project for this playbook');
                            return;
                        }
                        navigate(`/chat?playbookId=${playbook.itemId}&conversationId=${newChatConversationId()}`, {
                            state: { initialPrompt: prompt, mode: 'agent' }
                        });
                        setIsNavSidebarCollapsed(true);
                    }
                }}
/>
    );

    // Home. Sandbox-first (docs/SANDBOX_FIRST.md): ONE Home, the sandbox overview, with the prompt Home on top while Chat
    // is on (SandboxHome's renderChatHome); never two homes to choose from. Also behind a "New project" link (`/new`).
    const rootHome = config.sandboxFirst ? (
        <SandboxHome
            renderChatHome={({ sandboxSummary, liveNow, focusPrompt }) => React.cloneElement(legacyHome, { sandboxSummary, liveNow, autoFocusPrompt: focusPrompt })}
            selectedTenant={selectedTenant}
            user={user}
        />
    ) : legacyHome;

    return (
        // Global search (⌘K anywhere, the sidebar's Search box, the phone's magnifier): components/search.
        <SearchProvider>
        <div className={`app-container${isNavSidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
            {/* Open in Flow from Console Next (`?consoleHandoff=`): asks, then imports the repositories as a project. */}
            <ConsoleHandoff />
            {/* "What can Flow run?" and ⌘K open Supported languages and frameworks here, in a dialog (lazy). */}
            <SupportedStacksHost />
            {/* "New project" from a link (`/flow/new?template=…` or `?remix=…`): opens the dialog prefilled. */}
            {config.sandboxFirst && <NewProjectLink />}
            {/* Global toast — see globalToast's own comment above for why this
                needs to live here rather than relying on MainChat's local one. */}
            {globalToast && (
                <Toast
                    message={globalToast.message}
                    onClose={() => setGlobalToast(null)}
                    type={globalToast.type}
                />
            )}

            {/* Tenant switch loading overlay */}
            {isTenantSwitching && (
                <TenantSwitchOverlay
                    tenantName={switchingToTenantName}
                    onDone={() => setIsTenantSwitching(false)}
                />
            )}

            {/* Left Navigation Sidebar - Lovable style. Always rendered — see the
                auto-collapse effect above — so there's always a way back to
                home or another project, even from the code view. */}
            {/* New @mia-platform-internal/sidebar-widget-based sidebar (first
                integration of that shared package — see FlowSidebar.jsx's own
                doc comment). The "no Git connection" popup and per-item
                playbook badge are still not ported — see FlowSidebar.jsx's
                own doc comment for why (dead trigger logic / a legacy event
                system respectively). Mobile: below 767px this is now an
                off-canvas drawer (mobileNavOpen) opened via the hamburger
                button below, instead of the always-visible icon rail the
                previous "no mobile nav affordance" decision left in place —
                isCollapsed is forced false while the drawer is open so it
                renders full labels, not the icon-only rail. Only rendered
                while CLOSED — live-confirmed white-on-white otherwise: once
                open, the drawer itself is the same opaque --bg-card surface
                (see FlowSidebar.css), so a second toggle sitting on top of
                it at the same corner just visually vanished into it; the
                backdrop/nav-click/route-change closes already cover
                "how do I close it" without a redundant button. */}
            {!mobileNavOpen && (
                <button
                    aria-label="Open navigation menu"
                    className="mobile-nav-toggle"
                    onClick={() => setMobileNavOpen(true)}
                    type="button"
                >
                    <Icon name="list" size={20} />
                </button>
            )}
            {!mobileNavOpen && <MobileSearchButton />}
            <AnalyticsTracker />
            <FlowSidebar
                currentPath={location.pathname}
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
                onNavigate={navigate}
                selectedTenant={selectedTenant}
                isCollapsed={mobileNavOpen ? false : isNavSidebarCollapsed}
                onToggleCollapse={handleToggleNavSidebarCollapse}
                user={user}
            />

            {/* Main content area. id targeted by MainChat's Canvas portal below
                (Canvas.css's .canvas-container needs THIS div — full-width,
                already positioned past the nav sidebar by normal flex flow —
                as its containing block; portaling straight to document.body
                would lose that offset and reintroduce the exact nav-sidebar
                overlap the position:fixed→absolute switch was made to fix,
                see Canvas.css's own comment on .canvas-container). */}
            <div className="main-content" id="canvas-portal-root">
                {/* The sandbox admin's maintenance / incident banner: app-wide, above every page (dismissible). */}
                <SandboxMessagesBanner />
                {/* "Flow is being updated — back in about N minutes" (a release rolling out, 502/503/504), or "Flow was
                    updated" with Reload (a new version detected, utils/staleBuild.js); a page may show it itself. */}
                <FlowUpdatingBanner />
                {/* "Waking your sandbox…" when a request to it waits while it starts, or the person's connection is
                    offline, slow or can't reach Flow (a page may show it itself). */}
                <SandboxWakeBanner />
                <Routes>
                    {/* All routes in MainApp are protected */}

                    {/* Home. Sandbox-first (docs/SANDBOX_FIRST.md): ONE Home, the sandbox overview, with the prompt
                        Home on top while Chat is on (SandboxHome's renderChatHome); never two homes to choose from. */}
                    <Route element={rootHome} path="/" />

                    {/* "New project" links (`/flow/new?template=<id>`, `?remix=<projectId>`): Home, with the dialog
                        NewProjectLink opens over it; its own route, else `/:tenantId` would take "new" for a tenant. */}
                    <Route element={rootHome} path="/new" />

                    {/* The full sandbox overview has its own address too (the sidebar's Sandbox item while Chat is on). */}
                    {config.sandboxFirst && (
                        <Route
                            element={<SandboxHome selectedTenant={selectedTenant} user={user} />}
                            path="/sandbox"
                        />
                    )}
                    <Route
                        element={config.sandboxFirst ? <Navigate replace to="/" /> : legacyHome}
                        path="/home"
                    />

                    {/* The person's preferences, from Settings next to their avatar (sandbox-first) */}
                    <Route
                        element={<UserSettings />}
                        path="/settings"
                    />

                    {/* First-run setup: AI tool, Flow's assistant, or both (sandbox-first) */}
                    <Route
                        element={<Welcome user={user} />}
                        path="/welcome"
                    />

                    {/* Features › Chat: turn Flow's assistant on or off (sandbox-first) */}
                    <Route
                        element={<ChatFeature />}
                        path="/features/chat"
                    />

                    {/* Features › Connections: the paid add-ons and whether the person has each (sandbox-first) */}
                    <Route
                        element={<Connections />}
                        path="/features/connections"
                    />

                    {/* Projects page route */}
                    <Route
                        element={<Projects />}
                        path="/projects"
                    />

                    {/* Workspaces: groups of projects with their own members and brain (vfs-service docs/workspaces.md) */}
                    <Route
                        element={<Workspaces />}
                        path="/workspaces"
                    />
                    <Route
                        element={<Workspace />}
                        path="/workspaces/:workspaceId"
                    />

                    {/* Deep link from the "shared with you" email: opens the project in the person's organization */}
                    <Route
                        element={<OpenProject />}
                        path="/open/:projectId"
                    />

                    {/* Playbook launcher route */}
                    <Route
                        element={<PlaybookLauncher />}
                        path="/playbook/:playbookId"
                    />

                    {/* Templates page route */}
                    <Route
                        element={<Templates />}
                        path="/templates"
                    />

                    {/* Supported languages and frameworks (vfs-service GET /api/v1/stacks), lazy. */}
                    <Route
                        element={<React.Suspense fallback={null}><SupportedStacks /></React.Suspense>}
                        path="/supported-stacks"
                    />

                    {/* Learn page route */}
                    <Route
                        element={<Learn />}
                        path="/learn"
                    />

                    {/* Discover page route */}
                    <Route
                        element={<Discover />}
                        path="/discover"
                    />

                    {/* History page route (renamed from Memories) */}
                    <Route
                        element={<Memories />}
                        path="/history"
                    />
                    {/* Old /memories bookmarks/links redirect to the renamed route */}
                    <Route
                        element={<Navigate replace to="/history" />}
                        path="/memories"
                    />

                    {/* Kanban (task #193) is no longer a standalone page — it's a
                        view switch inside History itself (see Memories.jsx's
                        List/Kanban toggle). Old /kanban links land on that view
                        directly instead of 404ing. */}
                    <Route
                        element={<Navigate replace to="/history?view=kanban" />}
                        path="/kanban"
                    />

                    {/* Activity/usage dashboard (task #212) is no longer a standalone
                        nav item — it's a third view mode inside History (a button
                        next to List/Kanban, see Memories.jsx). Old /activity links
                        land on that view directly instead of 404ing. */}
                    <Route
                        element={<Navigate replace to="/history?view=activity" />}
                        path="/activity"
                    />

                    {/* Agents page route — front-door proposal door 3 */}
                    <Route
                        element={<Agents />}
                        path="/agents"
                    />

                    {/* Tools page route (Connectors) */}
                    <Route
                        element={<Connectors />}
                        path="/tools"
                    />

                    {/* Global, cross-project brain (vfs-service's reserved
                        `_global-brain` project id, commit f06e38f) — not
                        tied to any single project, so it gets its own nav
                        slot/route rather than living inside a project's
                        Dashboard (see that view's own per-project "Brain"
                        section, DashboardView.jsx). */}
                    <Route
                        element={<GlobalBrain />}
                        path="/brain"
                    />

                    {/* Hidden ops page — CI/SBOM/security status across the
                        Flow ecosystem's GitLab repos. Deliberately not
                        linked from any nav; reachable only by typing the
                        URL. See src/pages/InternalStatus.jsx. */}
                    <Route
                        element={<InternalStatus />}
                        path="/ops/status"
                    />

                    {/* Main chat route without top bar */}
                    <Route
                        element={
                            <MainChat
                                onNewConversation={handleResetConversation}
                            />
                        }
                        path="/code/:tenantId?/:projectId?/:microservice?/:branch?/:tab?/*"
                    />

                    {/* Compose route - chat only, no canvas */}
                    <Route
                        element={
                            <RequireChat>
                                <MainChat
                                    onNewConversation={handleResetConversation}
                                    hideCanvas={true}
                                />
                            </RequireChat>
                        }
                        path="/chat"
                    />

                    {/* Tenant ID route - when URL is just /:tenantId, show home page with tenant pre-selected */}
                    <Route
                        element={
                            <Home
                                fadeOut={homeFadingOut}
                                onStartChat={handleStartChatFromHome}
                                onSelectProject={handleSelectProjectFromHome}
                                onSelectTemplate={handleSelectTemplate}
                                onNavigate={(path) => navigate(path)}
                                projects={projects}
                                templates={templates}
                                user={user}
                                selectedTenant={selectedTenant}
                                hasGitConnection={hasGitConnection}
                            />
                        }
                        path="/:tenantId"
                    />
                </Routes>
            </div>

            {/* In-app browser overlay (task #26): renders external links from
                chat inside Flow instead of a brand new tab. */}
            {inAppBrowser?.isOpen && (
                <InAppBrowserOverlay
                    canGoBack={inAppBrowser.currentIndex > 0}
                    onBack={() => setInAppBrowser((prev) => (prev
                        ? {...prev, currentIndex: Math.max(0, prev.currentIndex - 1)}
                        : prev))}
                    onClose={() => setInAppBrowser(null)}
                    url={inAppBrowser.history[inAppBrowser.currentIndex]}
                />
            )}

            {/* Mobile Preview Page - Full-screen mobile preview experience */}
            <MobilePreviewPage
                isOpen={isMobilePreviewPageOpen}
                onClose={() => setIsMobilePreviewPageOpen(false)}
                onAskAI={(prompt) => {
                    // Handle ask AI from preview
                    navigate('/code');
                }}
            />
        </div>
        </SearchProvider>
    );
}

// Wrap App with the authorization providers + AuthProvider.
// OrganizationProvider (authz org/tenant selection) is outermost so PermissionsProvider —
// which resolves roles scoped to the selected org — can read it; TenantAccessProvider loads
// the tenant list + per-org access map. All authz providers sit inside AuthProvider since they
// need an authenticated user before calling the gateway/BFF.
function AppWithAuth() {
    return (
        <OrganizationProvider>
            <AuthProvider>
                <PermissionsProvider>
                    <TenantAccessProvider>
                        <App />
                    </TenantAccessProvider>
                </PermissionsProvider>
            </AuthProvider>
        </OrganizationProvider>
    );
}

export default AppWithAuth;
